/**
 * Compiler that emits bytecode for loopmaster
 */

import {
  AmplitudeInfo,
  BytecodeEncoder,
  BytecodeProgram,
  DelayType,
  FilterType,
  hashString,
  LfoType,
  NoteData,
  OpCode,
  OscType,
  PatternEventInfo,
  SequenceData,
  TimelineInfo,
  WaveformInfo,
} from './bytecode.ts'
import { DX7 } from './dx7.ts'
import { functionDefinitions, getAllFunctionNames } from './function-definitions.ts'
import { Lexer, TokenType } from './lexer.ts'
import { PRELUDE } from './lib/synths.ts'
import { expandPattern, expandPatternWithVariations, parsePattern, type PatternEvent } from './mini-notation-parser.ts'
import {
  ArrayLiteralNode,
  ASTNode,
  BinaryOpNode,
  CallNode,
  IdentifierNode,
  LiteralNode,
  NumberWithParamsNode,
  Parser,
  PipelineNode,
  ProgramNode,
} from './parser.ts'
import { parseTimelineString, type TimelineSegment } from './timeline-parser.ts'

// Expression nodes that can be used as argument values
type ExpressionNode =
  | LiteralNode
  | IdentifierNode
  | ArrayLiteralNode
  | PipelineNode
  | BinaryOpNode
  | CallNode
  | NumberWithParamsNode

export class BytecodeCompiler {
  private encoder = new BytecodeEncoder()
  private sequences: SequenceData[] = []
  private sequenceMaxPitches = new Map<number, number>() // seqId -> max pitch count
  private bpm = 120
  private tune = 1.0
  private hasExplicitBpm = false // Track if BPM was explicitly set in code
  private varCounter = 0
  private idCounter = 0
  private symbols = new Map<string, number>()
  private sequenceMap = new Map<string, number>() // name -> hashed ID
  private seqAssignments = new Map<string, any>() // varName -> seq() Call node
  private userFunctions = new Map<string, any>() // varName -> ArrowFunction node
  private expressionAssignments = new Map<string, any>() // varName -> expression node
  private impulseResponses = new Set<string>() // Track IR file paths
  private samples = new Set<string>() // Track unique sample file paths (for loading)
  private samplePaths: string[] = [] // Track sample paths in order of ID assignment (index = ID)
  private sampleIdCounter = 0 // Incremental ID counter for samples (resets each compilation)
  private samSpeeches = new Map<number, any>() // Track sam speech parameters (hash -> params)
  private walkArrays = new Map<number, { values: number[]; measure: number }>() // arrayId -> {values, measure}
  private slideArrays = new Map<number, { values: number[]; measure: number; exponent: number }>() // arrayId -> {values, measure, exponent}
  private postFunction: any = null // Track post-processing function
  private numbersWithParams: Map<any, { value: number; min: number; max: number; line?: number; column?: number }> =
    new Map() // Track numbers with params for UI
  private patternEvents: PatternEventInfo[] = [] // Track pattern events for UI
  private amplitudeInfos: { id: number; line: number; column: number; length: number }[] = [] // Track amplitude widgets for UI
  private waveformInfos: { id: number; line: number; column: number; length: number }[] = [] // Track waveform widgets for UI
  private levelInfos: { id: number; line: number; column: number; length: number }[] = [] // Track level widgets for UI
  private timelineInfos: TimelineInfo[] = [] // Track timeline widgets for UI
  private preludeLineCount: number = 0 // Store prelude line count for adjusting pattern event positions
  private tokens: any[] = [] // Store tokens for length calculation

  // Helper to calculate actual length of a function call from tokens
  private calculateCallLength(call: any): number {
    if (!call.line || !call.column) {
      // Fallback to estimated length if line/column not available
      return call.name.length + 2 + (call.args?.[0]?.value?.name?.length || 1)
    }

    // Find the function name token
    for (let i = 0; i < this.tokens.length; i++) {
      const token = this.tokens[i]
      if (token.type === TokenType.IDENTIFIER && token.value === call.name && token.line === call.line
        && token.column === call.column)
      {
        // Found the function name, now find the closing parenthesis
        let j = i + 1
        let depth = 0
        let foundOpenParen = false

        // Find opening parenthesis (may have whitespace tokens in between)
        while (j < this.tokens.length && this.tokens[j].line === token.line) {
          if (this.tokens[j].type === TokenType.LPAREN) {
            foundOpenParen = true
            depth = 1
            j++
            break
          }
          j++
        }

        if (!foundOpenParen) {
          // Not a function call with parentheses, use estimated length
          return call.name.length + 2 + (call.args?.[0]?.value?.name?.length || 1)
        }

        // Find the matching closing parenthesis
        while (j < this.tokens.length && depth > 0) {
          if (this.tokens[j].type === TokenType.LPAREN) depth++
          if (this.tokens[j].type === TokenType.RPAREN) {
            depth--
            if (depth === 0) {
              // Found the closing parenthesis
              const endToken = this.tokens[j]
              if (token.line === endToken.line) {
                // Same line, calculate length including the closing paren
                return (endToken.column + endToken.value.length) - token.column
              }
              break
            }
          }
          j++
        }

        // If we didn't find a closing paren on the same line, use estimated length
        return call.name.length + 2 + (call.args?.[0]?.value?.name?.length || 1)
      }
    }

    // Token not found, use estimated length
    return call.name.length + 2 + (call.args?.[0]?.value?.name?.length || 1)
  }

  // Counter-based IDs for each block type
  // Oscillators share oscillators Map
  private oscillatorCounterId = 0 // sin, tri, saw, sqr, ramp, pwm
  // Filter counters - separate for each filter type to ensure stable allocation IDs
  private lpCounterId = 0
  private hpCounterId = 0
  private bpCounterId = 0
  private bsCounterId = 0
  private notchCounterId = 0
  private lsCounterId = 0
  private hsCounterId = 0
  private allpassCounterId = 0
  private peakCounterId = 0
  private eq3CounterId = 0
  private svfFilterCounterId = 0 // svflp, svfhp, svfbp, svfnotch, svfpeak, svfallpass
  private diodeladderCounterId = 0
  private moogFilterCounterId = 0 // mooglp, mooghp
  private ffthpCounterId = 0
  private onepoleCounterId = 0
  // Delay types share delays Map
  private delayCounterId = 0 // delay, beatdelay
  private combCounterId = 0
  private chorusCounterId = 0
  private flangerCounterId = 0
  private dattorroCounterId = 0
  private freeverbCounterId = 0
  private fdnCounterId = 0
  private fdn2CounterId = 0
  private velvetCounterId = 0
  private lexiconCounterId = 0
  private springCounterId = 0
  private compCounterId = 0
  private expanderCounterId = 0
  private gateCounterId = 0
  private limiterCounterId = 0
  private vocoderCounterId = 0
  private sidechainCounterId = 0
  // Envelope types share envelopes Map
  private envelopeCounterId = 0 // ar, adsr
  private noiseCounterId = 0
  private harmonicsCounterId = 0
  private grainCounterId = 0
  private beatgrainCounterId = 0
  private sampleCounterId = 0
  private slicerCounterId = 0
  private karplusCounterId = 0
  private pitchshiftCounterId = 0
  private slewCounterId = 0
  private sahCounterId = 0
  private envfollowCounterId = 0
  private dcCounterId = 0
  private bitcrusherCounterId = 0
  private tanhCounterId = 0
  private atanCounterId = 0
  private absCounterId = 0
  private sqrtCounterId = 0
  private logCounterId = 0
  private expCounterId = 0
  private dbCounterId = 0
  private floorCounterId = 0
  private ceilCounterId = 0
  private fractCounterId = 0
  private signCounterId = 0
  private softCounterId = 0
  private asymCounterId = 0
  private mtofCounterId = 0
  private transposeCounterId = 0
  private swapCounterId = 0
  private selectCounterId = 0
  private crossfadeCounterId = 0
  private minCounterId = 0
  private maxCounterId = 0
  private clampCounterId = 0
  private wrapCounterId = 0
  private convCounterId = 0
  private skipCounterId = 0
  private everyCounterId = 0
  private euclidCounterId = 0
  private subdivideCounterId = 0
  private timelineCounterId = 0
  private sequenceCounterId = 0
  private amplitudeCounterId = 0
  private cachedKnownFunctions: Set<string> | null = null

  compile(source: string, defaultBpm: number = 120): BytecodeProgram {
    // Prelude: built-in DSL snippets available to every program
    const preludeSources: string[] = [DX7, PRELUDE]
    const combinedSource = `${preludeSources.join('\n')}\n${source}`

    const lexer = new Lexer(combinedSource)
    const tokens = lexer.tokenize()
    this.tokens = tokens
    const parser = new Parser(tokens)
    const ast = parser.parse()

    this.encoder.reset()
    this.sequences = []
    this.sequenceMaxPitches.clear()
    this.varCounter = 0
    this.idCounter = 0
    this.symbols.clear()
    this.sequenceMap.clear()
    this.patternEvents = []
    this.amplitudeInfos = []
    this.waveformInfos = []
    this.levelInfos = []
    this.timelineInfos = []
    this.numbersWithParams.clear()
    this.cachedKnownFunctions = null

    // Calculate prelude line count to adjust AST line numbers
    const joinedPrelude = preludeSources.join('\n')
    let newlineCount = 0
    for (let i = 0; i < joinedPrelude.length; i++) {
      if (joinedPrelude[i] === '\n') newlineCount++
    }
    // split('\n').length = newlines + 1 (unless string is empty)
    this.preludeLineCount = joinedPrelude.length > 0 ? newlineCount + 1 : 0
    this.seqAssignments.clear()
    this.expressionAssignments.clear()
    this.userFunctions.clear()
    this.impulseResponses.clear()
    this.samples.clear()
    this.samplePaths = []
    this.sampleIdCounter = 0
    this.samSpeeches.clear()
    this.walkArrays.clear()
    this.slideArrays.clear()
    this.postFunction = null
    // Initialize BPM to provided default so that tracks without explicit bpm use global bpm
    this.bpm = defaultBpm
    this.tune = 1.0
    this.hasExplicitBpm = false

    // Reset all block type counters to 0
    this.resetAllCounters()

    this.compileProgram(ast)

    // Extract NumberWithParams positions from user source (not combined with prelude)
    const numbersWithParamsArray: any[] = []

    if (this.numbersWithParams.size > 0) {
      // Lex the user source separately to get correct line numbers
      const userLexer = new Lexer(source)
      const userTokens = userLexer.tokenize()

      // Calculate prelude line count to adjust AST line numbers
      // AST was parsed from combined source (prelude + user), so we need to subtract prelude lines
      const preludeLineCount = preludeSources.join('\n').split('\n').length

      for (const [node, params] of this.numbersWithParams.entries()) {
        const nodeValue = params.value
        // Adjust line number by subtracting prelude lines (AST was parsed from combined source)
        const nodeLine = params.line !== undefined ? params.line - preludeLineCount : undefined
        const nodeColumn = params.column

        let found = false

        // If we have line/column from AST, use it to find exact match
        if (nodeLine !== undefined && nodeColumn !== undefined && nodeLine > 0) {
          for (let i = 0; i < userTokens.length; i++) {
            const token = userTokens[i]
            let isNegative = false
            let numberTokenIdx = i
            let startToken = token

            // Check for negative number
            if (token.type === 'MINUS' && userTokens[i + 1]?.type === 'NUMBER') {
              isNegative = true
              numberTokenIdx = i + 1
              startToken = token
            }

            const numberToken = userTokens[numberTokenIdx]
            if (numberToken?.type === 'NUMBER') {
              const tokenLine = isNegative ? startToken.line : numberToken.line
              const tokenColumn = isNegative ? startToken.column : numberToken.column

              // Match by line/column position
              if (tokenLine === nodeLine && tokenColumn === nodeColumn) {
                const tokenValue = parseFloat(numberToken.value) * (isNegative ? -1 : 1)

                // Verify value matches
                if (Math.abs(tokenValue - nodeValue) < 0.0001) {
                  const nextToken = userTokens[numberTokenIdx + 1]
                  if (nextToken?.type === 'LPAREN') {
                    let j = numberTokenIdx + 2
                    let depth = 1
                    while (j < userTokens.length && depth > 0) {
                      if (userTokens[j].type === 'LPAREN') depth++
                      if (userTokens[j].type === 'RPAREN') {
                        depth--
                        if (depth === 0) break
                      }
                      j++
                    }

                    if (j < userTokens.length && userTokens[j].type === 'RPAREN') {
                      const endToken = userTokens[j]

                      if (startToken.line === endToken.line) {
                        const totalLength = (endToken.column + endToken.value.length) - startToken.column
                        numbersWithParamsArray.push({
                          value: nodeValue,
                          line: nodeLine!, // Use line from AST node
                          column: nodeColumn!, // Use column from AST node
                          length: totalLength,
                          min: params.min,
                          max: params.max,
                        })
                        found = true
                        break
                      }
                    }
                  }
                }
              }
            }

            if (isNegative) i++ // Skip the number token we just processed
          }
        }

        // Fallback to value-based matching if line/column not available or didn't match
        if (!found) {
          for (let i = 0; i < userTokens.length; i++) {
            const token = userTokens[i]
            let isNegative = false
            let numberTokenIdx = i
            let startToken = token

            // Check for negative number
            if (token.type === 'MINUS' && userTokens[i + 1]?.type === 'NUMBER') {
              isNegative = true
              numberTokenIdx = i + 1
              startToken = token
            }

            const numberToken = userTokens[numberTokenIdx]
            if (numberToken?.type === 'NUMBER') {
              const tokenValue = parseFloat(numberToken.value) * (isNegative ? -1 : 1)

              // Match by value
              if (Math.abs(tokenValue - nodeValue) < 0.0001) {
                const nextToken = userTokens[numberTokenIdx + 1]
                if (nextToken?.type === 'LPAREN') {
                  let j = numberTokenIdx + 2
                  let depth = 1
                  while (j < userTokens.length && depth > 0) {
                    if (userTokens[j].type === 'LPAREN') depth++
                    if (userTokens[j].type === 'RPAREN') {
                      depth--
                      if (depth === 0) break
                    }
                    j++
                  }

                  if (j < userTokens.length && userTokens[j].type === 'RPAREN') {
                    const endToken = userTokens[j]

                    if (startToken.line === endToken.line) {
                      const totalLength = (endToken.column + endToken.value.length) - startToken.column
                      numbersWithParamsArray.push({
                        value: nodeValue,
                        line: startToken.line,
                        column: startToken.column,
                        length: totalLength,
                        min: params.min,
                        max: params.max,
                      })
                      break
                    }
                  }
                }
              }
            }

            if (isNegative) i++ // Skip the number token we just processed
          }
        }
      }
    }

    return {
      bytecode: this.encoder.getBytecode(),
      sequences: this.sequences,
      bpm: this.bpm,
      tune: this.tune,
      hasExplicitBpm: this.hasExplicitBpm,
      impulseResponses: Array.from(this.impulseResponses),
      samples: Array.from(this.samples),
      sampleIdMap: new Map<string, number>(), // Not used anymore, kept for compatibility
      samplePaths: this.samplePaths, // Array of paths in order of ID assignment
      samSpeeches: Array.from(this.samSpeeches.entries()),
      numbersWithParams: numbersWithParamsArray,
      patternEvents: this.patternEvents,
      amplitudeInfos: this.amplitudeInfos,
      waveformInfos: this.waveformInfos,
      levelInfos: this.levelInfos,
      timelineInfos: this.timelineInfos,
    }
  }

  private getNextSampleId(path: string): number {
    // Always assign a new incremental ID for each sample usage
    const id = this.sampleIdCounter++
    this.samples.add(path)
    this.samplePaths.push(path) // Store path in order (index = ID)
    return id
  }

  private resetAllCounters(): void {
    this.oscillatorCounterId = 0
    this.lpCounterId = 0
    this.hpCounterId = 0
    this.bpCounterId = 0
    this.bsCounterId = 0
    this.notchCounterId = 0
    this.lsCounterId = 0
    this.hsCounterId = 0
    this.allpassCounterId = 0
    this.peakCounterId = 0
    this.eq3CounterId = 0
    this.svfFilterCounterId = 0
    this.diodeladderCounterId = 0
    this.moogFilterCounterId = 0
    this.ffthpCounterId = 0
    this.onepoleCounterId = 0
    this.delayCounterId = 0
    this.combCounterId = 0
    this.chorusCounterId = 0
    this.flangerCounterId = 0
    this.dattorroCounterId = 0
    this.freeverbCounterId = 0
    this.fdnCounterId = 0
    this.fdn2CounterId = 0
    this.velvetCounterId = 0
    this.lexiconCounterId = 0
    this.springCounterId = 0
    this.compCounterId = 0
    this.expanderCounterId = 0
    this.gateCounterId = 0
    this.limiterCounterId = 0
    this.vocoderCounterId = 0
    this.sidechainCounterId = 0
    this.envelopeCounterId = 0
    this.noiseCounterId = 0
    this.harmonicsCounterId = 0
    this.grainCounterId = 0
    this.beatgrainCounterId = 0
    this.sampleCounterId = 0
    this.slicerCounterId = 0
    this.karplusCounterId = 0
    this.pitchshiftCounterId = 0
    this.slewCounterId = 0
    this.sahCounterId = 0
    this.envfollowCounterId = 0
    this.dcCounterId = 0
    this.bitcrusherCounterId = 0
    this.tanhCounterId = 0
    this.atanCounterId = 0
    this.absCounterId = 0
    this.sqrtCounterId = 0
    this.logCounterId = 0
    this.expCounterId = 0
    this.dbCounterId = 0
    this.floorCounterId = 0
    this.ceilCounterId = 0
    this.fractCounterId = 0
    this.signCounterId = 0
    this.softCounterId = 0
    this.asymCounterId = 0
    this.mtofCounterId = 0
    this.transposeCounterId = 0
    this.swapCounterId = 0
    this.selectCounterId = 0
    this.crossfadeCounterId = 0
    this.minCounterId = 0
    this.maxCounterId = 0
    this.clampCounterId = 0
    this.wrapCounterId = 0
    this.convCounterId = 0
    this.skipCounterId = 0
    this.everyCounterId = 0
    this.euclidCounterId = 0
    this.subdivideCounterId = 0
    this.timelineCounterId = 0
    this.sequenceCounterId = 0
    this.amplitudeCounterId = 0
  }

  private getCounterId(name: string): number {
    switch (name) {
      // Oscillators (share oscillators Map)
      case 'sin':
      case 'tri':
      case 'saw':
      case 'sqr':
      case 'ramp':
      case 'phasor':
      case 'pwm':
        return this.oscillatorCounterId++
      case 'lfosin':
      case 'lfotri':
      case 'lfosaw':
      case 'lforamp':
      case 'lfosqr':
      case 'lfopwm':
      case 'lfosah':
        return this.oscillatorCounterId++
      // Biquad filters - separate counters for each filter type
      case 'lp':
        return this.lpCounterId++
      case 'hp':
        return this.hpCounterId++
      case 'bp':
        return this.bpCounterId++
      case 'bs':
        return this.bsCounterId++
      case 'notch':
        return this.notchCounterId++
      case 'ls':
        return this.lsCounterId++
      case 'hs':
        return this.hsCounterId++
      case 'allpass':
        return this.allpassCounterId++
      case 'peak':
        return this.peakCounterId++
      case 'eq3':
        return this.eq3CounterId++
      // SVF filters (share svfFilters Map)
      case 'svflp':
      case 'svfhp':
      case 'svfbp':
      case 'svfnotch':
      case 'svfpeak':
      case 'svfallpass':
        return this.svfFilterCounterId++
      case 'diodeladder':
        return this.diodeladderCounterId++
      // Moog filters (share moogFilters Map)
      case 'mooglp':
      case 'mooghp':
        return this.moogFilterCounterId++
      case 'ffthp':
        return this.ffthpCounterId++
      case 'onepole':
        return this.onepoleCounterId++
      // Delay types (share delays Map)
      case 'delay':
      case 'beatdelay':
        return this.delayCounterId++
      case 'comb':
        return this.combCounterId++
      case 'chorus':
        return this.chorusCounterId++
      case 'flanger':
        return this.flangerCounterId++
      case 'dattorro':
        return this.dattorroCounterId++
      case 'freeverb':
        return this.freeverbCounterId++
      case 'fdn':
        return this.fdnCounterId++
      case 'fdn2':
        return this.fdn2CounterId++
      case 'velvet':
        return this.velvetCounterId++
      case 'lexicon':
        return this.lexiconCounterId++
      case 'spring':
        return this.springCounterId++
      case 'comp':
      case 'compressor':
        return this.compCounterId++
      case 'expander':
        return this.expanderCounterId++
      case 'gate':
        return this.gateCounterId++
      case 'limiter':
        return this.limiterCounterId++
      case 'vocoder':
        return this.vocoderCounterId++
      case 'side':
      case 'sidechain':
        return this.sidechainCounterId++
      // Envelope types (share envelopes Map)
      case 'ar':
      case 'adsr':
        return this.envelopeCounterId++
      case 'noise':
        return this.noiseCounterId++
      case 'harmonics':
        return this.harmonicsCounterId++
      case 'grain':
        return this.grainCounterId++
      case 'beatgrain':
      case 'beat':
        return this.beatgrainCounterId++
      case 'sample':
      case 'freesound':
      case 'sam':
      case 'espeak':
        return this.sampleCounterId++
      case 'slicer':
      case 'freesoundslicer':
        return this.slicerCounterId++
      case 'karplus':
        return this.karplusCounterId++
      case 'pitchshift':
        return this.pitchshiftCounterId++
      case 'slew':
        return this.slewCounterId++
      case 'sah':
        return this.sahCounterId++
      case 'envfollow':
        return this.envfollowCounterId++
      case 'dc':
        return this.dcCounterId++
      case 'bitcrusher':
        return this.bitcrusherCounterId++
      case 'tanh':
        return this.tanhCounterId++
      case 'atan':
        return this.atanCounterId++
      case 'abs':
        return this.absCounterId++
      case 'sqrt':
        return this.sqrtCounterId++
      case 'log':
        return this.logCounterId++
      case 'exp':
        return this.expCounterId++
      case 'db':
        return this.dbCounterId++
      case 'floor':
        return this.floorCounterId++
      case 'ceil':
        return this.ceilCounterId++
      case 'fract':
        return this.fractCounterId++
      case 'sign':
        return this.signCounterId++
      case 'soft':
        return this.softCounterId++
      case 'asym':
        return this.asymCounterId++
      case 'mtof':
        return this.mtofCounterId++
      case 'transpose':
        return this.transposeCounterId++
      case 'swap':
        return this.swapCounterId++
      case 'select':
        return this.selectCounterId++
      case 'crossfade':
        return this.crossfadeCounterId++
      case 'min':
        return this.minCounterId++
      case 'max':
        return this.maxCounterId++
      case 'clamp':
        return this.clampCounterId++
      case 'wrap':
        return this.wrapCounterId++
      case 'conv':
        return this.convCounterId++
      case 'skip':
        return this.skipCounterId++
      case 'every':
        return this.everyCounterId++
      case 'euclid':
        return this.euclidCounterId++
      case 'subdivide':
        return this.subdivideCounterId++
      case 'timeline':
        return this.timelineCounterId++
      case 'amplitude':
        return this.amplitudeCounterId++
      case 'waveform':
        return this.amplitudeCounterId++
      case 'level':
        return this.amplitudeCounterId++
      default:
        throw new Error(`Unknown block type for counter: ${name}`)
    }
  }

  private compilePatSeqInline(
    call: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Compile pat() or seq() inline when called inside a function
    // This creates a sequence, steps through it, calls the callback, and leaves the result on the stack
    // Generate a new voiceId for this inline sequence
    const inlineVoiceId = hashString(`v${this.idCounter++}`)

    if (call.name === 'pat') {
      let callbackArg = this.extractCallbackArg(call)
      const patArg = this.getCallArg(call, 'pat', 0)
      if (!patArg) {
        this.encoder.emit(OpCode.PUSH, 0)
        return
      }

      let swingArg = this.getCallArg(call, 'swing', 1)
      let offsetArg = this.getCallArg(call, 'offset', 2)
      const seedArg = this.getCallArg(call, 'seed', -1)
      const variationsArg = this.getCallArg(call, 'variations', -1)

      if (!callbackArg) {
        if (swingArg?.type === 'ArrowFunction') {
          callbackArg = swingArg
          swingArg = undefined
        }
        else if (offsetArg?.type === 'ArrowFunction') {
          callbackArg = offsetArg
          offsetArg = undefined
        }
      }
      else {
        if (swingArg?.type === 'ArrowFunction') swingArg = undefined
        if (offsetArg?.type === 'ArrowFunction') offsetArg = undefined
      }

      const swing = this.extractNumericArgValue(swingArg) ?? 0
      const offset = this.extractNumericArgValue(offsetArg) ?? 0
      const seed = seedArg?.type === 'Literal' && typeof seedArg.value === 'number' ? seedArg.value : undefined
      let variations = 1
      if (variationsArg?.type === 'Literal' && typeof variationsArg.value === 'number') {
        variations = Math.floor(variationsArg.value)
        if (variations < 1) {
          throw new Error(`pat() variations must be >= 1, got ${variations}`)
        }
      }

      let seqName: string
      let inlineSeqId: number | undefined

      let patternNode = patArg
      if (patArg.type === 'Identifier') {
        const resolved = this.expressionAssignments.get(patArg.name)
        if (!resolved || resolved.type !== 'Literal' || typeof resolved.value !== 'string') {
          const line = patArg.line
          const column = patArg.column
          const location = line && column ? ` at line ${line}, column ${column}` : ''
          throw new Error(
            `Pattern '${patArg.name}' not found or is not a string literal. `
              + `Define it first like ${patArg.name}='[bd sd]*2'.${location}`,
          )
        }
        patternNode = resolved
      }

      if (patternNode.type === 'Literal' && typeof patternNode.value === 'string') {
        const miniPattern = patternNode.value
        const originalPattern = miniPattern
        const lineOffset = patternNode.line || 0
        const columnOffset = (patternNode.column || 0) + 1

        const events = variations > 1
          ? expandPatternWithVariations(miniPattern, variations, seed, lineOffset, columnOffset, originalPattern)
          : expandPattern(miniPattern, seed, lineOffset, columnOffset, originalPattern)

        const patternParts: string[] = []
        for (const [time, values, duration, velocity, glide] of events) {
          let noteStr = ''
          if (values.length === 0) {
            noteStr = '_'
          }
          else if (values.length === 1) {
            noteStr = values[0]
          }
          else if (values.length > 1) {
            noteStr = values.join('')
          }

          let pattern = noteStr
          if (duration !== 1) {
            pattern += `:${duration}`
          }
          const velocityStr = (velocity === 0 || velocity === -1) ? '_' : String(velocity)
          if (glide > 0 || velocity !== 1) {
            if (duration === 1) {
              pattern += `:1@${velocityStr}`
            }
            else {
              pattern += `@${velocityStr}`
            }
          }
          if (glide > 0) {
            pattern += `\\${glide}`
          }

          patternParts.push(pattern)
        }

        const patternWithDurations = patternParts.join(' ')
        seqName = `inline_pat_fn_${this.idCounter++}`
        this.compileSequence(seqName, patternWithDurations || '~ ~', swing, offset)
        inlineSeqId = this.sequenceMap.get(seqName)
      }
      else if (patArg.type === 'Identifier') {
        seqName = patArg.name
        inlineSeqId = this.sequenceMap.get(seqName)
        if (inlineSeqId === undefined) {
          throw new Error(`Pattern '${seqName}' not found. Define it first as a string pattern.`)
        }
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0)
        return
      }

      if (inlineSeqId === undefined) {
        throw new Error(`Failed to compile pattern for '${seqName}'`)
      }

      if (!callbackArg) {
        throw new Error(`pat() requires a callback function`)
      }

      // Emit SEQ_STEP to get sequence values
      this.encoder.emit(OpCode.SEQ_STEP, inlineSeqId)

      // Store callback parameters
      const { impulseParam, velocityParam, pitchesParam } = this.extractCallbackParams(callbackArg, false)
      const { impulseVar: inlineImpulseVar, firstPitchVar: inlineFirstPitchVar } = this.storeCallbackParams(
        impulseParam,
        velocityParam,
        pitchesParam,
        inlineSeqId,
        false,
      )

      // Compile callback body - this should leave a value on the stack
      this.compileCallbackBody(callbackArg, inlineVoiceId, inlineImpulseVar, inlineFirstPitchVar, inlineSeqId)

      // Clean up callback parameters
      this.cleanupCallbackParams(impulseParam, velocityParam, pitchesParam)

      // Value from callback is now on the stack - leave it there for return
    }
    else if (call.name === 'seq') {
      const callbackArg = this.extractCallbackArg(call)
      const seqArg = this.getCallArg(call, 'seq', 0)
      if (!seqArg) {
        this.encoder.emit(OpCode.PUSH, 0)
        return
      }

      let swingArg = this.getCallArg(call, 'swing', 1)
      let offsetArg = this.getCallArg(call, 'offset', 2)

      if (swingArg?.type === 'ArrowFunction') {
        swingArg = offsetArg?.type !== 'ArrowFunction' ? this.getCallArg(call, 'swing', 2) : undefined
      }
      if (offsetArg?.type === 'ArrowFunction') {
        offsetArg = this.getCallArg(call, 'offset', 3)
      }

      const swing = this.extractNumericArgValue(swingArg) ?? 0
      const offset = this.extractNumericArgValue(offsetArg) ?? 0

      let seqName: string
      let inlineSeqId: number | undefined

      if (seqArg.type === 'Sequence') {
        const pattern = seqArg.pattern
        seqName = `inline_seq_fn_${this.idCounter++}`
        const line = seqArg.line
        const column = seqArg.column
        this.compileSequence(seqName, pattern, swing, offset, line, column)
        inlineSeqId = this.sequenceMap.get(seqName)
      }
      else if (seqArg.type === 'Literal' && typeof seqArg.value === 'string') {
        const pattern = seqArg.value
        seqName = `inline_seq_fn_${this.idCounter++}`
        this.compileSequence(seqName, pattern, swing, offset, seqArg.line, seqArg.column)
        inlineSeqId = this.sequenceMap.get(seqName)
      }
      else if (seqArg.type === 'Identifier') {
        seqName = seqArg.name
        inlineSeqId = this.sequenceMap.get(seqName)
        if (inlineSeqId === undefined) {
          throw new Error(`Sequence '${seqName}' not found. Define it first as a string pattern.`)
        }
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0)
        return
      }

      if (inlineSeqId === undefined) {
        throw new Error(`Failed to compile sequence for '${seqName}'`)
      }

      if (!callbackArg || callbackArg.type !== 'ArrowFunction') {
        throw new Error(
          `seq() requires a callback function (arrow function). Got: ${callbackArg ? callbackArg.type : 'undefined'}`,
        )
      }

      // Emit SEQ_STEP to get sequence values
      this.encoder.emit(OpCode.SEQ_STEP, inlineSeqId)

      // Store callback parameters
      const { impulseParam, velocityParam, pitchesParam } = this.extractCallbackParams(callbackArg, true)
      const { impulseVar: inlineImpulseVar, firstPitchVar: inlineFirstPitchVar } = this.storeCallbackParams(
        impulseParam,
        velocityParam,
        pitchesParam,
        inlineSeqId,
        true,
      )

      // Compile callback body - this should leave a value on the stack
      this.compileCallbackBody(callbackArg, inlineVoiceId, inlineImpulseVar, inlineFirstPitchVar, inlineSeqId)

      // Clean up callback parameters
      this.cleanupCallbackParams(impulseParam, velocityParam, pitchesParam)

      // Value from callback is now on the stack - leave it there for return
    }
  }

  private compileProgram(ast: ProgramNode) {
    // First pass: collect all assignments without compiling
    for (const stmt of ast.statements) {
      if (stmt.type === 'Call' && (stmt as any).name === 'post') {
        // Save post function for later
        const postArg = (stmt as any).args?.[0]?.value
        if (postArg?.type === 'ArrowFunction') {
          this.postFunction = postArg
        }
        continue
      }
      if (stmt.type === 'Assignment') {
        if (stmt.name === 'bpm') {
          // stmt.value is a Literal node with value property
          const bpmValue = (stmt.value as any).value
          this.bpm = bpmValue
          this.hasExplicitBpm = true
        }
        else if (stmt.name === 'tune') {
          // stmt.value is a Literal node with value property
          const tuneValue = (stmt.value as any).value
          this.tune = tuneValue
        }
        else if ((stmt.value as any).type === 'Sequence') {
          // Parser recognized it as a Sequence node
          const seqValue = (stmt.value as any).pattern
          if (typeof seqValue === 'string') {
            const seqNode = stmt.value as any
            const line = seqNode.line
            const column = seqNode.column
            this.compileSequence(stmt.name, seqValue, 0, 0, line, column)
          }
        }
        else if (
          (stmt.value as any).type === 'Literal'
          && typeof (stmt.value as any).value === 'string'
          && this.isNoteSequence((stmt.value as any).value)
        ) {
          // String literal that looks like a note sequence
          const literal = stmt.value as any
          this.compileSequence(stmt.name, literal.value, 0, 0, literal.line, literal.column)
        }
        else if ((stmt.value as any).type === 'Call'
          && ((stmt.value as any).name === 'seq' || (stmt.value as any).name === 'pat'))
        {
          // Track seq() and pat() assignments for compilation in second pass
          this.seqAssignments.set(stmt.name, stmt.value)
        }
        else if ((stmt.value as any).type === 'BinaryOp' && this.findSeqCall(stmt.value as any)) {
          // Track BinaryOp containing seq() (e.g., seq(...)*.7) for compilation in second pass
          this.seqAssignments.set(stmt.name, stmt.value)
        }
        else if ((stmt.value as any).type === 'Pipeline') {
          // Track Pipeline assignments that might contain seq() for compilation in second pass
          // Check if the pipeline contains a seq() call
          const pipeline = stmt.value as any
          const firstStage = pipeline.stages?.[0]
          if (
            firstStage
            && ((firstStage.type === 'Call' && (firstStage.name === 'seq' || firstStage.name === 'pat'))
              || (firstStage.type === 'BinaryOp' && this.findSeqCall(firstStage)))
          ) {
            this.seqAssignments.set(stmt.name, stmt.value)
          }
          else {
            // Pipeline without seq(), track as expression
            this.expressionAssignments.set(stmt.name, stmt.value)
          }
        }
        else if ((stmt.value as any).type === 'ArrowFunction') {
          // Track user-defined functions: sidechain=(in,chain)->...
          this.userFunctions.set(stmt.name, stmt.value)
        }
        else {
          // Track other expression assignments: main=hh+pad+bass+ping
          let value = stmt.value
          // If the value is a map expression, try to evaluate it at compile time
          if ((value as any).type === 'Call' && (value as any).name === 'map') {
            const mapCall = value as any
            const mapArray = mapCall.args?.[0]?.value
            const mapFn = mapCall.args?.[1]?.value
            if (mapArray?.type === 'ArrayLiteral' && mapFn) {
              const evaluated = this.tryEvaluateMap(mapArray, mapFn)
              if (evaluated) {
                value = evaluated
              }
            }
          }
          this.expressionAssignments.set(stmt.name, value)
        }
      }
    }

    // Second pass: compile seq() and pipeline assignments
    for (const stmt of ast.statements) {
      if (stmt.type === 'Assignment') {
        if ((stmt.value as any).type === 'Call'
          && ((stmt.value as any).name === 'seq' || (stmt.value as any).name === 'pat'))
        {
          // Compile seq() and pat() assignments as voices: kick=seq(...) or kick=pat(...)
          const seqPipeline = { stages: [stmt.value], assignmentName: stmt.name }
          this.compileVoice(seqPipeline)
        }
        else if ((stmt.value as any).type === 'BinaryOp' && this.findSeqCall(stmt.value as any)) {
          // Compile BinaryOp containing seq() (e.g., seq(...)*.7) as voices
          const seqPipeline = { stages: [stmt.value], assignmentName: stmt.name }
          this.compileVoice(seqPipeline)
        }
        else if ((stmt.value as any).type === 'Pipeline') {
          // Handle pipeline assignments: hh=seq(...) |> hp(...)
          const pipeline = stmt.value as any
          // Add assignment name to the pipeline for tracking
          pipeline.assignmentName = stmt.name
          this.compileVoice(pipeline)
        }
      }
      else if (stmt.type === 'Pipeline') {
        this.compileVoice(stmt)
      }
      else if (stmt.type !== 'Call' || (stmt as any).name !== 'post') {
        // Handle non-assignment, non-pipeline expressions that should produce output
        // Wrap them as a single-stage pipeline
        const pipeline = { stages: [stmt], assignmentName: null }
        this.compileVoice(pipeline)
      }
    }

    // Third pass: compile post() if it exists
    if (this.postFunction) {
      this.compilePostProcessing(this.postFunction)
    }
  }

  private isNoteSequence(str: string): boolean {
    // Check if string contains note-like patterns: _:duration@velocity or note:duration@velocity
    return /[a-g_].*:.*@/.test(str)
  }

  private compileSequence(name: string, notation: string, swing: number = 0, offset: number = 0, line?: number,
    column?: number)
  {
    const notes = notation.split(/\s+/).filter(n => n.length > 0)
    const noteData: NoteData[] = []
    // Use counter-based ID if not already assigned, otherwise reuse existing ID
    let seqId = this.sequenceMap.get(name)
    if (seqId === undefined) {
      seqId = this.sequenceCounterId++
      this.sequenceMap.set(name, seqId)
    }

    let maxPitches = 0
    const baseColumn = column !== undefined ? column + 1 : 1 // column points to quote, content starts after
    const currentLine = line || 1

    // Parse character-by-character to find note positions
    const notePositions: Array<{ note: string; column: number; length: number }> = []
    if (line !== undefined && column !== undefined) {
      let i = 0
      while (i < notation.length) {
        const char = notation[i]

        // Skip whitespace
        if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
          i++
          continue
        }

        // Found start of a note - capture it
        const noteStart = i
        const noteColumn = baseColumn + i
        let noteText = ''

        // Read until whitespace or end
        while (i < notation.length && notation[i] !== ' ' && notation[i] !== '\t' && notation[i] !== '\n'
          && notation[i] !== '\r')
        {
          noteText += notation[i]
          i++
        }

        if (noteText.length > 0) {
          notePositions.push({ note: noteText, column: noteColumn, length: noteText.length })
        }
      }
    }

    for (let i = 0; i < notes.length; i++) {
      const note = notes[i]
      const [pitchPart, rest] = note.split(':')
      const [durationPart, velocityHoldGlidePart] = rest?.split('@') || ['1', '1']

      // Split velocity/hold from glide: velocity,hold\glide
      const [velocityHoldPart, glidePart] = velocityHoldGlidePart?.split('\\') || [velocityHoldGlidePart, undefined]

      // Split velocity and hold: velocity,hold
      const velocityHoldParts = velocityHoldPart?.split(',') || []
      const velocityPart = velocityHoldParts[0] || '1'
      const holdPart = velocityHoldParts[1] // undefined if no comma

      const pitches = this.parseNote(pitchPart)
      const duration = this.parseDuration(durationPart || '1')
      // Allow underscore as velocity to mean "no trigger"
      const velocity = velocityPart === '_' ? -1 : parseFloat(velocityPart || '1')
      const noteHold = holdPart !== undefined ? parseFloat(holdPart) : 0
      const glide = glidePart !== undefined ? parseFloat(glidePart) : 0

      maxPitches = Math.max(maxPitches, pitches.length)

      noteData.push({
        pitches: new Float32Array(pitches),
        duration,
        velocity,
        hold: noteHold,
        glide,
      })

      // Create pattern event if we have position info
      // The notePositions array should match the notes array in order
      if (i < notePositions.length) {
        const pos = notePositions[i]
        const adjustedLine = currentLine > 0 ? currentLine - this.preludeLineCount : currentLine
        this.patternEvents.push({
          line: adjustedLine,
          column: pos.column,
          length: pos.length,
          type: pitchPart === '_' ? 'rest' : 'note',
          sequenceId: seqId,
          noteIndex: i,
        })
      }
    }

    // Store max pitch count for this sequence
    this.sequenceMaxPitches.set(seqId, maxPitches)

    this.sequences.push({ id: seqId, notes: noteData, swing, offset })
  }

  private parseNote(pitch: string): number[] {
    // Treat rests ("_") as having no pitches so callbacks like pitches.map()
    // see an initially empty array that sums to 0 on rests.
    if (pitch === '_') return []

    const matches = pitch.match(/[a-g][#b]?\d+/gi)
    if (!matches) {
      // Check if it looks like it might be a note but is invalid
      if (/[a-z]/i.test(pitch) && /\d/.test(pitch)) {
        throw new Error(
          `Invalid note syntax "${pitch}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`,
        )
      }
      return [0]
    }

    // Validate that the entire pitch string is valid (no unmatched characters)
    const matchedString = matches.join('')
    if (matchedString !== pitch) {
      throw new Error(
        `Invalid note syntax "${pitch}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`,
      )
    }

    return matches.map(note => {
      const noteNames = ['c', 'c#', 'd', 'd#', 'e', 'f', 'f#', 'g', 'g#', 'a', 'a#', 'b']
      const match = note.match(/([a-g][#b]?)(\d+)/i)
      if (!match) return 0

      let noteName = match[1].toLowerCase()
      let octave = parseInt(match[2])

      // Handle sharp notes (for consistency, even e# and b#)
      if (noteName.endsWith('#')) {
        const baseNote = noteName[0]
        const baseIndex = noteNames.indexOf(baseNote)
        if (baseIndex !== -1 && baseIndex < noteNames.length - 1) {
          noteName = noteNames[baseIndex + 1]
        }
        else if (baseNote === 'b') {
          // b# -> c (next octave)
          noteName = 'c'
          octave++
        }
        else if (baseNote === 'e') {
          // e# -> f
          noteName = 'f'
        }
      }

      // Handle flat notes (for consistency, even cb and fb)
      if (noteName.endsWith('b')) {
        const baseNote = noteName[0]
        const baseIndex = noteNames.indexOf(baseNote)
        if (baseIndex > 0) {
          noteName = noteNames[baseIndex - 1]
        }
        else if (baseNote === 'c') {
          // cb -> b (previous octave)
          noteName = 'b'
          octave--
        }
        else if (baseNote === 'f') {
          // fb -> e
          noteName = 'e'
        }
      }

      const noteIndex = noteNames.indexOf(noteName)
      if (noteIndex === -1) return 0

      const midiNote = (octave + 1) * 12 + noteIndex
      return 440 * Math.pow(2, (midiNote - 69) / 12)
    })
  }

  private parseDuration(duration: string): number {
    try {
      return Function(`"use strict"; return (${duration})`)()
    }
    catch {
      return 1
    }
  }

  private getKnownFunctions(): Set<string> {
    if (!this.cachedKnownFunctions) {
      this.cachedKnownFunctions = new Set(getAllFunctionNames())
      this.cachedKnownFunctions.add('out')
      this.cachedKnownFunctions.add('solo')
      this.cachedKnownFunctions.add('post')
      this.cachedKnownFunctions.add('seq')
      this.cachedKnownFunctions.add('pat')
      this.cachedKnownFunctions.add('map')
      this.cachedKnownFunctions.add('sum')
    }
    return this.cachedKnownFunctions
  }

  private extractNumericArgValue(arg: any): number | null {
    if (!arg) return null
    if (arg.type === 'Literal' && typeof arg.value === 'number') {
      return arg.value
    }
    if (
      arg.type === 'BinaryOp'
      && arg.operator === '-'
      && arg.left.type === 'Literal'
      && arg.left.value === 0
      && arg.right.type === 'Literal'
    ) {
      return -arg.right.value
    }
    return null
  }

  private extractCallbackArg(call: any): any {
    let callbackArg = this.getCallArg(call, 'cb', 3)
      || this.getCallArg(call, 'cb', 2)
      || this.getCallArg(call, 'cb', 1)
    if (!callbackArg) {
      const callbackArgNode = call.args?.find(
        (arg: any) => arg.value?.type === 'ArrowFunction',
      )
      callbackArg = callbackArgNode?.value
    }
    return callbackArg
  }

  private extractCallbackParams(callback: any, hasVelocity: boolean): {
    impulseParam: string
    velocityParam?: string
    pitchesParam?: string
  } {
    const impulseParam = callback.params[0]
    let velocityParam: string | undefined
    let pitchesParam: string | undefined

    if (callback.params.length === 2) {
      velocityParam = callback.params[1]
    }
    else if (callback.params.length === 3) {
      velocityParam = callback.params[1]
      pitchesParam = callback.params[2]
    }

    return { impulseParam, velocityParam, pitchesParam }
  }

  private storeCallbackParams(
    impulseParam: string,
    velocityParam: string | undefined,
    pitchesParam: string | undefined,
    seqId: number,
    hasVelocity: boolean,
  ): { impulseVar: number; firstPitchVar: number } {
    const impulseVar = this.varCounter++
    this.symbols.set(impulseParam, impulseVar)
    this.encoder.emit(OpCode.STORE, impulseVar)

    if (velocityParam) {
      const velocityVar = this.varCounter++
      this.symbols.set(velocityParam, velocityVar)
      this.encoder.emit(OpCode.STORE, velocityVar)
    }
    else if (hasVelocity) {
      this.encoder.emit(OpCode.POP)
    }

    const pitchCount = this.sequenceMaxPitches.get(seqId) || 1
    const firstPitchVar = pitchesParam ? this.varCounter : -1

    if (pitchesParam) {
      for (let i = 0; i < pitchCount; i++) {
        const pitchVar = this.varCounter++
        this.encoder.emit(OpCode.STORE, pitchVar)
      }
      this.symbols.set(pitchesParam, firstPitchVar)
    }
    else {
      for (let i = 0; i < pitchCount; i++) {
        this.encoder.emit(OpCode.POP)
      }
    }

    return { impulseVar, firstPitchVar }
  }

  private cleanupCallbackParams(
    impulseParam: string,
    velocityParam: string | undefined,
    pitchesParam: string | undefined,
  ) {
    if (impulseParam && impulseParam !== '_') {
      this.symbols.delete(impulseParam)
    }
    if (velocityParam && velocityParam !== '_') {
      this.symbols.delete(velocityParam)
    }
    if (pitchesParam && pitchesParam !== '_') {
      this.symbols.delete(pitchesParam)
    }
  }

  private compilePipelineStages(
    pipeline: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
    assignmentName: string | null,
  ) {
    const stages = pipeline.stages.slice(1)
    const endsWithOut = stages.length > 0
      && stages[stages.length - 1].type === 'Call'
      && stages[stages.length - 1].name === 'out'
    const stagesBeforeOut = endsWithOut ? stages.slice(0, -1) : stages
    const outStage = endsWithOut ? stages[stages.length - 1] : null

    for (let i = 0; i < stagesBeforeOut.length; i++) {
      this.compilePipelineStage(
        stagesBeforeOut[i],
        0,
        voiceId,
        impulseVar,
        pitchesVar,
        seqId,
      )
    }

    if (assignmentName) {
      const signalVar = this.varCounter++
      this.symbols.set(assignmentName, signalVar)
      this.encoder.emit(OpCode.DUP)
      this.encoder.emit(OpCode.STORE, signalVar)
    }

    if (outStage) {
      this.compilePipelineStage(
        outStage,
        0,
        voiceId,
        impulseVar,
        pitchesVar,
        seqId,
      )
    }
  }

  private compileVoice(pipeline: any) {
    let firstStage = pipeline.stages[0]
    let assignmentName: string | null = pipeline.assignmentName || null
    let postSeqOperation: any = null

    // Handle identifier reference to assignments: kick |> out(...) or main |> out(...)
    if (firstStage.type === 'Identifier') {
      // Don't set assignmentName from first stage - it should only be set from explicit assignments
      // (e.g., rumble=kick |> ... sets assignmentName to 'rumble', not 'kick')
      // Standalone pipelines like 'kick |> tanh(...)' should not assign back to 'kick'
      if (!this.symbols.has(firstStage.name)) {
        const seqCall = this.seqAssignments.get(firstStage.name)
        const exprValue = this.expressionAssignments.get(firstStage.name)
        if (seqCall) {
          firstStage = seqCall
        }
        else if (exprValue) {
          firstStage = exprValue
        }
        else {
          if (firstStage.name === 't' || firstStage.name === 'co' || firstStage.name === '_') {
            // Valid built-in identifiers
          }
          else {
            const knownFunctions = this.getKnownFunctions()
            if (knownFunctions.has(firstStage.name)) {
              throw new Error(
                `Function '${firstStage.name}' must be called with parentheses: ${firstStage.name}(...)`,
              )
            }
            const line = (firstStage as any).line
            const column = (firstStage as any).column
            const location = line && column ? ` at line ${line}, column ${column}` : ''
            throw new Error(`Undefined variable: ${firstStage.name}${location}`)
          }
        }
      }
    }

    // Handle BinaryOp wrapping a seq() call: seq(...)*2.9 |> out(...)
    if (firstStage.type === 'BinaryOp') {
      const seqCall = this.findSeqCall(firstStage)
      if (seqCall) {
        // Extract the seq() call and remember the operation to apply after
        postSeqOperation = this.replaceSeqWithPlaceholder(firstStage)
        firstStage = seqCall
      }
    }

    // Handle sequences: seq(sequence, callback) |> ... or seq(seq:..., swing:..., offset:..., cb:...)
    if (firstStage.type === 'Call' && firstStage.name === 'seq') {
      const callbackArg = this.extractCallbackArg(firstStage)
      const seqArg = this.getCallArg(firstStage, 'seq', 0)
      if (!seqArg) return

      let swingArg = this.getCallArg(firstStage, 'swing', 1)
      let offsetArg = this.getCallArg(firstStage, 'offset', 2)

      if (swingArg?.type === 'ArrowFunction') {
        swingArg = offsetArg?.type !== 'ArrowFunction' ? this.getCallArg(firstStage, 'swing', 2) : undefined
      }
      if (offsetArg?.type === 'ArrowFunction') {
        offsetArg = this.getCallArg(firstStage, 'offset', 3)
      }

      const swing = this.extractNumericArgValue(swingArg) ?? 0
      const offset = this.extractNumericArgValue(offsetArg) ?? 0

      let seqName: string
      let seqId: number | undefined

      // Check if it's an inline Sequence node (parser converts string patterns to Sequence nodes)
      if (seqArg.type === 'Sequence') {
        // Compile inline sequence pattern
        const pattern = seqArg.pattern
        // Use counter-based ID for inline sequences
        seqName = `inline_seq_${this.sequenceCounterId}`
        const line = seqArg.line
        const column = seqArg.column
        this.compileSequence(seqName, pattern, swing, offset, line, column)
        seqId = this.sequenceMap.get(seqName)
      }
      else if (seqArg.type === 'Literal' && typeof seqArg.value === 'string') {
        // Compile inline sequence pattern (if not already parsed as Sequence)
        const pattern = seqArg.value
        // Use counter-based ID for inline sequences
        seqName = `inline_seq_${this.sequenceCounterId}`
        this.compileSequence(seqName, pattern, swing, offset, seqArg.line, seqArg.column)
        seqId = this.sequenceMap.get(seqName)
      }
      else if (seqArg.type === 'Identifier') {
        // Reference to a pre-defined sequence
        seqName = seqArg.name
        seqId = this.sequenceMap.get(seqName)
        if (seqId === undefined) {
          throw new Error(`Sequence '${seqName}' not found. Define it first as a string pattern.`)
        }
        // Update swing and offset for existing sequence
        const seqData = this.sequences.find(s => s.id === seqId)
        if (seqData) {
          ;(seqData as any).swing = swing
          ;(seqData as any).offset = offset
        }
      }
      else {
        return
      }

      if (seqId === undefined) {
        throw new Error(`Failed to compile sequence for '${seqName}'`)
      }

      if (!callbackArg || callbackArg.type !== 'ArrowFunction') {
        throw new Error(
          `seq() requires a callback function (arrow function). Got: ${callbackArg ? callbackArg.type : 'undefined'}`,
        )
      }

      const voiceId = hashString(`v${this.idCounter++}`)
      this.encoder.emit(OpCode.SEQ_STEP, seqId)

      const { impulseParam, velocityParam, pitchesParam } = this.extractCallbackParams(callbackArg, true)
      const { impulseVar, firstPitchVar } = this.storeCallbackParams(
        impulseParam,
        velocityParam,
        pitchesParam,
        seqId,
        true,
      )

      this.compileCallbackBody(callbackArg, voiceId, impulseVar, firstPitchVar, seqId)

      if (postSeqOperation) {
        this.applyPostSeqOperation(postSeqOperation, voiceId, impulseVar, firstPitchVar, seqId)
      }

      this.cleanupCallbackParams(impulseParam, velocityParam, pitchesParam)
      this.compilePipelineStages(pipeline, voiceId, impulseVar, firstPitchVar, seqId, assignmentName)
    }
    // Handle patterns: pat(pattern, callback) |> ... or pat(pat:..., swing:..., offset:..., cb:...)
    else if (firstStage.type === 'Call' && firstStage.name === 'pat') {
      let callbackArg = this.extractCallbackArg(firstStage)
      const patArg = this.getCallArg(firstStage, 'pat', 0)
      if (!patArg) return

      let swingArg = this.getCallArg(firstStage, 'swing', 1)
      let offsetArg = this.getCallArg(firstStage, 'offset', 2)
      const seedArg = this.getCallArg(firstStage, 'seed', -1)
      const variationsArg = this.getCallArg(firstStage, 'variations', -1)

      if (!callbackArg) {
        if (swingArg?.type === 'ArrowFunction') {
          callbackArg = swingArg
          swingArg = undefined
        }
        else if (offsetArg?.type === 'ArrowFunction') {
          callbackArg = offsetArg
          offsetArg = undefined
        }
      }
      else {
        if (swingArg?.type === 'ArrowFunction') swingArg = undefined
        if (offsetArg?.type === 'ArrowFunction') offsetArg = undefined
      }

      const swing = this.extractNumericArgValue(swingArg) ?? 0
      const offset = this.extractNumericArgValue(offsetArg) ?? 0
      const seed = seedArg?.type === 'Literal' && typeof seedArg.value === 'number' ? seedArg.value : undefined
      let variations = 1
      if (variationsArg?.type === 'Literal' && typeof variationsArg.value === 'number') {
        variations = Math.floor(variationsArg.value)
        if (variations < 1) {
          throw new Error(`pat() variations must be >= 1, got ${variations}`)
        }
      }

      let seqName: string
      let seqId: number | undefined

      // Parse Mini-Notation pattern and convert to sequence.
      // Support both inline string literals and named pattern variables:
      //   hatPattern='[.3 .3 .4 .3 [.4 .5 .6 .3]]*2'
      //   hats=pat(hatPattern, (trig,v)->...)
      let patternNode = patArg

      // If the argument is an identifier, try to resolve it from expression assignments
      if (patArg.type === 'Identifier') {
        const resolved = this.expressionAssignments.get(patArg.name)
        if (!resolved || resolved.type !== 'Literal' || typeof resolved.value !== 'string') {
          const line = patArg.line
          const column = patArg.column
          const location = line && column ? ` at line ${line}, column ${column}` : ''
          throw new Error(
            `Pattern '${patArg.name}' not found or is not a string literal. `
              + `Define it first like ${patArg.name}='[bd sd]*2'.${location}`,
          )
        }
        patternNode = resolved
      }

      if (patternNode.type === 'Literal' && typeof patternNode.value === 'string') {
        const miniPattern = patternNode.value
        const originalPattern = miniPattern // The value is already unquoted (content between quotes)
        const lineOffset = patternNode.line || 0
        // Column offset: token.column is where quote is, so content starts at column + 1
        const columnOffset = (patternNode.column || 0) + 1

        // Parse pattern to get events with position info
        const seq = parsePattern(miniPattern, lineOffset, columnOffset, originalPattern)

        // Collect pattern events for UI - only individual notes/rests/chords, not nested sequences
        // Track them temporarily with replication info for mapping to sequence notes
        const tempPatternEvents: Array<
          { line: number; column: number; length: number; type: 'note' | 'rest' | 'chord'; patternEventIndex: number;
            replicate: number; multiplier: number }
        > = []
        const collectEvents = (events: PatternEvent[], patternEventIndex: { value: number }) => {
          for (const event of events) {
            // Handle nested sequences
            if ((event as any).nested) {
              const nested = (event as any).nested
              // For nested alternating patterns, treat them as a single element
              // (don't recursively collect their events, as they alternate at runtime)
              if (nested.type === 'alternating') {
                // Treat nested alternating pattern as a single pattern element
                const adjustedLine = event.line > 0 ? event.line - this.preludeLineCount : event.line
                tempPatternEvents.push({
                  line: adjustedLine,
                  column: event.column,
                  length: event.length,
                  type: 'note', // Treat as note for display purposes
                  patternEventIndex: patternEventIndex.value++,
                  replicate: event.replicate || 1,
                  multiplier: event.multiplier || 1,
                })
              }
              else {
                // For non-alternating nested sequences, recursively collect their events
                collectEvents(nested.events, patternEventIndex)
              }
              continue
            }

            // Only collect actual notes, rests, or chords (not structural elements)
            if (event.type === 'note' || event.type === 'rest' || event.type === 'chord') {
              // Adjust line number by subtracting prelude lines (AST was parsed from combined source)
              const adjustedLine = event.line > 0 ? event.line - this.preludeLineCount : event.line
              tempPatternEvents.push({
                line: adjustedLine,
                column: event.column,
                length: event.length,
                type: event.type,
                patternEventIndex: patternEventIndex.value++,
                replicate: event.replicate || 1,
                multiplier: event.multiplier || 1,
              })
            }
          }
        }
        const patternEventIndex = { value: 0 }
        collectEvents(seq.events, patternEventIndex)

        // Parse Mini-Notation to get timed events (with variations if requested)
        const events = variations > 1
          ? expandPatternWithVariations(miniPattern, variations, seed, lineOffset, columnOffset, originalPattern)
          : expandPattern(miniPattern, seed, lineOffset, columnOffset, originalPattern)

        // Convert to sequence format (note:duration@velocity,hold)
        const patternParts: string[] = []

        for (const [time, values, duration, velocity, glide, hold] of events) {
          let noteStr = ''
          if (values.length === 0) {
            // Rest - use underscore so it doesn't trigger callbacks
            noteStr = '_'
          }
          else if (values.length === 1) {
            noteStr = values[0]
          }
          else if (values.length > 1) {
            // Chord notation: concatenate notes like seq() expects (c2g2c3, not [c2,g2,c3])
            noteStr = values.join('')
          }

          // Build: note:duration@velocity,hold\glide
          let pattern = noteStr
          if (duration !== 1) {
            pattern += `:${duration}`
          }
          // If glide is present, always include @velocity (even if velocity is 1)
          // so the parser can properly split velocity from glide
          // For rests (velocity 0 or -1), use '_' so it doesn't trigger callbacks
          const velocityStr = (velocity === 0 || velocity === -1) ? '_' : String(velocity)
          if (glide > 0 || velocity !== 1 || hold > 0) {
            if (duration === 1) {
              pattern += `:1@${velocityStr}`
            }
            else {
              pattern += `@${velocityStr}`
            }
          }
          // Add hold value after velocity (format: @velocity,hold)
          if (hold > 0) {
            pattern += `,${hold}`
          }
          if (glide > 0) {
            pattern += `\\${glide}`
          }

          patternParts.push(pattern)
        }

        const patternWithDurations = patternParts.join(' ')

        // Use counter-based ID for inline patterns
        seqName = `inline_pat_${this.sequenceCounterId}`

        this.compileSequence(seqName, patternWithDurations || '~ ~', swing, offset)
        seqId = this.sequenceMap.get(seqName)

        // Calculate cycle length for nested alternating patterns
        // If the pattern was expanded to multiple cycles, store the original cycle length
        let cycleLength: number | undefined = undefined
        if (seqId !== undefined) {
          const originalPatternLength = tempPatternEvents.length
          const expandedSequenceLength = events.length
          const notesPerCycle = expandedSequenceLength / (variations > 1 ? variations : 1)
          if (notesPerCycle > 0 && notesPerCycle % originalPatternLength === 0
            && notesPerCycle > originalPatternLength)
          {
            // Pattern was expanded to multiple cycles for nested alternation
            // Store the original cycle length so sequencer can loop at that boundary
            cycleLength = originalPatternLength
          }
        }

        // Map pattern events to sequence notes
        // Create pattern events for each expanded note, mapping back to original pattern elements
        if (seqId !== undefined && tempPatternEvents.length > 0) {
          // Find the sequence we just created (it's the last one with this ID)
          const finalSeq = this.sequences.filter(s => s.id === seqId).pop()
          if (finalSeq) {
            const originalPatternLength = tempPatternEvents.length
            const expandedSequenceLength = events.length
            const notesPerCycle = expandedSequenceLength / (variations > 1 ? variations : 1)
            const multiplier = seq.multiplier || 1

            // For nested alternating patterns, we expand to multiple cycles to show alternation
            // The "original cycle" is the base pattern length before expansion for nested alternation
            // Calculate notesPerOriginalCycle: if expanded length is a multiple of original length,
            // use original length; otherwise use the calculated value from multiplier
            let notesPerOriginalCycle: number
            if (notesPerCycle > 0 && notesPerCycle % originalPatternLength === 0) {
              // Pattern was expanded to multiple cycles (likely for nested alternation)
              // Use the original pattern length as the cycle length
              notesPerOriginalCycle = originalPatternLength
            }
            else {
              // Normal case: use multiplier-based calculation
              notesPerOriginalCycle = notesPerCycle / multiplier
            }

            // Calculate how many expanded notes each original element produces
            const notesPerOriginalElement: number[] = []
            for (const tempEvent of tempPatternEvents) {
              const repeatCount = tempEvent.multiplier * tempEvent.replicate
              notesPerOriginalElement.push(repeatCount)
            }

            // Create pattern events for each expanded note
            for (let expandedNoteIndex = 0; expandedNoteIndex < expandedSequenceLength; expandedNoteIndex++) {
              // Calculate position within the original cycle (handles multipliers and nested alternation)
              const positionInOriginalCycle = expandedNoteIndex % notesPerOriginalCycle

              // Find which original element this note came from by counting notes
              let noteCount = 0
              let originalElementIndex = 0
              for (let i = 0; i < originalPatternLength; i++) {
                const notesForThisElement = notesPerOriginalElement[i]
                if (positionInOriginalCycle < noteCount + notesForThisElement) {
                  originalElementIndex = i
                  break
                }
                noteCount += notesForThisElement
              }

              const originalEvent = tempPatternEvents[originalElementIndex]
              if (originalEvent) {
                // Map this expanded note back to the original pattern element
                // For nested alternating patterns, multiple expanded notes map to the same element
                this.patternEvents.push({
                  line: originalEvent.line,
                  column: originalEvent.column,
                  length: originalEvent.length,
                  type: originalEvent.type,
                  sequenceId: seqId,
                  noteIndex: expandedNoteIndex,
                })
              }
            }
          }

          // Update sequence with cycleLength if we calculated it
          if (cycleLength !== undefined && finalSeq) {
            finalSeq.cycleLength = cycleLength
          }
        }
      }
      else if (patArg.type === 'Identifier') {
        // Reference to a pre-defined pattern
        seqName = patArg.name
        seqId = this.sequenceMap.get(seqName)
        if (seqId === undefined) {
          throw new Error(`Pattern '${seqName}' not found. Define it first as a string pattern.`)
        }
        // Update swing and offset for existing sequence
        const seqData = this.sequences.find(s => s.id === seqId)
        if (seqData) {
          ;(seqData as any).swing = swing
          ;(seqData as any).offset = offset
        }
      }
      else {
        return
      }

      if (seqId === undefined) {
        throw new Error(`Failed to compile pattern for '${seqName}'`)
      }

      if (!callbackArg) {
        throw new Error(`pat() requires a callback function`)
      }

      const voiceId = hashString(`v${this.idCounter++}`)
      this.encoder.emit(OpCode.SEQ_STEP, seqId)

      const { impulseParam, velocityParam, pitchesParam } = this.extractCallbackParams(callbackArg, false)
      const { impulseVar, firstPitchVar } = this.storeCallbackParams(
        impulseParam,
        velocityParam,
        pitchesParam,
        seqId,
        false,
      )

      this.compileCallbackBody(callbackArg, voiceId, impulseVar, firstPitchVar, seqId)

      if (postSeqOperation) {
        this.applyPostSeqOperation(postSeqOperation, voiceId, impulseVar, firstPitchVar, seqId)
      }

      this.cleanupCallbackParams(impulseParam, velocityParam, pitchesParam)
      this.compilePipelineStages(pipeline, voiceId, impulseVar, firstPitchVar, seqId, assignmentName)
    }
    else {
      // Handle simple pipelines without sequences: saw(hz:123) |> out(L:%,R:%) or main |> out(L:%,R:%)
      const voiceId = hashString(`v${this.idCounter++}`)
      const impulseVar = -1
      const pitchesVar = -1
      const seqId = 0

      this.compileExpression(firstStage, voiceId, impulseVar, pitchesVar, seqId)
      this.compilePipelineStages(pipeline, voiceId, impulseVar, pitchesVar, seqId, assignmentName)
    }
  }

  private compileCallbackBody(
    callback: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Save current symbols to restore after callback (for proper scoping)
    const savedSymbols = new Map(this.symbols)

    const body = callback.body
    if (body.type === 'Block') {
      this.compileBlock(body, voiceId, impulseVar, pitchesVar, seqId)
    }
    else {
      // Single expression
      this.compileExpression(body, voiceId, impulseVar, pitchesVar, seqId)
    }

    // Restore symbols to remove any variables declared inside callback
    this.symbols = savedSymbols
  }

  private compileBlock(
    block: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    if (!block || !block.statements) {
      this.encoder.emit(OpCode.PUSH, 0)
      return
    }

    const statements = block.statements

    // Handle empty block
    if (!Array.isArray(statements) || statements.length === 0) {
      this.encoder.emit(OpCode.PUSH, 0)
      return
    }

    // Compile all statements except the last one
    for (let i = 0; i < statements.length - 1; i++) {
      const stmt = statements[i]
      if (!stmt) continue

      if (stmt.type === 'Assignment') {
        this.compileExpression(stmt.value, voiceId, impulseVar, pitchesVar, seqId)
        const varSlot = this.varCounter++
        this.symbols.set(stmt.name, varSlot)
        this.encoder.emit(OpCode.STORE, varSlot)
      }
      else if (stmt.type === 'Return') {
        this.compileExpression(stmt.value, voiceId, impulseVar, pitchesVar, seqId)
        // Return statement always exits
        return
      }
      else {
        // Any other expression - compile it and pop the result
        // Note: compileExpression should leave a value, but if it doesn't (e.g., empty pipeline),
        // we need to handle it. For now, we'll pop and if there's nothing, that's okay.
        this.compileExpression(stmt, voiceId, impulseVar, pitchesVar, seqId)
        // Pop the result - if there's nothing on the stack, this might cause issues,
        // but for valid expressions this should be fine
        this.encoder.emit(OpCode.POP)
      }
    }

    // Compile the last statement - this becomes the return value
    const lastStmt = statements[statements.length - 1]
    if (!lastStmt) {
      // Last statement is null/undefined, push 0
      this.encoder.emit(OpCode.PUSH, 0)
      return
    }

    if (lastStmt.type === 'Assignment') {
      this.compileExpression(lastStmt.value, voiceId, impulseVar, pitchesVar, seqId)
      const varSlot = this.varCounter++
      this.symbols.set(lastStmt.name, varSlot)
      this.encoder.emit(OpCode.STORE, varSlot)
      // Assignments don't return a value
      this.encoder.emit(OpCode.PUSH, 0)
    }
    else if (lastStmt.type === 'Return') {
      this.compileExpression(lastStmt.value, voiceId, impulseVar, pitchesVar, seqId)
      // Return statement always exits
      return
    }
    else {
      // Any other expression - compile it and leave it on the stack as the return value
      // This MUST leave a value on the stack - it's the return value of the block
      // For most expression types (BinaryOp, Call, etc.), compileExpression always leaves one value
      this.compileExpression(lastStmt, voiceId, impulseVar, pitchesVar, seqId)
    }
  }

  private compileExpression(
    expr: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    if (expr.type === 'Pipeline') {
      // Compile pipeline stages: compile first stage, then apply remaining stages
      if (expr.stages.length === 0) return

      // Save the outer % symbol if it exists (for nested pipelines)
      const outerPercentVar = this.symbols.get('%')

      // Compile the first stage
      this.compileExpression(expr.stages[0], voiceId, impulseVar, pitchesVar, seqId)

      // Apply remaining pipeline stages
      for (let i = 1; i < expr.stages.length; i++) {
        this.compilePipelineStage(
          expr.stages[i],
          0, // stageId no longer used, counter-based IDs are generated inside
          voiceId,
          impulseVar,
          pitchesVar,
          seqId,
        )
      }

      // Restore the outer % symbol if it existed (for nested pipelines)
      if (outerPercentVar !== undefined) {
        this.symbols.set('%', outerPercentVar)
      }
    }
    else if (expr.type === 'ArrayLiteral') {
      // Check if this array might be used in a method call chain
      // If so, push it as a first-class array value
      // Otherwise, use the old behavior (push elements for operations like sum)
      const willBeChained = this.willArrayBeChained(expr)

      if (willBeChained) {
        // Push array as first-class value using PUSH_ARRAY
        this.pushArrayLiteral(expr, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        // Old behavior: push elements for operations
        const isNoteArray = expr.isNoteArray === true
        for (let i = expr.elements.length - 1; i >= 0; i--) {
          const element = expr.elements[i]
          // Apply tune to note array elements
          if (isNoteArray && element.type === 'Literal' && typeof element.value === 'number') {
            this.encoder.emit(OpCode.PUSH, element.value * this.tune)
          }
          else {
            this.compileExpression(element, voiceId, impulseVar, pitchesVar, seqId)
          }
        }
        // Push array count
        this.encoder.emit(OpCode.PUSH, expr.elements.length)
      }
    }
    else if (expr.type === 'Call') {
      // Parser puts method call object in args[0].value
      let callObject = expr.args?.[0]?.value

      // Check if this is <pitches-var>.map(...) or <pitches-var>.sum() - do this early
      if (expr.name === 'map' && callObject?.type === 'Identifier') {
        const identifierName = callObject.name
        if (pitchesVar >= 0 && this.symbols.get(identifierName) === pitchesVar) {
          this.compileMapSum(expr, voiceId, impulseVar, pitchesVar, seqId)
          return
        }
      }

      // Check if this is <pitches-var>.map(...).sum()
      if (expr.name === 'sum' && callObject?.type === 'Call' && callObject.name === 'map') {
        const mapCallObject = callObject.args?.[0]?.value
        if (mapCallObject?.type === 'Identifier' && pitchesVar >= 0) {
          const identifierName = mapCallObject.name
          if (this.symbols.get(identifierName) === pitchesVar) {
            this.compileMapSum(callObject, voiceId, impulseVar, pitchesVar, seqId)
            return
          }
        }
      }

      // Resolve identifiers to their values (after chaining detection)
      if (callObject?.type === 'Identifier') {
        const resolvedValue = this.expressionAssignments.get(callObject.name)
        if (resolvedValue) {
          callObject = resolvedValue
        }
      }

      // If the callObject is a map call, evaluate it to get the array
      if (callObject?.type === 'Call' && callObject.name === 'map') {
        const mapArray = callObject.args?.[0]?.value
        const mapFn = callObject.args?.[1]?.value
        if (mapArray?.type === 'ArrayLiteral' && mapFn) {
          const evaluated = this.tryEvaluateMap(mapArray, mapFn)
          if (evaluated) {
            callObject = evaluated
          }
        }
      }

      // Check if this is <array-from-stack>.walk(beat:measure, offset:offset, swing:swing)
      if (expr.name === 'walk' && callObject?.type === 'Call') {
        // The callObject is a method call result
        // For now, this is not fully implemented - would need to handle other method chains
        // Fall through to regular handling
      }

      // Check if this is <array>.subdivide(beat:measure, pattern:[...], callback)
      if (expr.name === 'subdivide' && callObject?.type === 'ArrayLiteral') {
        // Support both named (beat:, pattern:, cb:) and positional arguments
        let beatArg = this.findArgByName(expr.args || [], 'beat')?.value
        let patternArg = this.findArgByName(expr.args || [], 'pattern')?.value
        let callbackArg = this.findArgByName(expr.args || [], 'cb')?.value

        console.log('[Compiler] subdivide detection - callObject:', callObject)
        console.log('[Compiler] subdivide detection - args:', expr.args)

        // Fall back to positional if not using named args
        // Skip named args when looking for positional
        const { positional: positionalArgs } = this.separateArgs(expr.args || [])

        console.log('[Compiler] subdivide detection - positionalArgs:', positionalArgs)

        if (!beatArg && positionalArgs.length > 0) {
          // First positional arg that's not an array and not a function is the beat
          for (let i = 0; i < positionalArgs.length; i++) {
            const arg = positionalArgs[i]?.value
            if (arg && arg.type !== 'ArrayLiteral' && arg.type !== 'ArrowFunction') {
              beatArg = arg
              console.log('[Compiler] Found beat arg:', arg)
              break
            }
          }
        }

        if (!patternArg) {
          // Find first positional arg that is an ArrayLiteral (but NOT a note array)
          for (let i = 0; i < positionalArgs.length; i++) {
            const arg = positionalArgs[i]?.value
            console.log(`[Compiler] Checking positionalArgs[${i}]:`, arg, 'type:', arg?.type, 'isNoteArray:',
              arg?.isNoteArray)
            if (arg && arg.type === 'ArrayLiteral' && !arg.isNoteArray) {
              patternArg = arg
              console.log('[Compiler] Found pattern arg:', arg)
              break
            }
          }
        }

        if (!callbackArg) {
          // Find first ArrowFunction in args
          const callbackArgNode = (expr.args || []).find(
            (arg: any) => arg.value?.type === 'ArrowFunction',
          )
          callbackArg = callbackArgNode?.value
        }

        if (!beatArg) {
          const line = (expr as any).line
          const column = (expr as any).column
          const location = line && column ? ` at line ${line}, column ${column}` : ''
          throw new Error(
            `subdivide() requires a beat argument: [values].subdivide(beat:1/4, pattern:[1,2,3], (trig,value)->...) or [values].subdivide(1/4, [1,2,3], (trig,value)->...)${location}`,
          )
        }
        if (!patternArg) {
          const line = (expr as any).line
          const column = (expr as any).column
          const location = line && column ? ` at line ${line}, column ${column}` : ''
          throw new Error(
            `subdivide() requires a pattern array argument: [values].subdivide(beat:1/4, pattern:[1,2,3], (trig,value)->...) or [values].subdivide(1/4, [1,2,3], (trig,value)->...)${location}`,
          )
        }
        if (patternArg.type !== 'ArrayLiteral') {
          const line = (expr as any).line
          const column = (expr as any).column
          const location = line && column ? ` at line ${line}, column ${column}` : ''
          throw new Error(
            `subdivide() pattern must be an array literal, got ${patternArg.type}: [values].subdivide(beat:1/4, pattern:[1,2,3], (trig,value)->...) or [values].subdivide(1/4, [1,2,3], (trig,value)->...)${location}`,
          )
        }
        if (!callbackArg || callbackArg.type !== 'ArrowFunction') {
          const line = (expr as any).line
          const column = (expr as any).column
          const location = line && column ? ` at line ${line}, column ${column}` : ''
          throw new Error(
            `subdivide() requires a callback function: [values].subdivide(beat:1/4, pattern:[1,2,3], (trig,value)->...) or [values].subdivide(1/4, [1,2,3], (trig,value)->...)${location}`,
          )
        }
        this.compileArraySubdivide(
          callObject,
          beatArg,
          patternArg,
          callbackArg,
          expr,
          voiceId,
          impulseVar,
          pitchesVar,
          seqId,
        )
        return
      }

      // Check if this is <array>.walk(beat:measure, offset:offset, swing:swing)
      if (expr.name === 'walk' && callObject?.type === 'ArrayLiteral') {
        // Support both named (beat:, offset:, swing:) and positional arguments
        let measureArg = this.findArgByName(expr.args || [], 'beat')?.value
        let offsetArg = this.findArgByName(expr.args || [], 'offset')?.value
        let swingArg = this.findArgByName(expr.args || [], 'swing')?.value

        // Fall back to positional if not using named args
        if (!measureArg) {
          measureArg = expr.args?.[1]?.value
        }

        if (!offsetArg && expr.args?.length > 2 && !expr.args[2]?.name) {
          offsetArg = expr.args[2]?.value
        }

        if (!swingArg && expr.args?.length > 3 && !expr.args[3]?.name) {
          swingArg = expr.args[3]?.value
        }

        if (!measureArg) {
          throw new Error(
            'walk() requires a measure argument: [values].walk(beat:measure) or [values].walk(measure)',
          )
        }
        this.compileWalk(
          callObject,
          measureArg,
          offsetArg,
          swingArg,
          voiceId,
          impulseVar,
          pitchesVar,
          seqId,
        )
        return
      }

      // Check if this is <array>.slide(beat:measure, offset:offset, exponent:exponent, swing:swing)
      if (expr.name === 'slide' && callObject?.type === 'ArrayLiteral') {
        // Support both named (beat:, offset:, exponent:, swing:) and positional arguments
        let measureArg = this.findArgByName(expr.args || [], 'beat')?.value
        let offsetArg = this.findArgByName(expr.args || [], 'offset')?.value
        let exponentArg = this.findArgByName(expr.args || [], 'exponent')?.value
        let swingArg = this.findArgByName(expr.args || [], 'swing')?.value

        // Fall back to positional if not using named args
        if (!measureArg) {
          measureArg = expr.args?.[1]?.value
        }

        if (!offsetArg && expr.args?.length > 2 && !expr.args[2]?.name) {
          offsetArg = expr.args[2]?.value
        }

        if (!exponentArg && expr.args?.length > 3 && !expr.args[3]?.name) {
          exponentArg = expr.args[3]?.value
        }

        if (!swingArg && expr.args?.length > 4 && !expr.args[4]?.name) {
          swingArg = expr.args[4]?.value
        }

        if (!measureArg) {
          throw new Error(
            'slide() requires a measure argument: [values].slide(beat:measure) or [values].slide(measure)',
          )
        }
        this.compileSlide(
          callObject,
          measureArg,
          offsetArg,
          exponentArg,
          swingArg,
          voiceId,
          impulseVar,
          pitchesVar,
          seqId,
        )
        return
      }

      // Check if this is <array>.adv(trig:trigger) or <array>.step(on:trigger, offset?:offset, trig?:reset)
      if ((expr.name === 'adv' || expr.name === 'step') && callObject?.type === 'ArrayLiteral') {
        // For .adv, use 'trig' parameter name (backward compatibility)
        // For .step, use 'on' for the main trigger and 'trig' for reset
        let onArg: any = null
        let offsetArg = this.findArgByName(expr.args || [], 'offset')?.value
        let resetArg: any = null

        if (expr.name === 'step') {
          // .step uses 'on' for main trigger and 'trig' for reset
          onArg = this.findArgByName(expr.args || [], 'on')?.value
          resetArg = this.findArgByName(expr.args || [], 'trig')?.value
        }
        else {
          // .adv uses 'trig' for backward compatibility
          onArg = this.findArgByName(expr.args || [], 'trig')?.value
        }

        // Fall back to positional if not using named args
        if (!onArg) {
          onArg = expr.args?.[1]?.value
        }

        if (!offsetArg && expr.args?.length > 2 && !expr.args[2]?.name) {
          offsetArg = expr.args[2]?.value
        }

        if (!resetArg && expr.args?.length > 3 && !expr.args[3]?.name) {
          resetArg = expr.args[3]?.value
        }

        if (!onArg) {
          const fnName = expr.name === 'adv' ? 'adv' : 'step'
          const paramName = expr.name === 'step' ? 'on' : 'trig'
          throw new Error(
            `${fnName}() requires a trigger argument: [values].${fnName}(${paramName}:impulse) or [values].${fnName}(impulse)`,
          )
        }
        this.compileAdv(callObject, onArg, offsetArg, resetArg, voiceId, impulseVar, pitchesVar, seqId)
        return
      }

      // Check if this is <array>.advslide(trig:trigger, exponent:exponent)
      if (expr.name === 'advslide' && callObject?.type === 'ArrayLiteral') {
        // Support both named (trig:, exponent:) and positional arguments
        let trigArg = this.findArgByName(expr.args || [], 'trig')?.value
        let exponentArg = this.findArgByName(expr.args || [], 'exponent')?.value

        // Fall back to positional if not using named args
        if (!trigArg) {
          trigArg = expr.args?.[1]?.value
        }

        if (!exponentArg && expr.args?.length > 2 && !expr.args[2]?.name) {
          exponentArg = expr.args[2]?.value
        }

        if (!trigArg) {
          throw new Error(
            'advslide() requires a trigger argument: [values].advslide(trig:trigger, exponent:exponent) or [values].advslide(trigger, exponent)',
          )
        }
        this.compileAdvSlide(
          callObject,
          trigArg,
          exponentArg,
          voiceId,
          impulseVar,
          pitchesVar,
          seqId,
        )
        return
      }

      // Check if this is <array>.rand(trig:trigger)
      if (expr.name === 'rand' && callObject?.type === 'ArrayLiteral') {
        // Support both named (trig:) and positional arguments
        let trigArg = this.findArgByName(expr.args || [], 'trig')?.value

        // Fall back to positional if not using named args
        if (!trigArg) {
          trigArg = expr.args?.[1]?.value
        }

        if (!trigArg) {
          throw new Error(
            'rand() requires a trigger argument: [values].rand(trig:trigger) or [values].rand(trigger)',
          )
        }
        this.compileRand(callObject, trigArg, voiceId, impulseVar, pitchesVar, seqId)
        return
      }

      // Check if this is <array>.randslide(trig:trigger, exponent:exponent)
      if (expr.name === 'randslide' && callObject?.type === 'ArrayLiteral') {
        // Support both named (trig:, exponent:) and positional arguments
        let trigArg = this.findArgByName(expr.args || [], 'trig')?.value
        let exponentArg = this.findArgByName(expr.args || [], 'exponent')?.value

        // Fall back to positional if not using named args
        if (!trigArg) {
          trigArg = expr.args?.[1]?.value
        }

        if (!exponentArg && expr.args?.length > 2 && !expr.args[2]?.name) {
          exponentArg = expr.args[2]?.value
        }

        if (!trigArg) {
          throw new Error(
            'randslide() requires a trigger argument: [values].randslide(trig:trigger, exponent:exponent) or [values].randslide(trigger, exponent)',
          )
        }
        this.compileRandSlide(
          callObject,
          trigArg,
          exponentArg,
          voiceId,
          impulseVar,
          pitchesVar,
          seqId,
        )
        return
      }

      // Check if this is <array>.randbeat(beat:measure)
      if (expr.name === 'randbeat' && callObject?.type === 'ArrayLiteral') {
        // Support both named (beat:) and positional arguments
        let beatArg = this.findArgByName(expr.args || [], 'beat')?.value

        // Fall back to positional if not using named args
        if (!beatArg) {
          beatArg = expr.args?.[1]?.value
        }

        // Default to 1/4 if no beat argument
        if (!beatArg) {
          beatArg = { type: 'Literal', value: 0.25 }
        }

        // Create synthetic beatgrain call: beatgrain(beat:beatArg, prob:1)
        const syntheticTrigArg = {
          type: 'Call',
          name: 'beatgrain',
          args: [
            { name: 'beat', value: beatArg },
            { name: 'prob', value: { type: 'Literal', value: 1 } },
          ],
        }

        this.compileRand(callObject, syntheticTrigArg, voiceId, impulseVar, pitchesVar, seqId)
        return
      }

      // Check if this is <array>.randbeatslide(beat:measure, exponent:exponent)
      if (expr.name === 'randbeatslide' && callObject?.type === 'ArrayLiteral') {
        // Support both named (beat:, exponent:) and positional arguments
        let beatArg = this.findArgByName(expr.args || [], 'beat')?.value
        let exponentArg = this.findArgByName(expr.args || [], 'exponent')?.value

        // Fall back to positional if not using named args
        if (!beatArg) {
          beatArg = expr.args?.[1]?.value
        }

        if (!exponentArg && expr.args?.length > 2 && !expr.args[2]?.name) {
          exponentArg = expr.args[2]?.value
        }

        // Default to 1/4 if no beat argument
        if (!beatArg) {
          beatArg = { type: 'Literal', value: 0.25 }
        }

        // Create synthetic beatgrain call: beatgrain(beat:beatArg, prob:1)
        const syntheticTrigArg = {
          type: 'Call',
          name: 'beatgrain',
          args: [
            { name: 'beat', value: beatArg },
            { name: 'prob', value: { type: 'Literal', value: 1 } },
          ],
        }

        this.compileRandSlide(
          callObject,
          syntheticTrigArg,
          exponentArg,
          voiceId,
          impulseVar,
          pitchesVar,
          seqId,
        )
        return
      }

      // Check if this is <array>.euclid(beat:measure, hits:number, steps:number)
      if (expr.name === 'euclid' && callObject?.type === 'ArrayLiteral') {
        // Support both named (beat:, hits:, steps:) and positional arguments
        let beatArg = this.findArgByName(expr.args || [], 'beat')?.value
        let hitsArg = this.findArgByName(expr.args || [], 'hits')?.value
        let stepsArg = this.findArgByName(expr.args || [], 'steps')?.value

        // Fall back to positional if not using named args
        if (!beatArg) {
          beatArg = expr.args?.[1]?.value
        }
        if (!hitsArg) {
          hitsArg = expr.args?.[2]?.value
        }
        if (!stepsArg) {
          stepsArg = expr.args?.[3]?.value
        }

        if (!beatArg) {
          throw new Error(
            'euclid() requires a beat argument: [values].euclid(beat:measure, hits:number, steps:number)',
          )
        }
        if (!hitsArg) {
          throw new Error(
            'euclid() requires a hits argument: [values].euclid(beat:measure, hits:number, steps:number)',
          )
        }
        if (!stepsArg) {
          throw new Error(
            'euclid() requires a steps argument: [values].euclid(beat:measure, hits:number, steps:number)',
          )
        }

        this.compileEuclid(
          callObject,
          beatArg,
          hitsArg,
          stepsArg,
          voiceId,
          impulseVar,
          pitchesVar,
          seqId,
        )
        return
      }

      // Check if this is <array>.map(fn) or <array>.map(v->fn(v))
      if (expr.name === 'map' && callObject?.type === 'ArrayLiteral') {
        this.compileArrayMap(expr, callObject, voiceId, impulseVar, pitchesVar, seqId)
        return
      }

      // Check if this is <array>.pick(el)
      if (expr.name === 'pick' && callObject?.type === 'ArrayLiteral') {
        this.compileArrayPick(expr, callObject, voiceId, impulseVar, pitchesVar, seqId)
        return
      }

      // Check if this is an array method being called on a non-array
      // Only throw error if we can definitively determine it's not an array
      const arrayMethods = ['map', 'walk', 'slide', 'step', 'adv', 'advslide', 'rand', 'randslide', 'sum', 'pick',
        'subdivide']
      if (arrayMethods.includes(expr.name)) {
        // Allow ArrayLiteral (obviously an array)
        // Allow Call (might return an array from chaining like array.map().walk())
        // Allow Identifier (might be a variable that holds an array)
        // But reject Literal (numbers, strings), BinaryOp, UnaryOp, etc.
        if (callObject?.type === 'Literal' || callObject?.type === 'BinaryOp' || callObject?.type === 'UnaryOp') {
          const typeName = this.getTypeName(callObject)
          throw new Error(`${expr.name}() requires an array, but got ${typeName}`)
        }
      }

      this.compileCall(expr, voiceId, impulseVar, pitchesVar, seqId)
    }
    else if (expr.type === 'BinaryOp') {
      // Check if left side is a map() that needs automatic summing
      if (this.isPitchesMap(expr.left, pitchesVar)) {
        this.compileMapSum(expr.left, voiceId, impulseVar, pitchesVar, seqId)
      }
      else if (this.isArrayLiteral(expr.left)) {
        // Auto-sum array literals (e.g., "c2" -> [65.41] -> 65.41)
        this.compileArrayLiteralSum(expr.left, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.compileExpression(expr.left, voiceId, impulseVar, pitchesVar, seqId)
      }

      // Check if right side is a map() that needs automatic summing
      if (this.isPitchesMap(expr.right, pitchesVar)) {
        this.compileMapSum(expr.right, voiceId, impulseVar, pitchesVar, seqId)
      }
      else if (this.isArrayLiteral(expr.right)) {
        // Auto-sum array literals (e.g., "c2" -> [65.41] -> 65.41)
        this.compileArrayLiteralSum(expr.right, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.compileExpression(expr.right, voiceId, impulseVar, pitchesVar, seqId)
      }

      switch (expr.operator) {
        case '+':
          this.encoder.emit(OpCode.ADD)
          break
        case '-':
          this.encoder.emit(OpCode.SUB)
          break
        case '*':
          this.encoder.emit(OpCode.MUL)
          break
        case '/':
          this.encoder.emit(OpCode.DIV)
          break
        case '^':
          this.encoder.emit(OpCode.POW)
          break
      }
    }
    else if (expr.type === 'Literal') {
      // Validate string literals that look like notes
      if (typeof expr.value === 'string') {
        // Allow '_' to output 0
        if (expr.value === '_') {
          this.encoder.emit(OpCode.PUSH, 0)
          return
        }

        // Check for mini-notation patterns (contain brackets like '[c1 c2]' or '<a2 a3 a4>')
        if (expr.value.includes('[') || expr.value.includes('<')) {
          this.compileMiniNotationInline(expr.value, expr as any, voiceId, impulseVar, pitchesVar, seqId)
          return
        }

        // Check if it looks like an incomplete note (just a letter without octave)
        if (/^[a-g][#b]?$/i.test(expr.value)) {
          throw new Error(
            `Invalid note "${expr.value}". Notes must include an octave number (e.g., "${expr.value}4" instead of "${expr.value}").`,
          )
        }
        // Check if it looks like a sequence pattern (contains spaces or multiple tokens)
        // This check should come before the single note check so we can identify specific invalid notes
        if (/\s/.test(expr.value) || (expr.value.includes('_') && expr.value.length > 1)) {
          // Check for invalid note syntax (e.g., "dc3" instead of "c3" or "d3")
          const tokens = expr.value.trim().split(/\s+/)
          const invalidNotes = tokens.filter((token: string) => {
            if (token === '_') return false
            // Valid note pattern: [a-g][#b]?\d+
            // Invalid: anything else that looks like it might be a note (has letters and digits)
            if (/[a-g]/i.test(token) && /\d/.test(token)) {
              return !/^[a-g][#b]?\d+$/i.test(token)
            }
            return false
          })

          if (invalidNotes.length > 0) {
            throw new Error(
              `Invalid note syntax in "${expr.value}". Note "${
                invalidNotes[0]
              }" is not valid. Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`,
            )
          }

          throw new Error(
            `Cannot use sequence pattern "${expr.value}" where a number is expected. Sequence patterns can only be used with array methods like .walk(), .slide(), etc.`,
          )
        }
        // Check if it looks like a note (has letters and digits) but doesn't match valid note pattern
        // This check comes after the sequence pattern check
        // Before throwing error, try parsing as mini-notation pattern
        if (/[a-z]/i.test(expr.value) && /\d/.test(expr.value) && !/^[a-g][#b]?\d+$/i.test(expr.value)) {
          // Try parsing with Mini-Notation parser
          try {
            const lineOffset = (expr as any).line || 0
            const columnOffset = ((expr as any).column || 0) + 1
            parsePattern(expr.value, lineOffset, columnOffset, expr.value)
            // If parsing succeeds, treat as mini-notation pattern
            this.compileMiniNotationInline(expr.value, expr as any, voiceId, impulseVar, pitchesVar, seqId)
            return
          }
          catch {
            // If parsing fails, throw the original error
          }
          throw new Error(
            `Invalid note syntax "${expr.value}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`,
          )
        }
        throw new Error(`Cannot use string "${expr.value}" where a number is expected.`)
      }
      this.encoder.emit(OpCode.PUSH, expr.value)
    }
    else if (expr.type === 'NumberWithParams') {
      // Collect params metadata for UI
      const params = expr.params || []
      let min = -Infinity
      let max = Infinity
      const positionalParams: number[] = []

      for (const param of params) {
        if (param.name === 'min' && param.value?.type === 'Literal' && typeof param.value.value === 'number') {
          min = param.value.value
        }
        if (param.name === 'max' && param.value?.type === 'Literal' && typeof param.value.value === 'number') {
          max = param.value.value
        }
        if (!param.name && param.value?.type === 'Literal' && typeof param.value.value === 'number') {
          positionalParams.push(param.value.value)
        }
      }

      if (positionalParams.length === 2) {
        min = positionalParams[0]
        max = positionalParams[1]
      }

      if (min !== -Infinity && max !== Infinity) {
        this.numbersWithParams.set(expr, { value: expr.value, min, max, line: expr.line, column: expr.column })
      }

      // Emit the numeric value
      this.encoder.emit(OpCode.PUSH, expr.value)
    }
    else if (expr.type === 'Identifier') {
      // Check for global 'co' (BPM coefficient: bpm/60 to convert beats to Hz)
      // At 120 BPM: co = 120/60 = 2, so sin(1*co) = sin(2 Hz) = 1 cycle per beat
      if (expr.name === 'co') {
        const co = this.bpm / 60
        this.encoder.emit(OpCode.PUSH, co)
      }
      else if (expr.name === 't') {
        // Global clock in seconds
        this.encoder.emit(OpCode.PUSH_CLOCK)
      }
      else if (expr.name === '_') {
        // Allow '_' as it's used for ignored parameters
        this.encoder.emit(OpCode.PUSH, 0)
      }
      else {
        const varSlot = this.symbols.get(expr.name)
        if (varSlot !== undefined) {
          this.encoder.emit(OpCode.LOAD, varSlot)
        }
        else {
          // Check if it's an expression assignment and recursively compile it
          const exprValue = this.expressionAssignments.get(expr.name)
          if (exprValue) {
            this.compileExpression(exprValue, voiceId, impulseVar, pitchesVar, seqId)
          }
          else {
            const seqCall = this.seqAssignments.get(expr.name)
            if (seqCall) {
              throw new Error(
                `Variable '${expr.name}' is a seq() call but needs to be used in a pipeline with |> to produce output`,
              )
            }
            else {
              // Check if it's a known function name
              const knownFunctions = new Set(getAllFunctionNames())
              knownFunctions.add('out')
              knownFunctions.add('post')
              knownFunctions.add('seq')
              knownFunctions.add('pat')
              knownFunctions.add('map')
              knownFunctions.add('sum')
              if (knownFunctions.has(expr.name)) {
                throw new Error(
                  `Function '${expr.name}' must be called with parentheses: ${expr.name}(...)`,
                )
              }
              const line = (expr as any).line
              const column = (expr as any).column
              const location = line && column ? ` at line ${line}, column ${column}` : ''
              throw new Error(`Undefined variable: ${expr.name}${location}`)
            }
          }
        }
      }
    }
    else if (expr.type === 'MemberAccess') {
      const validArrayMethods = new Set(['walk', 'slide', 'step', 'adv', 'advslide', 'rand', 'randslide', 'subdivide', 'map', 'sum', 'pick', 'euclid'])
      if (!validArrayMethods.has(expr.property)) {
        const line = (expr as any).line
        const column = (expr as any).column
        const location = line && column ? ` at line ${line}, column ${column}` : ''
        throw new Error(`Invalid property '${expr.property}'${location}`)
      }
      // If it's a valid method but not called, that's also an error
      throw new Error(`Property '${expr.property}' must be called as a method: [array].${expr.property}(...)`)
    }
  }

  private getTypeName(node: any): string {
    if (!node) return 'unknown'
    if (node.type === 'ArrayLiteral') return 'array'
    if (node.type === 'Identifier') return `variable '${node.name}'`
    if (node.type === 'Literal') {
      if (typeof node.value === 'number') return 'number'
      if (typeof node.value === 'string') return 'string'
      return 'literal'
    }
    if (node.type === 'NumberWithParams') return 'number'
    if (node.type === 'Call') return 'function call'
    if (node.type === 'BinaryOp') return 'expression'
    if (node.type === 'UnaryOp') return 'expression'
    if (node.type === 'ArrowFunction') return 'function'
    return node.type || 'unknown'
  }

  private extractNumericValue(elem: any, isNoteArray: boolean): number {
    if (elem.type === 'Literal' && typeof elem.value === 'number') {
      return isNoteArray ? elem.value * this.tune : elem.value
    }
    else if (elem.type === 'NumberWithParams') {
      return isNoteArray ? elem.value * this.tune : elem.value
    }
    else if (
      elem.type === 'BinaryOp'
      && elem.operator === '-'
      && elem.left.type === 'Literal'
      && elem.left.value === 0
      && elem.right.type === 'Literal'
      && typeof elem.right.value === 'number'
    ) {
      // Handle negative numbers (parser converts unary minus to 0 - value)
      return isNoteArray ? -elem.right.value * this.tune : -elem.right.value
    }
    else if (
      elem.type === 'BinaryOp'
      && elem.operator === '/'
      && elem.left.type === 'Literal'
      && typeof elem.left.value === 'number'
      && elem.right.type === 'Literal'
      && typeof elem.right.value === 'number'
    ) {
      // Handle division operations like 1/6
      const value = elem.left.value / elem.right.value
      return isNoteArray ? value * this.tune : value
    }
    return NaN
  }

  private compileArraySubdivide(
    arrayExpr: any,
    beatArg: any,
    patternArg: any,
    callbackArg: any,
    expr: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Extract array values
    if (arrayExpr.type !== 'ArrayLiteral') {
      throw new Error('subdivide() can only be called on array literals')
    }

    const values: number[] = []
    const glides: number[] = []
    const isNoteArray = arrayExpr.isNoteArray === true
    for (const elem of arrayExpr.elements) {
      const value = this.extractNumericValue(elem, isNoteArray)
      if (isNaN(value)) {
        throw new Error('subdivide() only supports arrays of literal numbers, negative numbers, and fractions')
      }
      values.push(value)
      // Extract glide if present
      const glide = (elem.glide !== undefined && typeof elem.glide === 'number') ? elem.glide : 0
      glides.push(glide)
    }

    // Extract pattern values
    if (patternArg.type !== 'ArrayLiteral') {
      throw new Error('subdivide() pattern must be an array literal')
    }

    const patternValues: number[] = []
    for (const elem of patternArg.elements) {
      const value = this.extractNumericValue(elem, false)
      if (isNaN(value)) {
        throw new Error('subdivide() pattern array must contain only literal numbers')
      }
      patternValues.push(value)
    }

    if (patternValues.length === 0) {
      throw new Error('subdivide() pattern array must not be empty')
    }

    // Extract beat value
    let beat = 0.25 // default to 1/4
    if (beatArg.type === 'Literal' && typeof beatArg.value === 'number') {
      beat = beatArg.value
    }
    else if (beatArg.type === 'BinaryOp' && beatArg.operator === '/') {
      const left = beatArg.left.value || 1
      const right = beatArg.right.value || 4
      beat = left / right
    }

    // If array length doesn't match pattern length, cycle the pattern
    const patternSum = patternValues.reduce((a, b) => a + b, 0)
    if (patternSum <= 0) {
      throw new Error('subdivide() pattern values must sum to a positive number')
    }

    // Debug: log pattern values
    console.log(`[Compiler] subdivide pattern:`, patternValues, `beat:`, beat)

    // Create durations based on pattern proportions
    const noteData: NoteData[] = []
    for (let i = 0; i < values.length; i++) {
      const patternIndex = i % patternValues.length
      const proportion = patternValues[patternIndex] / patternSum
      const duration = beat * proportion

      console.log(`[Compiler] note[${i}] value=${values[i]} duration=${duration} (proportion=${proportion})`)

      noteData.push({
        pitches: new Float32Array([values[i]]),
        duration,
        velocity: 1,
        hold: 0,
        glide: glides[i],
      })
    }

    // Create a deterministic ID for this subdivide sequence
    const subdivideId = this.sequenceCounterId++

    // Store array info for potential runtime use
    this.walkArrays.set(subdivideId, { values, measure: beat })

    // Create sequence
    this.sequences.push({ id: subdivideId, notes: noteData, offset: 0 })
    this.sequenceMaxPitches.set(subdivideId, 1)

    // SEQ_STEP: args = [sequenceId]
    // Pushes: [pitch_count, pitch0, ..., velocity, impulse]
    this.encoder.emit(OpCode.SEQ_STEP, subdivideId)

    // Store parameters for callback: (trig, value)
    const callback = callbackArg
    if (!callback || callback.type !== 'ArrowFunction') {
      const line = (expr as any).line
      const column = (expr as any).column
      const location = line && column ? ` at line ${line}, column ${column}` : ''
      throw new Error(`subdivide() requires a callback function (arrow function)${location}`)
    }

    // Impulse is on top of stack
    const trigParam = callback.params[0]
    const valueParam = callback.params[1]

    // Store impulse (trig)
    const trigVar = this.varCounter++
    if (trigParam) {
      this.symbols.set(trigParam, trigVar)
    }
    this.encoder.emit(OpCode.STORE, trigVar)

    // Pop velocity (not used in subdivide callback)
    this.encoder.emit(OpCode.POP)

    // Store pitch0 (the value)
    const valueVar = this.varCounter++
    if (valueParam) {
      this.symbols.set(valueParam, valueVar)
    }
    this.encoder.emit(OpCode.STORE, valueVar)

    // Compile callback body
    this.compileCallbackBody(callback, voiceId, trigVar, valueVar, subdivideId)

    // Clean up callback parameter symbols
    if (trigParam && trigParam !== '_') {
      this.symbols.delete(trigParam)
    }
    if (valueParam && valueParam !== '_') {
      this.symbols.delete(valueParam)
    }
  }

  private compileWalk(
    arrayExpr: any,
    measureArg: any,
    offsetArg: any,
    swingArg: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Extract literal values from array if possible
    if (arrayExpr.type !== 'ArrayLiteral') {
      throw new Error('walk() can only be called on array literals')
    }

    const values: number[] = []
    const glides: number[] = []
    const isNoteArray = arrayExpr.isNoteArray === true
    for (const elem of arrayExpr.elements) {
      const value = this.extractNumericValue(elem, isNoteArray)
      if (isNaN(value)) {
        throw new Error('walk() only supports arrays of literal numbers, negative numbers, and fractions')
      }
      values.push(value)
      // Extract glide if present
      const glide = (elem.glide !== undefined && typeof elem.glide === 'number') ? elem.glide : 0
      glides.push(glide)
    }

    // Compile the measure argument (beat duration)
    let measure = 0.25 // default to 1/4
    if (measureArg.type === 'Literal' && typeof measureArg.value === 'number') {
      measure = measureArg.value
    }
    else if (measureArg.type === 'BinaryOp' && measureArg.operator === '/') {
      // Handle fractions like 1/4
      const left = measureArg.left.value || 1
      const right = measureArg.right.value || 4
      measure = left / right
    }

    // Compile the offset argument (in seconds)
    let offset = 0
    if (offsetArg) {
      if (offsetArg.type === 'Literal' && typeof offsetArg.value === 'number') {
        offset = offsetArg.value
      }
      else if (
        offsetArg.type === 'BinaryOp'
        && offsetArg.operator === '-'
        && offsetArg.left.type === 'Literal'
        && offsetArg.left.value === 0
        && offsetArg.right.type === 'Literal'
      ) {
        // Handle negative numbers (parser converts unary minus to 0 - value)
        offset = -offsetArg.right.value
      }
    }

    // Compile the swing argument (0-1)
    let swing = 0
    if (swingArg) {
      if (swingArg.type === 'Literal' && typeof swingArg.value === 'number') {
        swing = swingArg.value
      }
      else if (
        swingArg.type === 'BinaryOp'
        && swingArg.operator === '-'
        && swingArg.left.type === 'Literal'
        && swingArg.left.value === 0
        && swingArg.right.type === 'Literal'
      ) {
        // Handle negative numbers (parser converts unary minus to 0 - value)
        swing = -swingArg.right.value
      }
    }

    // Create a deterministic ID for this walk array based on its content
    const walkId = this.sequenceCounterId++

    // Store array info for potential runtime use
    this.walkArrays.set(walkId, { values, measure })

    // Convert array to a sequence for beat-locking
    const noteData: NoteData[] = values.map((value, index) => ({
      pitches: new Float32Array([value]),
      duration: measure,
      velocity: 1,
      hold: 0,
      glide: glides[index],
    }))

    this.sequences.push({ id: walkId, notes: noteData, swing, offset })
    this.sequenceMaxPitches.set(walkId, 1)

    // Create pattern events for UI highlighting (if elements have position info)
    if (arrayExpr.elements.length > 0) {
      for (let i = 0; i < arrayExpr.elements.length; i++) {
        const elem = arrayExpr.elements[i]
        if (elem.line !== undefined && elem.column !== undefined) {
          // Adjust line number by subtracting prelude lines
          const adjustedLine = elem.line > 0 ? elem.line - this.preludeLineCount : elem.line

          // Calculate length based on element type
          let length = 1
          if (elem.noteText) {
            // Note array element (e.g., "a1")
            length = elem.noteText.length
          }
          else if (elem.sourceLength !== undefined) {
            // Numeric literal with source length (e.g., .3 = 2, not 3 for "0.3")
            length = elem.sourceLength
          }
          else if (elem.type === 'Literal' && elem.value !== undefined) {
            // Fallback: use stringified value length
            length = String(elem.value).length
          }

          this.patternEvents.push({
            line: adjustedLine,
            column: elem.column,
            length,
            type: 'note',
            sequenceId: walkId,
            noteIndex: i,
          })
        }
      }
    }

    // Emit SEQ_STEP to get the current value based on beat timing
    this.encoder.emit(OpCode.SEQ_STEP, walkId)

    // The SEQ_STEP pushes: [pitch_count, pitch0, ..., velocity, impulse]
    // For walk, we only care about pitch0 (the value)
    // Pop impulse
    this.encoder.emit(OpCode.POP)
    // Pop velocity
    this.encoder.emit(OpCode.POP)
    // Keep pitch0 (the walked value) on stack
  }

  private compileSlide(
    arrayExpr: any,
    measureArg: any,
    offsetArg: any,
    exponentArg: any,
    swingArg: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Extract literal values from array if possible
    if (arrayExpr.type !== 'ArrayLiteral') {
      throw new Error('slide() can only be called on array literals')
    }

    const values: number[] = []
    const glides: number[] = []
    const isNoteArray = arrayExpr.isNoteArray === true
    for (const elem of arrayExpr.elements) {
      const value = this.extractNumericValue(elem, isNoteArray)
      if (isNaN(value)) {
        throw new Error('slide() only supports arrays of literal numbers, negative numbers, and fractions')
      }
      values.push(value)
      // Extract glide if present
      const glide = (elem.glide !== undefined && typeof elem.glide === 'number') ? elem.glide : 0
      glides.push(glide)
    }

    // Compile the measure argument (beat duration)
    let measure = 0.25 // default to 1/4
    if (measureArg.type === 'Literal' && typeof measureArg.value === 'number') {
      measure = measureArg.value
    }
    else if (measureArg.type === 'BinaryOp' && measureArg.operator === '/') {
      // Handle fractions like 1/4
      const left = measureArg.left.value || 1
      const right = measureArg.right.value || 4
      measure = left / right
    }

    // Compile the offset argument (in seconds)
    let offset = 0
    if (offsetArg) {
      if (offsetArg.type === 'Literal' && typeof offsetArg.value === 'number') {
        offset = offsetArg.value
      }
      else if (
        offsetArg.type === 'BinaryOp'
        && offsetArg.operator === '-'
        && offsetArg.left.type === 'Literal'
        && offsetArg.left.value === 0
        && offsetArg.right.type === 'Literal'
      ) {
        // Handle negative numbers (parser converts unary minus to 0 - value)
        offset = -offsetArg.right.value
      }
    }

    // Compile the exponent argument (curve power)
    let exponent = 1 // default to 1 (linear)
    if (exponentArg) {
      if (exponentArg.type === 'Literal' && typeof exponentArg.value === 'number') {
        exponent = exponentArg.value
      }
    }

    // Compile the swing argument (0-1)
    let swing = 0
    if (swingArg) {
      if (swingArg.type === 'Literal' && typeof swingArg.value === 'number') {
        swing = swingArg.value
      }
      else if (
        swingArg.type === 'BinaryOp'
        && swingArg.operator === '-'
        && swingArg.left.type === 'Literal'
        && swingArg.left.value === 0
        && swingArg.right.type === 'Literal'
      ) {
        // Handle negative numbers (parser converts unary minus to 0 - value)
        swing = -swingArg.right.value
      }
    }

    // Create a deterministic ID for this slide array based on its content
    const slideId = this.sequenceCounterId++

    // Store array info for potential runtime use
    this.slideArrays.set(slideId, { values, measure, exponent })

    // Convert array to a sequence for beat-locking with interpolation
    const noteData: NoteData[] = values.map((value, index) => ({
      pitches: new Float32Array([value]),
      duration: measure,
      velocity: 1,
      hold: 0,
      glide: glides[index],
    }))

    this.sequences.push({ id: slideId, notes: noteData, swing, offset, exponent })
    this.sequenceMaxPitches.set(slideId, 1)

    // Create pattern events for UI highlighting (if elements have position info)
    if (arrayExpr.elements.length > 0) {
      for (let i = 0; i < arrayExpr.elements.length; i++) {
        const elem = arrayExpr.elements[i]
        if (elem.line !== undefined && elem.column !== undefined) {
          // Adjust line number by subtracting prelude lines
          const adjustedLine = elem.line > 0 ? elem.line - this.preludeLineCount : elem.line

          // Calculate length based on element type
          let length = 1
          if (elem.noteText) {
            // Note array element (e.g., "a1")
            length = elem.noteText.length
          }
          else if (elem.sourceLength !== undefined) {
            // Numeric literal with source length (e.g., .3 = 2, not 3 for "0.3")
            length = elem.sourceLength
          }
          else if (elem.type === 'Literal' && elem.value !== undefined) {
            // Fallback: use stringified value length
            length = String(elem.value).length
          }

          this.patternEvents.push({
            line: adjustedLine,
            column: elem.column,
            length,
            type: 'note',
            sequenceId: slideId,
            noteIndex: i,
          })
        }
      }
    }

    // Emit SEQ_SLIDE to get the interpolated value based on beat timing
    this.encoder.emit(OpCode.SEQ_SLIDE, slideId)

    // The SEQ_SLIDE pushes: [pitch_count, pitch0, ..., velocity, impulse]
    // For slide, we only care about pitch0 (the interpolated value)
    // Pop impulse
    this.encoder.emit(OpCode.POP)
    // Pop velocity
    this.encoder.emit(OpCode.POP)
    // Keep pitch0 (the slided value) on stack
  }

  private compileAdv(
    arrayExpr: any,
    trigArg: any,
    offsetArg: any,
    resetArg: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Extract literal values from array if possible
    if (arrayExpr.type !== 'ArrayLiteral') {
      throw new Error('adv() can only be called on array literals')
    }

    const values: number[] = []
    const glides: number[] = []
    const isNoteArray = arrayExpr.isNoteArray === true
    for (const elem of arrayExpr.elements) {
      const value = this.extractNumericValue(elem, isNoteArray)
      if (isNaN(value)) {
        throw new Error('adv() only supports arrays of literal numbers, negative numbers, and fractions')
      }
      values.push(value)
      // Extract glide if present
      const glide = (elem.glide !== undefined && typeof elem.glide === 'number') ? elem.glide : 0
      glides.push(glide)
    }

    // Extract offset value (default to 0)
    let offset = 0
    if (offsetArg) {
      if (offsetArg.type === 'Literal' && typeof offsetArg.value === 'number') {
        offset = Math.floor(offsetArg.value)
      }
      else {
        throw new Error('step() offset argument must be a literal number')
      }
    }

    // Clamp offset to valid range
    offset = Math.max(0, Math.min(offset, values.length - 1))

    // Create counter-based ID for this adv array
    const advId = this.sequenceCounterId++

    // Store array info for potential runtime use
    this.walkArrays.set(advId, { values, measure: 0 })

    // Convert array to a sequence (measure doesn't matter for trigger-based)
    const noteData: NoteData[] = values.map((value, index) => ({
      pitches: new Float32Array([value]),
      duration: 1, // Duration doesn't matter for trigger-based
      velocity: 1,
      hold: 0,
      glide: glides[index],
    }))

    this.sequences.push({ id: advId, notes: noteData })
    this.sequenceMaxPitches.set(advId, 1)

    // Create pattern events for UI highlighting (if elements have position info)
    if (arrayExpr.elements.length > 0) {
      for (let i = 0; i < arrayExpr.elements.length; i++) {
        const elem = arrayExpr.elements[i]
        if (elem.line !== undefined && elem.column !== undefined) {
          // Adjust line number by subtracting prelude lines
          const adjustedLine = elem.line > 0 ? elem.line - this.preludeLineCount : elem.line

          // Calculate length based on element type
          let length = 1
          if (elem.noteText) {
            // Note array element (e.g., "a1")
            length = elem.noteText.length
          }
          else if (elem.sourceLength !== undefined) {
            // Numeric literal with source length (e.g., .3 = 2, not 3 for "0.3")
            length = elem.sourceLength
          }
          else if (elem.type === 'Literal' && elem.value !== undefined) {
            // Fallback: use stringified value length
            length = String(elem.value).length
          }

          this.patternEvents.push({
            line: adjustedLine,
            column: elem.column,
            length,
            type: 'note',
            sequenceId: advId,
            noteIndex: i,
          })
        }
      }
    }

    // Compile the trigger argument and push it to stack
    this.compileExpression(trigArg, voiceId, impulseVar, pitchesVar, seqId)

    // Compile offset (as constant) and push to stack
    this.encoder.emit(OpCode.PUSH, offset)

    // Compile reset trigger if provided, otherwise push 0 (no reset)
    if (resetArg) {
      this.compileExpression(resetArg, voiceId, impulseVar, pitchesVar, seqId)
    }
    else {
      this.encoder.emit(OpCode.PUSH, -1) // -1 indicates no reset trigger
    }

    // Emit SEQ_ADV to get the current value based on trigger impulses
    // SEQ_ADV now pops: resetTrigger (or -1), offset, trig, then sequenceId from args
    this.encoder.emit(OpCode.SEQ_ADV, advId)

    // The SEQ_ADV pushes: [pitch_count, pitch0, ..., velocity, impulse]
    // For adv, we only care about pitch0 (the value)
    // Pop impulse
    this.encoder.emit(OpCode.POP)
    // Pop velocity
    this.encoder.emit(OpCode.POP)
    // Keep pitch0 (the advanced value) on stack
  }

  private compileEuclid(
    arrayExpr: any,
    beatArg: any,
    hitsArg: any,
    stepsArg: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Extract literal values from array if possible
    if (arrayExpr.type !== 'ArrayLiteral') {
      throw new Error('euclid() can only be called on array literals')
    }

    const values: number[] = []
    const glides: number[] = []
    const isNoteArray = arrayExpr.isNoteArray === true
    for (const elem of arrayExpr.elements) {
      const value = this.extractNumericValue(elem, isNoteArray)
      if (isNaN(value)) {
        throw new Error('euclid() only supports arrays of literal numbers, negative numbers, and fractions')
      }
      values.push(value)
      // Extract glide if present
      const glide = (elem.glide !== undefined && typeof elem.glide === 'number') ? elem.glide : 0
      glides.push(glide)
    }

    // Compile the beat argument (beat duration)
    let beat = 0.25 // default to 1/4
    if (beatArg.type === 'Literal' && typeof beatArg.value === 'number') {
      beat = beatArg.value
    }
    else if (beatArg.type === 'BinaryOp' && beatArg.operator === '/') {
      // Handle fractions like 1/4
      const left = beatArg.left.value || 1
      const right = beatArg.right.value || 4
      beat = left / right
    }

    // Compile hits and steps arguments
    let hits = 0
    if (hitsArg.type === 'Literal' && typeof hitsArg.value === 'number') {
      hits = hitsArg.value
    }
    else {
      throw new Error('euclid() hits argument must be a literal number')
    }

    let steps = 0
    if (stepsArg.type === 'Literal' && typeof stepsArg.value === 'number') {
      steps = stepsArg.value
    }
    else {
      throw new Error('euclid() steps argument must be a literal number')
    }

    // Create a deterministic ID for this euclid array based on its content
    const euclidId = this.sequenceCounterId++

    // Store array info for potential runtime use
    this.walkArrays.set(euclidId, { values, measure: beat })

    // Convert array to a sequence (each element has duration = beat)
    const noteData: NoteData[] = values.map((value, index) => ({
      pitches: new Float32Array([value]),
      duration: beat,
      velocity: 1,
      hold: 0,
      glide: glides[index],
    }))

    this.sequences.push({ id: euclidId, notes: noteData })
    this.sequenceMaxPitches.set(euclidId, 1)

    // Compile beat, hits, and steps arguments and push them to stack
    this.compileExpression(beatArg, voiceId, impulseVar, pitchesVar, seqId)
    this.compileExpression(hitsArg, voiceId, impulseVar, pitchesVar, seqId)
    this.compileExpression(stepsArg, voiceId, impulseVar, pitchesVar, seqId)

    // Push array ID (we'll use the sequence ID)
    this.encoder.emit(OpCode.PUSH, euclidId)

    // Emit ARRAY_METHOD_EUCLID which pops: steps, hits, beat, arrayId
    // and returns: current value from array advanced by euclidean rhythm
    this.encoder.emit(OpCode.ARRAY_METHOD_EUCLID)

    // The ARRAY_METHOD_EUCLID pushes: [pitch_count, pitch0, ..., velocity, impulse]
    // For euclid, we only care about pitch0 (the value)
    // Pop impulse
    this.encoder.emit(OpCode.POP)
    // Pop velocity
    this.encoder.emit(OpCode.POP)
    // Keep pitch0 (the euclidean-advanced value) on stack
  }

  private compileAdvSlide(
    arrayExpr: any,
    trigArg: any,
    exponentArg: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Extract literal values from array if possible
    if (arrayExpr.type !== 'ArrayLiteral') {
      throw new Error('advslide() can only be called on array literals')
    }

    const values: number[] = []
    const glides: number[] = []
    const isNoteArray = arrayExpr.isNoteArray === true
    for (const elem of arrayExpr.elements) {
      const value = this.extractNumericValue(elem, isNoteArray)
      if (isNaN(value)) {
        throw new Error('advslide() only supports arrays of literal numbers, negative numbers, and fractions')
      }
      values.push(value)
      // Extract glide if present
      const glide = (elem.glide !== undefined && typeof elem.glide === 'number') ? elem.glide : 0
      glides.push(glide)
    }

    // Compile the exponent argument (curve power)
    let exponent = 1 // default to 1 (linear)
    if (exponentArg) {
      if (exponentArg.type === 'Literal' && typeof exponentArg.value === 'number') {
        exponent = exponentArg.value
      }
    }

    // Create a deterministic ID for this advslide array based on its content
    const advSlideId = this.sequenceCounterId++

    // Store array info for potential runtime use
    this.slideArrays.set(advSlideId, { values, measure: 0, exponent })

    // Convert array to a sequence (measure doesn't matter for trigger-based)
    const noteData: NoteData[] = values.map((value, index) => ({
      pitches: new Float32Array([value]),
      duration: 1, // Duration doesn't matter for trigger-based
      velocity: 1,
      hold: 0,
      glide: glides[index],
    }))

    this.sequences.push({ id: advSlideId, notes: noteData, offset: 0, exponent })
    this.sequenceMaxPitches.set(advSlideId, 1)

    // Create pattern events for UI highlighting (if elements have position info)
    if (arrayExpr.elements.length > 0) {
      for (let i = 0; i < arrayExpr.elements.length; i++) {
        const elem = arrayExpr.elements[i]
        if (elem.line !== undefined && elem.column !== undefined) {
          // Adjust line number by subtracting prelude lines
          const adjustedLine = elem.line > 0 ? elem.line - this.preludeLineCount : elem.line

          // Calculate length based on element type
          let length = 1
          if (elem.noteText) {
            // Note array element (e.g., "a1")
            length = elem.noteText.length
          }
          else if (elem.sourceLength !== undefined) {
            // Numeric literal with source length (e.g., .3 = 2, not 3 for "0.3")
            length = elem.sourceLength
          }
          else if (elem.type === 'Literal' && elem.value !== undefined) {
            // Fallback: use stringified value length
            length = String(elem.value).length
          }

          this.patternEvents.push({
            line: adjustedLine,
            column: elem.column,
            length,
            type: 'note',
            sequenceId: advSlideId,
            noteIndex: i,
          })
        }
      }
    }

    // Compile the trigger argument and push it to stack
    this.compileExpression(trigArg, voiceId, impulseVar, pitchesVar, seqId)

    // Emit SEQ_ADVSLIDE to get the interpolated value based on trigger impulses
    this.encoder.emit(OpCode.SEQ_ADVSLIDE, advSlideId)

    // The SEQ_ADVSLIDE pushes: [pitch_count, pitch0, ..., velocity, impulse]
    // For advslide, we only care about pitch0 (the interpolated value)
    // Pop impulse
    this.encoder.emit(OpCode.POP)
    // Pop velocity
    this.encoder.emit(OpCode.POP)
    // Keep pitch0 (the slided value) on stack
  }

  private compileRand(
    arrayExpr: any,
    trigArg: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Extract literal values from array if possible
    if (arrayExpr.type !== 'ArrayLiteral') {
      throw new Error('rand() can only be called on array literals')
    }

    const values: number[] = []
    const glides: number[] = []
    const isNoteArray = arrayExpr.isNoteArray === true
    for (const elem of arrayExpr.elements) {
      const value = this.extractNumericValue(elem, isNoteArray)
      if (isNaN(value)) {
        throw new Error('rand() only supports arrays of literal numbers, negative numbers, and fractions')
      }
      values.push(value)
      // Extract glide if present
      const glide = (elem.glide !== undefined && typeof elem.glide === 'number') ? elem.glide : 0
      glides.push(glide)
    }

    // Create a deterministic ID for this rand array based on its content
    const randId = this.sequenceCounterId++

    // Store array info for potential runtime use
    this.walkArrays.set(randId, { values, measure: 0 })

    // Convert array to a sequence (measure doesn't matter for trigger-based)
    const noteData: NoteData[] = values.map((value, index) => ({
      pitches: new Float32Array([value]),
      duration: 1, // Duration doesn't matter for trigger-based
      velocity: 1,
      hold: 0,
      glide: glides[index],
    }))

    this.sequences.push({ id: randId, notes: noteData })
    this.sequenceMaxPitches.set(randId, 1)

    // Create pattern events for UI highlighting (if elements have position info)
    if (arrayExpr.elements.length > 0) {
      for (let i = 0; i < arrayExpr.elements.length; i++) {
        const elem = arrayExpr.elements[i]
        if (elem.line !== undefined && elem.column !== undefined) {
          // Adjust line number by subtracting prelude lines
          const adjustedLine = elem.line > 0 ? elem.line - this.preludeLineCount : elem.line

          // Calculate length based on element type
          let length = 1
          if (elem.noteText) {
            // Note array element (e.g., "a1")
            length = elem.noteText.length
          }
          else if (elem.sourceLength !== undefined) {
            // Numeric literal with source length (e.g., .3 = 2, not 3 for "0.3")
            length = elem.sourceLength
          }
          else if (elem.type === 'Literal' && elem.value !== undefined) {
            // Fallback: use stringified value length
            length = String(elem.value).length
          }

          this.patternEvents.push({
            line: adjustedLine,
            column: elem.column,
            length,
            type: 'note',
            sequenceId: randId,
            noteIndex: i,
          })
        }
      }
    }

    // Compile the trigger argument and push it to stack
    this.compileExpression(trigArg, voiceId, impulseVar, pitchesVar, seqId)

    // Emit SEQ_RAND to get a random value based on trigger impulses
    this.encoder.emit(OpCode.SEQ_RAND, randId)

    // The SEQ_RAND pushes: [pitch_count, pitch0, ..., velocity, impulse]
    // For rand, we only care about pitch0 (the value)
    // Pop impulse
    this.encoder.emit(OpCode.POP)
    // Pop velocity
    this.encoder.emit(OpCode.POP)
    // Keep pitch0 (the random value) on stack
  }

  private compileRandSlide(
    arrayExpr: any,
    trigArg: any,
    exponentArg: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Extract literal values from array if possible
    if (arrayExpr.type !== 'ArrayLiteral') {
      throw new Error('randslide() can only be called on array literals')
    }

    const values: number[] = []
    const glides: number[] = []
    const isNoteArray = arrayExpr.isNoteArray === true
    for (const elem of arrayExpr.elements) {
      const value = this.extractNumericValue(elem, isNoteArray)
      if (isNaN(value)) {
        throw new Error('randslide() only supports arrays of literal numbers, negative numbers, and fractions')
      }
      values.push(value)
      // Extract glide if present
      const glide = (elem.glide !== undefined && typeof elem.glide === 'number') ? elem.glide : 0
      glides.push(glide)
    }

    // Compile the exponent argument (curve power)
    let exponent = 1 // default to 1 (linear)
    if (exponentArg) {
      if (exponentArg.type === 'Literal' && typeof exponentArg.value === 'number') {
        exponent = exponentArg.value
      }
    }

    // Create a deterministic ID for this randslide array based on its content
    const randSlideId = this.sequenceCounterId++

    // Store array info for potential runtime use
    this.slideArrays.set(randSlideId, { values, measure: 0, exponent })

    // Convert array to a sequence (measure doesn't matter for trigger-based)
    const noteData: NoteData[] = values.map((value, index) => ({
      pitches: new Float32Array([value]),
      duration: 1, // Duration doesn't matter for trigger-based
      velocity: 1,
      hold: 0,
      glide: glides[index],
    }))

    this.sequences.push({ id: randSlideId, notes: noteData, offset: 0, exponent })
    this.sequenceMaxPitches.set(randSlideId, 1)

    // Create pattern events for UI highlighting (if elements have position info)
    if (arrayExpr.elements.length > 0) {
      for (let i = 0; i < arrayExpr.elements.length; i++) {
        const elem = arrayExpr.elements[i]
        if (elem.line !== undefined && elem.column !== undefined) {
          // Adjust line number by subtracting prelude lines
          const adjustedLine = elem.line > 0 ? elem.line - this.preludeLineCount : elem.line

          // Calculate length based on element type
          let length = 1
          if (elem.noteText) {
            // Note array element (e.g., "a1")
            length = elem.noteText.length
          }
          else if (elem.sourceLength !== undefined) {
            // Numeric literal with source length (e.g., .3 = 2, not 3 for "0.3")
            length = elem.sourceLength
          }
          else if (elem.type === 'Literal' && elem.value !== undefined) {
            // Fallback: use stringified value length
            length = String(elem.value).length
          }

          this.patternEvents.push({
            line: adjustedLine,
            column: elem.column,
            length,
            type: 'note',
            sequenceId: randSlideId,
            noteIndex: i,
          })
        }
      }
    }

    // Compile the trigger argument and push it to stack
    this.compileExpression(trigArg, voiceId, impulseVar, pitchesVar, seqId)

    // Emit SEQ_RANDSLIDE to get the interpolated value based on trigger impulses
    this.encoder.emit(OpCode.SEQ_RANDSLIDE, randSlideId)

    // The SEQ_RANDSLIDE pushes: [pitch_count, pitch0, ..., velocity, impulse]
    // For randslide, we only care about pitch0 (the interpolated value)
    // Pop impulse
    this.encoder.emit(OpCode.POP)
    // Pop velocity
    this.encoder.emit(OpCode.POP)
    // Keep pitch0 (the slided value) on stack
  }

  private isPitchesMap(expr: any, pitchesVar: number): boolean {
    // Check if this is <pitches-var>.map(...)
    if (expr?.type !== 'Call' || expr.name !== 'map') {
      return false
    }

    const mapObjectIdentifier = expr.args?.[0]?.value
    return (
      mapObjectIdentifier?.type === 'Identifier'
      && pitchesVar >= 0
      && this.symbols.get(mapObjectIdentifier.name) === pitchesVar
    )
  }

  private willArrayBeChained(arrayExpr: any): boolean {
    // Check if this array is part of a method call chain
    // This is a heuristic - we check the parent context
    // For now, assume arrays used in method calls should be chained
    return true // Simplified: always push arrays as first-class for now
  }

  private pushArrayLiteral(
    arrayExpr: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    const isNoteArray = arrayExpr.isNoteArray === true
    const values: number[] = []

    for (const element of arrayExpr.elements) {
      const value = this.extractNumericValue(element, isNoteArray)
      if (isNaN(value)) {
        throw new Error('PUSH_ARRAY only supports arrays of literal numbers, negative numbers, and fractions')
      }
      values.push(value)
    }

    // Emit PUSH_ARRAY: count, value1, value2, ..., valueN
    this.encoder.emit(OpCode.PUSH_ARRAY, values.length, ...values)
  }

  private isArrayLiteral(expr: any): boolean {
    // Check if this is an ArrayLiteral (e.g., from "c2" note string)
    return expr?.type === 'ArrayLiteral'
  }

  private compileArrayLiteralSum(
    arrayExpr: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Compile array elements
    for (const element of arrayExpr.elements) {
      // If this is a note array and the element is a literal frequency, apply tune
      if (
        arrayExpr.isNoteArray
        && element.type === 'Literal'
        && typeof element.value === 'number'
      ) {
        this.encoder.emit(OpCode.PUSH, element.value * this.tune)
      }
      else {
        this.compileExpression(element, voiceId, impulseVar, pitchesVar, seqId)
      }
    }
    // Sum all the array elements
    this.encoder.emit(OpCode.ARRAY_SUM, arrayExpr.elements.length)
  }

  private compileArrayPick(
    pickExpr: any,
    arrayExpr: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // [a,b,c].pick(el) where el is -1..1
    // Compile array elements in reverse order (they'll be popped in order)
    for (let i = arrayExpr.elements.length - 1; i >= 0; i--) {
      const element = arrayExpr.elements[i]
      // If this is a note array and the element is a literal frequency, apply tune
      if (
        arrayExpr.isNoteArray
        && element.type === 'Literal'
        && typeof element.value === 'number'
      ) {
        this.encoder.emit(OpCode.PUSH, element.value * this.tune)
      }
      else {
        this.compileExpression(element, voiceId, impulseVar, pitchesVar, seqId)
      }
    }

    // Compile the element value (the -1..1 selector)
    const elArg = pickExpr.args?.[1]?.value
    if (!elArg) {
      const line = (pickExpr as any).line
      const column = (pickExpr as any).column
      const location = line && column ? ` at line ${line}, column ${column}` : ''
      throw new Error(`pick() requires an element selector argument${location}`)
    }
    this.compileExpression(elArg, voiceId, impulseVar, pitchesVar, seqId)

    // Emit ARRAY_PICK opcode with array length
    this.encoder.emit(OpCode.ARRAY_PICK, arrayExpr.elements.length)
  }

  private compileArrayMap(
    mapExpr: any,
    arrayExpr: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // [64,65,66].map(mtof) or [64,65,66].map(v->mtof(v))
    // Strategy: Unroll at compile time - apply function to each element

    // The function argument is in args[1] (args[0] is the array object)
    const mapArg = mapExpr.args?.[1]

    if (!mapArg || !mapArg.value) {
      // No valid function argument, just compile as array
      this.compileExpression(arrayExpr, voiceId, impulseVar, pitchesVar, seqId)
      return
    }

    const fnValue = mapArg.value

    // Try to evaluate at compile time if possible for functions like mtof
    const evaluatedArray = this.tryEvaluateMap(arrayExpr, fnValue)
    if (evaluatedArray) {
      // Replace the map expression with the evaluated array in expression assignments
      // This allows .walk() and other methods to work with the result
      this.replaceMapInAssignments(mapExpr, evaluatedArray)
      // Compile the evaluated array
      this.compileExpression(evaluatedArray, voiceId, impulseVar, pitchesVar, seqId)
      return
    }

    // Fallback: Handle arrow function: v->mtof(v)
    if (fnValue.type === 'ArrowFunction') {
      const paramName = fnValue.params[0]
      const savedSymbols = new Map(this.symbols)

      // Compile each element by applying the arrow function (in reverse order for stack)
      const isNoteArray = arrayExpr.isNoteArray === true
      for (let i = arrayExpr.elements.length - 1; i >= 0; i--) {
        const element = arrayExpr.elements[i]
        // Allocate a temp variable for this element value
        const tempVar = this.varCounter++
        // Apply tune to note array elements
        if (isNoteArray && element.type === 'Literal' && typeof element.value === 'number') {
          this.encoder.emit(OpCode.PUSH, element.value * this.tune)
        }
        else {
          this.compileExpression(element, voiceId, impulseVar, pitchesVar, seqId)
        }
        this.encoder.emit(OpCode.STORE, tempVar)

        // Set parameter to point to temp variable
        this.symbols.set(paramName, tempVar)

        // Compile the arrow function body
        const body = fnValue.body
        if (body && body.type === 'Block') {
          this.compileBlock(body, voiceId, impulseVar, pitchesVar, seqId)
        }
        else {
          this.compileExpression(body, voiceId, impulseVar, pitchesVar, seqId)
        }

        // Restore symbols after each iteration to clear any variables declared inside
        this.symbols = new Map(savedSymbols)
      }
    }
    // Handle function reference: mtof
    else if (fnValue.type === 'Identifier') {
      const fnName = fnValue.name
      const isNoteArray = arrayExpr.isNoteArray === true

      // Compile each element by calling the function (in reverse order for stack)
      for (let i = arrayExpr.elements.length - 1; i >= 0; i--) {
        const element = arrayExpr.elements[i]
        // Compile the element value, applying tune to note arrays
        if (isNoteArray && element.type === 'Literal' && typeof element.value === 'number') {
          this.encoder.emit(OpCode.PUSH, element.value * this.tune)
        }
        else {
          this.compileExpression(element, voiceId, impulseVar, pitchesVar, seqId)
        }

        // Call the function with the element as argument
        const callId = this.getCounterId(fnName)

        // Apply the function to the value on the stack
        this.compileFunctionCall(fnName, callId)
      }
    }
    else {
      // Unknown function type, just compile as array
      this.compileExpression(arrayExpr, voiceId, impulseVar, pitchesVar, seqId)
      return
    }

    // Push array count
    this.encoder.emit(OpCode.PUSH, arrayExpr.elements.length)
  }

  private tryEvaluateMap(arrayExpr: any, fnValue: any): any | null {
    // Only evaluate if all elements are literals
    const allLiterals = arrayExpr.elements.every((el: any) => el.type === 'Literal')
    if (!allLiterals) return null

    // Handle function reference (e.g., mtof)
    if (fnValue.type === 'Identifier') {
      const fnName = fnValue.name

      // Only handle known pure functions
      if (fnName === 'mtof') {
        const mappedElements = arrayExpr.elements.map((el: any) => {
          const note = el.value
          const freq = 440 * Math.pow(2, (note - 69) / 12)
          return { type: 'Literal', value: freq }
        })
        return { type: 'ArrayLiteral', elements: mappedElements }
      }
      if (fnName === 'transpose') {
        const mappedElements = arrayExpr.elements.map((el: any) => {
          const semitones = el.value
          const multiplier = Math.pow(2, semitones / 12)
          return { type: 'Literal', value: multiplier }
        })
        return { type: 'ArrayLiteral', elements: mappedElements }
      }
    }

    // Handle arrow function with simple call (e.g., v->mtof(v))
    if (fnValue.type === 'ArrowFunction' && fnValue.body.type === 'Call') {
      const callName = fnValue.body.name
      const param = fnValue.params[0]

      // Check if the call uses the parameter directly
      const arg = fnValue.body.args?.[0]
      if (arg && arg.value?.type === 'Identifier' && arg.value.name === param) {
        // This is a simple call like v->fn(v)
        if (callName === 'mtof') {
          const mappedElements = arrayExpr.elements.map((el: any) => {
            const note = el.value
            const freq = 440 * Math.pow(2, (note - 69) / 12)
            return { type: 'Literal', value: freq }
          })
          return { type: 'ArrayLiteral', elements: mappedElements }
        }
        if (callName === 'transpose') {
          const mappedElements = arrayExpr.elements.map((el: any) => {
            const semitones = el.value
            const multiplier = Math.pow(2, semitones / 12)
            return { type: 'Literal', value: multiplier }
          })
          return { type: 'ArrayLiteral', elements: mappedElements }
        }
      }
    }

    return null
  }

  private replaceMapInAssignments(mapExpr: any, evaluatedArray: any): void {
    // Replace any reference to the map expression with the evaluated array
    for (const [name, value] of this.expressionAssignments.entries()) {
      if (value === mapExpr) {
        this.expressionAssignments.set(name, evaluatedArray)
      }
    }
  }

  private compileFunctionCall(fnName: string, callId: number) {
    // Emit the opcode for a simple function call (value already on stack)
    switch (fnName) {
      case 'tanh':
        this.encoder.emit(OpCode.TANH, callId)
        break
      case 'amplitude':
        this.encoder.emit(OpCode.AMPLITUDE, callId)
        break
      case 'waveform':
        this.encoder.emit(OpCode.AMPLITUDE, callId)
        break
      case 'level':
        this.encoder.emit(OpCode.AMPLITUDE, callId)
        break
      case 'atan':
        this.encoder.emit(OpCode.ATAN, callId)
        break
      case 'abs':
        this.encoder.emit(OpCode.ABS, callId)
        break
      case 'sqrt':
        this.encoder.emit(OpCode.SQRT, callId)
        break
      case 'log':
        this.encoder.emit(OpCode.LOG, callId)
        break
      case 'exp':
        this.encoder.emit(OpCode.EXP, callId)
        break
      case 'db':
        this.encoder.emit(OpCode.DB, callId)
        break
      case 'floor':
        this.encoder.emit(OpCode.FLOOR, callId)
        break
      case 'ceil':
        this.encoder.emit(OpCode.CEIL, callId)
        break
      case 'fract':
        this.encoder.emit(OpCode.FRACT, callId)
        break
      case 'sign':
        this.encoder.emit(OpCode.SIGN, callId)
        break
      case 'mtof':
        this.encoder.emit(OpCode.MTOF, callId)
        break
      case 'transpose':
        this.encoder.emit(OpCode.TRANSPOSE, callId)
        break
      default:
        // Unknown function, just leave value on stack
        break
    }
  }

  private compileMapSum(
    mapExpr: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // pitches.map(pitch => tri(hz:pitch, trig:impulse)).sum()
    // Strategy: Unroll at compile time based on sequence's max pitch count

    const arrowFn = mapExpr.args.find((arg: any) => arg.value?.type === 'ArrowFunction')?.value
    if (!arrowFn) {
      this.encoder.emit(OpCode.PUSH, 0)
      return
    }

    // Get the pitch count for this sequence (we know it at compile time!)
    const pitchCount = this.sequenceMaxPitches.get(seqId) || 1

    const pitchParam = arrowFn.params[0]
    const savedSymbols = new Map(this.symbols)

    // Unroll the map: compile the expression N times, once per pitch slot
    for (let i = 0; i < pitchCount; i++) {
      // Set pitch parameter to point to this pitch variable
      // The expression will LOAD it when it encounters the identifier
      this.symbols.set(pitchParam, pitchesVar + i)

      // Compile the map body for this pitch
      const body = arrowFn.body
      if (body && body.type === 'Block') {
        this.compileBlock(body, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.compileExpression(body, voiceId, impulseVar, pitchesVar, seqId)
      }

      // Restore symbols after each iteration to clear any variables declared inside
      this.symbols = new Map(savedSymbols)
    }

    // Sum all the results
    this.encoder.emit(OpCode.ARRAY_SUM, pitchCount)
  }

  private compileCall(
    call: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Store original name before aliasing for validation
    const originalName = call.name

    // Normalize aliases
    if (call.name === 'beat') {
      call = { ...call, name: 'beatgrain' }
    }
    if (call.name === 'compressor') {
      call = { ...call, name: 'comp' }
    }

    // Check if this is a user-defined function
    const userFunc = this.userFunctions.get(call.name)
    if (userFunc) {
      this.inlineUserFunction(userFunc, call.args, voiceId, impulseVar, pitchesVar, seqId)
      return
    }

    // Check if this is a known built-in function
    if (!this.cachedKnownFunctions) {
      this.cachedKnownFunctions = new Set(getAllFunctionNames())
      // Also include special functions that might not be in functionDefinitions
      this.cachedKnownFunctions.add('out')
      this.cachedKnownFunctions.add('solo')
      this.cachedKnownFunctions.add('post')
      this.cachedKnownFunctions.add('seq')
      this.cachedKnownFunctions.add('pat')
      this.cachedKnownFunctions.add('map')
      this.cachedKnownFunctions.add('sum')

      // Also check for method names without dot prefix (parser strips the dot)
      // Methods like .walk(), .slide(), etc. are registered as '.walk' but called as 'walk'
      const methodNames = ['walk', 'slide', 'step', 'adv', 'advslide', 'rand', 'randslide', 'sum', 'pick', 'map']
      for (const methodName of methodNames) {
        this.cachedKnownFunctions.add(methodName)
      }
    }
    const knownFunctions = this.cachedKnownFunctions

    if (!knownFunctions.has(call.name) && !knownFunctions.has('.' + call.name)) {
      throw new Error(`Unknown function: ${call.name}`)
    }

    // Functions that don't need counter IDs (handled specially)
    const functionsWithoutCounters = ['out', 'solo', 'post', 'seq', 'pat']
    const needsCounter = !functionsWithoutCounters.includes(call.name)
    const callId = needsCounter ? this.getCounterId(call.name) : 0

    // Handle pat() and seq() specially when called inside functions
    // They create sequences and need to be compiled inline to return a value
    if (call.name === 'pat' || call.name === 'seq') {
      this.compilePatSeqInline(call, voiceId, impulseVar, pitchesVar, seqId)
      return
    }

    // Handle beatgrain/beat specially - it needs beat, prob, swing, and offset in order
    if (call.name === 'beatgrain' || originalName === 'beat') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        // Use original name for validation so 'beat' validates against 'beat' definition
        this.validateFunctionParams(originalName === 'beat' ? 'beat' : 'beatgrain', args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for beat
      const beatArg = this.findArgByName(namedArgs, 'beat')
      const beatValue = beatArg ? beatArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for prob
      const probArg = this.findArgByName(namedArgs, 'prob')
      const probValue = probArg ? probArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for swing (only for 'beat', not 'beatgrain')
      let swingValue: any = undefined
      if (originalName === 'beat') {
        const swingArg = this.findArgByName(namedArgs, 'swing')
        swingValue = swingArg ? swingArg.value : positionalArgs[positionalIndex++]?.value
      }

      // Find or use positional args for offset
      const offsetArg = this.findArgByName(namedArgs, 'offset')
      const offsetValue = offsetArg ? offsetArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for skipFirst (only for 'beat', not 'beatgrain')
      let skipFirstValue: any = undefined
      if (originalName === 'beat') {
        const skipFirstArg = this.findArgByName(namedArgs, 'skipFirst')
        skipFirstValue = skipFirstArg ? skipFirstArg.value : positionalArgs[positionalIndex++]?.value
      }

      // Push beat (use default if not provided)
      if (beatValue) {
        this.compileArgValue(beatValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.25) // Default beat (1/4)
      }

      // Push prob (use default if not provided)
      if (probValue) {
        this.compileArgValue(probValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 1) // Default prob
      }

      // Push swing (use default if not provided, always push for stack consistency)
      if (originalName === 'beat' && swingValue !== undefined) {
        this.compileArgValue(swingValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default swing (0 for beatgrain or when not provided)
      }

      // Push offset (use default if not provided)
      if (offsetValue) {
        this.compileArgValue(offsetValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default offset
      }

      // Push skipFirst (use default if not provided, always push for stack consistency)
      if (originalName === 'beat' && skipFirstValue !== undefined) {
        this.compileArgValue(skipFirstValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default skipFirst (0 for beatgrain or when not provided)
      }

      this.encoder.emit(OpCode.BEAT_GRAIN, callId)
      return
    }

    // Handle subdivide specially - it needs beat, pattern, swing, and offset
    if (call.name === 'subdivide') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams('subdivide', args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for beat
      const beatArg = this.findArgByName(namedArgs, 'beat')
      const beatValue = beatArg ? beatArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for pattern
      const patternArg = this.findArgByName(namedArgs, 'pattern')
      const patternValue = patternArg ? patternArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for swing
      const swingArg = this.findArgByName(namedArgs, 'swing')
      const swingValue = swingArg ? swingArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for offset
      const offsetArg = this.findArgByName(namedArgs, 'offset')
      const offsetValue = offsetArg ? offsetArg.value : positionalArgs[positionalIndex++]?.value

      // Extract pattern array values
      if (!patternValue || patternValue.type !== 'ArrayLiteral') {
        throw new Error('subdivide() pattern argument must be an array literal')
      }

      const patternValues: number[] = []
      for (const elem of patternValue.elements) {
        const value = this.extractNumericValue(elem, false)
        if (isNaN(value)) {
          throw new Error(
            'subdivide() pattern array must contain only literal numbers, negative numbers, and fractions',
          )
        }
        patternValues.push(value)
      }

      if (patternValues.length === 0) {
        throw new Error('subdivide() pattern array must not be empty')
      }

      // Create a deterministic ID for this subdivide pattern
      const subdivideId = this.subdivideCounterId++

      // Store pattern array for runtime access
      this.walkArrays.set(subdivideId, { values: patternValues, measure: 0 })

      // Push beat (use default if not provided)
      if (beatValue) {
        this.compileArgValue(beatValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.25) // Default beat (1/4)
      }

      // Push pattern values as constants (will be extracted by executor)
      for (let i = patternValues.length - 1; i >= 0; i--) {
        this.encoder.emit(OpCode.PUSH, patternValues[i])
      }

      // Push pattern length
      this.encoder.emit(OpCode.PUSH, patternValues.length)

      // Push swing (use default if not provided)
      if (swingValue !== undefined) {
        this.compileArgValue(swingValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default swing
      }

      // Push offset (use default if not provided)
      if (offsetValue) {
        this.compileArgValue(offsetValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default offset
      }

      // Push pattern ID
      this.encoder.emit(OpCode.PUSH, subdivideId)

      this.encoder.emit(OpCode.SUBDIVIDE, callId)
      return
    }

    // Handle timeline specially - it needs beat and pattern string
    if (call.name === 'timeline') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams('timeline', args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for beat
      const beatArg = this.findArgByName(namedArgs, 'beat')
      const beatValue = beatArg ? beatArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for pattern
      const patternArg = this.findArgByName(namedArgs, 'pattern')
      const patternValue = patternArg ? patternArg.value : positionalArgs[positionalIndex++]?.value

      // Extract pattern string
      if (!patternValue || patternValue.type !== 'Literal' || typeof patternValue.value !== 'string') {
        throw new Error('timeline() pattern argument must be a string literal')
      }

      const patternStr = patternValue.value as string
      let segments: TimelineSegment[]
      try {
        segments = parseTimelineString(patternStr)
      }
      catch (e: any) {
        throw new Error(`timeline() pattern parse error: ${e.message}`)
      }

      if (segments.length === 0) {
        throw new Error('timeline() pattern must not be empty')
      }

      // Convert segments to flat array: [type, value, divisions, ...]
      // type: 0=fall, 1=rise, 2=hold (swapped to match behavior)
      const segmentsArray: number[] = []
      for (const seg of segments) {
        if (seg.type === 'rise') {
          segmentsArray.push(1, 0, seg.divisions) // 1 = rise (/) - goes 0 to 1
        }
        else if (seg.type === 'fall') {
          segmentsArray.push(0, 0, seg.divisions) // 0 = fall (\) - goes 1 to 0
        }
        else if (seg.type === 'hold') {
          segmentsArray.push(2, seg.value, seg.divisions)
        }
      }

      // Create a deterministic ID for this timeline pattern
      const timelineId = this.timelineCounterId++

      // Store segments array for runtime access (similar to subdivide)
      this.walkArrays.set(timelineId, { values: segmentsArray, measure: 0 })

      // Track timeline info for UI visualization
      let beatValueNum = 0.25 // default
      if (beatValue) {
        if (beatValue.type === 'Literal' && typeof beatValue.value === 'number') {
          beatValueNum = beatValue.value
        }
        else if (beatValue.type === 'BinaryOp' && beatValue.operator === '/') {
          const left = beatValue.left?.value ?? 1
          const right = beatValue.right?.value ?? 4
          beatValueNum = left / right
        }
      }
      // Adjust line number by subtracting prelude lines (AST was parsed from combined source)
      const adjustedLine = call.line && call.line > 0 ? call.line - this.preludeLineCount : call.line || 1

      // Find the pattern argument in the source to get accurate positions for each segment
      const patternArgValue = patternArg ? patternArg.value : positionalArgs[positionalIndex - 1]?.value
      if (patternArgValue && patternArgValue.line && patternArgValue.column) {
        // Parse through the pattern string character by character to map positions
        let charIndex = 0
        let segmentIndex = 0

        let parsedSegmentIndex = 0
        for (const seg of segments) {
          // Skip whitespace before segment
          while (charIndex < patternStr.length && /\s/.test(patternStr[charIndex])) {
            charIndex++
          }

          if (seg.type === 'rise') {
            // Create widget for the '/' character and number if present
            const startCharIndex = charIndex
            charIndex++ // skip '/'
            let numLength = 0
            // Count the number if present
            if (charIndex < patternStr.length && /\d/.test(patternStr[charIndex])) {
              while (charIndex < patternStr.length && /\d/.test(patternStr[charIndex])) {
                numLength++
                charIndex++
              }
            }
            const widgetLength = 1 + numLength // '/' plus number digits
            this.timelineInfos.push({
              id: timelineId,
              line: adjustedLine,
              column: patternArgValue.column + 1 + startCharIndex, // +1 for opening quote
              length: widgetLength,
              pattern: patternStr,
              beat: beatValueNum,
              segmentIndex: parsedSegmentIndex, // Use parsed segment index
              characterIndex: segmentIndex, // Track character index separately
              segmentType: 'rise',
              segmentDivisions: seg.divisions,
            })
            segmentIndex++
            parsedSegmentIndex++
          }
          else if (seg.type === 'fall') {
            // Create widget for the '\' character and number if present
            const startCharIndex = charIndex
            charIndex++ // skip '\'
            let numLength = 0
            // Count the number if present
            if (charIndex < patternStr.length && /\d/.test(patternStr[charIndex])) {
              while (charIndex < patternStr.length && /\d/.test(patternStr[charIndex])) {
                numLength++
                charIndex++
              }
            }
            const widgetLength = 1 + numLength // '\' plus number digits
            this.timelineInfos.push({
              id: timelineId,
              line: adjustedLine,
              column: patternArgValue.column + 1 + startCharIndex, // +1 for opening quote
              length: widgetLength,
              pattern: patternStr,
              beat: beatValueNum,
              segmentIndex: parsedSegmentIndex, // Use parsed segment index
              characterIndex: segmentIndex, // Track character index separately
              segmentType: 'fall',
              segmentDivisions: seg.divisions,
            })
            segmentIndex++
            parsedSegmentIndex++
          }
          else if (seg.type === 'hold') {
            // For holds, check if there's a number after the character
            // If there's a number (e.g., '4), create one widget covering the character and number
            // If there's no number (e.g., '''' or multiple characters), create widgets for each character
            const startCharIndex = charIndex
            const holdChar = seg.value === 1 ? '\'' : '.'

            // Check if there's a number immediately after the character
            let numLength = 0
            let nextCharIndex = charIndex + 1
            if (nextCharIndex < patternStr.length && /\d/.test(patternStr[nextCharIndex])) {
              while (nextCharIndex < patternStr.length && /\d/.test(patternStr[nextCharIndex])) {
                numLength++
                nextCharIndex++
              }
              // Numbered hold (e.g., '4): create one widget covering character + number
              const widgetLength = 1 + numLength
              this.timelineInfos.push({
                id: timelineId,
                line: adjustedLine,
                column: patternArgValue.column + 1 + startCharIndex, // +1 for opening quote
                length: widgetLength,
                pattern: patternStr,
                beat: beatValueNum,
                segmentIndex: parsedSegmentIndex,
                characterIndex: segmentIndex,
                segmentType: 'hold',
                segmentValue: seg.value,
                segmentDivisions: seg.divisions,
              })
              charIndex = nextCharIndex // Skip character and number
            }
            else {
              // Multiple characters (e.g., ''''): create a widget for each character
              // All characters in the same hold segment share the same parsedSegmentIndex
              for (let i = 0; i < seg.divisions; i++) {
                this.timelineInfos.push({
                  id: timelineId,
                  line: adjustedLine,
                  column: patternArgValue.column + 1 + charIndex, // +1 for opening quote
                  length: 1,
                  pattern: patternStr,
                  beat: beatValueNum,
                  segmentIndex: parsedSegmentIndex, // All characters in hold share same segment index
                  characterIndex: segmentIndex + i, // Track character index separately
                  segmentType: 'hold',
                  segmentValue: seg.value,
                  segmentDivisions: 1, // Each character is 1 division
                })
                charIndex++
              }
            }
            segmentIndex += seg.divisions
            parsedSegmentIndex++
          }
        }
      }

      // Push beat (use default if not provided)
      if (beatValue) {
        this.compileArgValue(beatValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.25) // Default beat (1/4)
      }

      // Push segment values as constants (will be extracted by executor)
      for (let i = segmentsArray.length - 1; i >= 0; i--) {
        this.encoder.emit(OpCode.PUSH, segmentsArray[i])
      }

      // Push segments length (number of values, not number of segments)
      this.encoder.emit(OpCode.PUSH, segmentsArray.length)

      // Push timeline ID
      this.encoder.emit(OpCode.PUSH, timelineId)

      this.encoder.emit(OpCode.TIMELINE, callId)
      return
    }

    // Handle pwm specially - it needs hz, width, and trig in order
    if (call.name === 'pwm') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for hz
      const hzArg = this.findArgByName(namedArgs, 'hz')
      const hzValue = hzArg ? hzArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for width
      const widthArg = this.findArgByName(namedArgs, 'width')
      const widthValue = widthArg ? widthArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for trig
      const trigArg = this.findArgByName(namedArgs, 'trig')
      const trigValue = trigArg ? trigArg.value : positionalArgs[positionalIndex++]?.value

      // Push freq (use default if not provided)
      if (hzValue) {
        this.compileArgValue(hzValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 440) // Default frequency
      }

      // Push width (use default if not provided)
      if (widthValue) {
        this.compileArgValue(widthValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default width (no offset)
      }

      // Push trig (use default if not provided)
      if (trigValue) {
        this.compileArgValue(trigValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // No trigger
      }

      this.encoder.emit(OpCode.OSC_PWM, callId)
      return
    }

    // Handle LFOs specially - they need beat, offset, trig in order (trig is last)
    if (['lfosin', 'lfotri', 'lfosaw', 'lforamp', 'lfosqr', 'lfopwm', 'lfosah'].includes(call.name)) {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for beat
      const beatArg = this.findArgByName(namedArgs, 'beat')
      const beatValue = beatArg ? beatArg.value : positionalArgs[positionalIndex++]?.value

      // For lfosah, seed comes after beat
      let seedValue: any = null
      if (call.name === 'lfosah') {
        const seedArg = this.findArgByName(namedArgs, 'seed')
        seedValue = seedArg ? seedArg.value : positionalArgs[positionalIndex++]?.value
      }

      // Find or use positional args for offset
      const offsetArg = this.findArgByName(namedArgs, 'offset')
      const offsetValue = offsetArg ? offsetArg.value : positionalArgs[positionalIndex++]?.value

      // For lfopwm, width comes after offset
      let widthValue: any = null
      if (call.name === 'lfopwm') {
        const widthArg = this.findArgByName(namedArgs, 'width')
        widthValue = widthArg ? widthArg.value : positionalArgs[positionalIndex++]?.value
      }

      // Find or use positional args for trig (last parameter)
      const trigArg = this.findArgByName(namedArgs, 'trig')
      const trigValue = trigArg ? trigArg.value : positionalArgs[positionalIndex++]?.value

      // Push beat (use default if not provided)
      if (beatValue) {
        this.compileArgValue(beatValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.25) // Default beat (1/4 note)
      }

      // For lfosah, push seed after beat
      if (call.name === 'lfosah') {
        if (seedValue) {
          this.compileArgValue(seedValue, voiceId, impulseVar, pitchesVar, seqId)
        }
        else {
          this.encoder.emit(OpCode.PUSH, 0) // Default seed
        }
      }

      // Push offset (use default if not provided)
      if (offsetValue) {
        this.compileArgValue(offsetValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // No offset
      }

      // For lfopwm, push width after offset
      if (call.name === 'lfopwm') {
        if (widthValue) {
          this.compileArgValue(widthValue, voiceId, impulseVar, pitchesVar, seqId)
        }
        else {
          this.encoder.emit(OpCode.PUSH, 0) // Default width
        }
      }

      // Push trig (use default if not provided) - last parameter
      if (trigValue) {
        this.compileArgValue(trigValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // No trigger
      }

      switch (call.name) {
        case 'lfosin':
          this.encoder.emit(OpCode.LFO, LfoType.SIN, callId)
          break
        case 'lfotri':
          this.encoder.emit(OpCode.LFO, LfoType.TRI, callId)
          break
        case 'lfosaw':
          this.encoder.emit(OpCode.LFO, LfoType.SAW, callId)
          break
        case 'lforamp':
          this.encoder.emit(OpCode.LFO, LfoType.RAMP, callId)
          break
        case 'lfosqr':
          this.encoder.emit(OpCode.LFO, LfoType.SQR, callId)
          break
        case 'lfopwm':
          this.encoder.emit(OpCode.LFO, LfoType.PWM, callId)
          break
        case 'lfosah':
          this.encoder.emit(OpCode.LFO, LfoType.SAH, callId)
          break
      }
      return
    }

    // Handle oscillators specially - they need freq and trig in order
    if (['sin', 'tri', 'saw', 'sqr', 'ramp', 'phasor'].includes(call.name)) {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for hz
      const hzArg = this.findArgByName(namedArgs, 'hz')
      const hzValue = hzArg ? hzArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for trig
      const trigArg = this.findArgByName(namedArgs, 'trig')
      const trigValue = trigArg ? trigArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for offset
      const offsetArg = this.findArgByName(namedArgs, 'offset')
      const offsetValue = offsetArg ? offsetArg.value : positionalArgs[positionalIndex++]?.value

      // Push freq (use default if not provided)
      if (hzValue) {
        this.compileArgValue(hzValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 440) // Default frequency
      }

      // Push trig (use default if not provided)
      if (trigValue) {
        this.compileArgValue(trigValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // No trigger
      }

      // Push offset (use default if not provided)
      if (offsetValue) {
        this.compileArgValue(offsetValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default offset is 0
      }

      switch (call.name) {
        case 'sin':
          this.encoder.emit(OpCode.OSC, OscType.SIN, callId)
          break
        case 'tri':
          this.encoder.emit(OpCode.OSC, OscType.TRI, callId)
          break
        case 'saw':
          this.encoder.emit(OpCode.OSC, OscType.SAW, callId)
          break
        case 'sqr':
          this.encoder.emit(OpCode.OSC, OscType.SQR, callId)
          break
        case 'ramp':
          this.encoder.emit(OpCode.OSC, OscType.RAMP, callId)
          break
        case 'phasor':
          this.encoder.emit(OpCode.OSC, OscType.PHASOR, callId)
          break
      }
      return
    }

    // Handle noise specially - it needs seed, type, and trig in order
    if (call.name === 'noise') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for seed
      const seedArg = this.findArgByName(namedArgs, 'seed')
      const seedValue = seedArg ? seedArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for type
      const typeArg = this.findArgByName(namedArgs, 'type')
      let typeValue = typeArg ? typeArg.value : positionalArgs[positionalIndex++]?.value

      // Convert type string to number: 0=white, 1=pink, 2=brown
      let typeNum = 0 // default to white
      if (typeValue) {
        if (typeValue.type === 'Literal' && typeof typeValue.value === 'string') {
          const typeStr = typeValue.value.toLowerCase()
          if (typeStr === 'pink') typeNum = 1
          else if (typeStr === 'brown') typeNum = 2
        }
      }

      // Find or use positional args for trig
      const trigArg = this.findArgByName(namedArgs, 'trig')
      const trigValue = trigArg ? trigArg.value : positionalArgs[positionalIndex++]?.value

      // Push seed (use default if not provided)
      if (seedValue) {
        this.compileArgValue(seedValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default seed
      }

      // Push type (as number)
      this.encoder.emit(OpCode.PUSH, typeNum)

      // Push trig (use default if not provided)
      if (trigValue) {
        this.compileArgValue(trigValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // No trigger
      }

      this.encoder.emit(OpCode.NOISE, callId)
      return
    }

    // Handle harmonics specially - it needs hz, partials (array), and trig in order
    if (call.name === 'harmonics') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for hz
      const hzArg = this.findArgByName(namedArgs, 'hz')
      const hzValue = hzArg ? hzArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for partials
      const partialsArg = this.findArgByName(namedArgs, 'partials')
      const partialsValue = partialsArg
        ? partialsArg.value
        : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for trig
      const trigArg = this.findArgByName(namedArgs, 'trig')
      const trigValue = trigArg ? trigArg.value : positionalArgs[positionalIndex++]?.value

      // Push hz first (bottom of stack)
      if (hzValue) {
        this.compileArgValue(hzValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 440) // Default frequency
      }

      // Push partials array (compile as array literal)
      if (partialsValue && partialsValue.type === 'ArrayLiteral') {
        // Compile array elements in reverse order (so they pop in correct order)
        for (let i = partialsValue.elements.length - 1; i >= 0; i--) {
          this.compileArgValue(partialsValue.elements[i], voiceId, impulseVar, pitchesVar, seqId)
        }
        // Push array count
        this.encoder.emit(OpCode.PUSH, partialsValue.elements.length)
      }
      else if (
        partialsValue
        && partialsValue.type === 'Literal'
        && typeof partialsValue.value === 'number'
      ) {
        // Handle single number as single-element array [value]
        this.encoder.emit(OpCode.PUSH, partialsValue.value)
        this.encoder.emit(OpCode.PUSH, 1)
      }
      else if (partialsValue) {
        // If it's not an array literal, compile it as an expression (might be a variable)
        this.compileExpression(partialsValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        // Default to [1] (single harmonic at full amplitude)
        this.encoder.emit(OpCode.PUSH, 1)
        this.encoder.emit(OpCode.PUSH, 1)
      }

      // Push trig last (top of stack)
      if (trigValue) {
        this.compileArgValue(trigValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // No trigger
      }

      this.encoder.emit(OpCode.HARMONICS, callId)
      return
    }

    // Handle grain specially - it needs speed in order
    if (call.name === 'grain') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)

      // Find or use positional args for speed
      const speedArg = this.findArgByName(namedArgs, 'speed')
      const speedValue = speedArg ? speedArg.value : positionalArgs[0]?.value

      // Push speed (use default if not provided)
      if (speedValue) {
        this.compileArgValue(speedValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.5) // Default speed
      }

      this.encoder.emit(OpCode.GRAIN, callId)
      return
    }

    // Handle sample specially - it needs src, trig, speed, and offset in order
    if (call.name === 'sample') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for src
      const srcArg = this.findArgByName(namedArgs, 'src')
      const srcValue = srcArg ? srcArg.value : positionalArgs[positionalIndex++]?.value

      let sampleId = 0
      if (srcValue && srcValue.type === 'Literal' && typeof srcValue.value === 'string') {
        const samplePath = srcValue.value
        sampleId = this.getNextSampleId(samplePath)
      }

      // Find or use positional args for trig
      const trigArg = this.findArgByName(namedArgs, 'trig')
      const trigValue = trigArg ? trigArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for speed
      const speedArg = this.findArgByName(namedArgs, 'speed')
      const speedValue = speedArg ? speedArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for offset
      const offsetArg = this.findArgByName(namedArgs, 'offset')
      const offsetValue = offsetArg ? offsetArg.value : positionalArgs[positionalIndex++]?.value

      // Push trig (use default if not provided)
      if (trigValue) {
        this.compileArgValue(trigValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // No trigger
      }

      // Push speed (use default if not provided)
      if (speedValue) {
        this.compileArgValue(speedValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 1.0) // Normal speed
      }

      // Push offset (use default if not provided)
      if (offsetValue) {
        this.compileArgValue(offsetValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.0) // Start at beginning
      }

      this.encoder.emit(OpCode.SAMPLE, callId, sampleId)
      return
    }

    // Handle freesound specially - it needs id, trig, speed, and offset in order
    if (call.name === 'freesound') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for id
      const idArg = this.findArgByName(namedArgs, 'id')
      const idValue = idArg ? idArg.value : positionalArgs[positionalIndex++]?.value

      let sampleId = 0
      if (idValue && idValue.type === 'Literal' && typeof idValue.value === 'number') {
        const freesoundId = idValue.value
        const samplePath = `https://freesound.cowbell.workers.dev/get?id=${freesoundId}`
        sampleId = this.getNextSampleId(samplePath)
      }

      // Find or use positional args for trig
      const trigArg = this.findArgByName(namedArgs, 'trig')
      const trigValue = trigArg ? trigArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for speed
      const speedArg = this.findArgByName(namedArgs, 'speed')
      const speedValue = speedArg ? speedArg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for offset
      const offsetArg = this.findArgByName(namedArgs, 'offset')
      const offsetValue = offsetArg ? offsetArg.value : positionalArgs[positionalIndex++]?.value

      // Push trig (use default if not provided)
      if (trigValue) {
        this.compileArgValue(trigValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // No trigger
      }

      // Push speed (use default if not provided)
      if (speedValue) {
        this.compileArgValue(speedValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 1.0) // Normal speed
      }

      // Push offset (use default if not provided)
      if (offsetValue) {
        this.compileArgValue(offsetValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.0) // Start at beginning
      }

      this.encoder.emit(OpCode.SAMPLE, callId, sampleId)
      return
    }

    // Handle sam specially - text-to-speech with voice parameters
    if (call.name === 'sam') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for each parameter
      const speakArg = this.findArgByName(namedArgs, 'speak')
      const speakValue = speakArg ? speakArg.value : positionalArgs[positionalIndex++]?.value

      const trigArg = this.findArgByName(namedArgs, 'trig')
      const trigValue = trigArg ? trigArg.value : positionalArgs[positionalIndex++]?.value

      const speedArg = this.findArgByName(namedArgs, 'speed')
      const speedValue = speedArg ? speedArg.value : positionalArgs[positionalIndex++]?.value

      const pitchArg = this.findArgByName(namedArgs, 'pitch')
      const pitchValue = pitchArg ? pitchArg.value : positionalArgs[positionalIndex++]?.value

      const throatArg = this.findArgByName(namedArgs, 'throat')
      const throatValue = throatArg ? throatArg.value : positionalArgs[positionalIndex++]?.value

      const mouthArg = this.findArgByName(namedArgs, 'mouth')
      const mouthValue = mouthArg ? mouthArg.value : positionalArgs[positionalIndex++]?.value

      const playbackArg = this.findArgByName(namedArgs, 'playback')
      const playbackValue = playbackArg
        ? playbackArg.value
        : positionalArgs[positionalIndex++]?.value

      const offsetArg = this.findArgByName(namedArgs, 'offset')
      const offsetValue = offsetArg ? offsetArg.value : positionalArgs[positionalIndex++]?.value

      // Extract speech parameters (these define the audio to generate)
      let samId = 0
      if (speakValue && speakValue.type === 'Literal' && typeof speakValue.value === 'string') {
        const speak = speakValue.value
        const speed = speedValue?.type === 'Literal' ? speedValue.value : 0.5
        const pitch = pitchValue?.type === 'Literal' ? pitchValue.value : 0.5
        const throat = throatValue?.type === 'Literal' ? throatValue.value : 0.5
        const mouth = mouthValue?.type === 'Literal' ? mouthValue.value : 0.5

        const params = { speak, speed, pitch, throat, mouth }
        const key = JSON.stringify(params)
        // Use hash for SAM since it's based on parameters, not a file path
        const samHash = hashString(key)
        this.samSpeeches.set(samHash, params)
        // Create a synthetic path for SAM samples
        const samplePath = `__SAM__${samHash}`
        samId = this.getNextSampleId(samplePath)
      }

      // Push trig (use default if not provided)
      if (trigValue) {
        this.compileArgValue(trigValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // No trigger
      }

      // Push playback speed (use default if not provided)
      if (playbackValue) {
        this.compileArgValue(playbackValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 1.0) // Normal speed
      }

      // Push offset (use default if not provided)
      if (offsetValue) {
        this.compileArgValue(offsetValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.0) // Start at beginning
      }

      this.encoder.emit(OpCode.SAMPLE, callId, samId)
      return
    }

    // Handle espeak specially - text-to-speech via URL with query parameters
    if (call.name === 'espeak') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for text
      const textArg = this.findArgByName(namedArgs, 'text')
      const textValue = textArg ? textArg.value : positionalArgs[positionalIndex++]?.value

      const variantArg = this.findArgByName(namedArgs, 'variant')
      const variantValue = variantArg ? variantArg.value : positionalArgs[positionalIndex++]?.value

      const speedArg = this.findArgByName(namedArgs, 'speed')
      const speedValue = speedArg ? speedArg.value : positionalArgs[positionalIndex++]?.value

      const pitchArg = this.findArgByName(namedArgs, 'pitch')
      const pitchValue = pitchArg ? pitchArg.value : positionalArgs[positionalIndex++]?.value

      const rateArg = this.findArgByName(namedArgs, 'rate')
      const rateValue = rateArg ? rateArg.value : positionalArgs[positionalIndex++]?.value

      const offsetArg = this.findArgByName(namedArgs, 'offset')
      const offsetValue = offsetArg ? offsetArg.value : positionalArgs[positionalIndex++]?.value

      const trigArg = this.findArgByName(namedArgs, 'trig')
      const trigValue = trigArg ? trigArg.value : positionalArgs[positionalIndex++]?.value

      // Generate URL with query parameters if text is a literal
      let sampleId = 0
      if (textValue && textValue.type === 'Literal' && typeof textValue.value === 'string') {
        const text = textValue.value
        const variant = variantValue?.type === 'Literal' ? variantValue.value : 'm1'
        const speed = speedValue?.type === 'Literal' ? speedValue.value : 0.5
        const pitch = pitchValue?.type === 'Literal' ? pitchValue.value : 0.5

        // Map speed from 0..1 to 0..200, pitch from 0..1 to 0..100
        const speedParam = Math.round(Math.max(0, Math.min(200, speed * 200)))
        const pitchParam = Math.round(Math.max(0, Math.min(100, pitch * 100)))

        // Construct URL with query parameters
        // Use a special prefix that will be resolved at runtime in the sample loader
        const queryParams = `text=${encodeURIComponent(text)}&variant=${
          encodeURIComponent(
            variant,
          )
        }&speed=${speedParam}&pitch=${pitchParam}`
        const samplePath = `__MESPEAK__?${queryParams}`
        sampleId = this.getNextSampleId(samplePath)
      }

      // Push trig (use default if not provided)
      if (trigValue) {
        this.compileArgValue(trigValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // No trigger
      }

      // Push playback rate (use default if not provided)
      if (rateValue) {
        this.compileArgValue(rateValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 1.0) // Normal speed
      }

      // Push offset (use default if not provided)
      if (offsetValue) {
        this.compileArgValue(offsetValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.0) // Start at beginning
      }

      this.encoder.emit(OpCode.SAMPLE, callId, sampleId)
      return
    }

    // Handle slicer - like sample but with peak detection
    if (call.name === 'slicer') {
      this.validateFunctionParams(call.name, call.args || [])

      const srcArg = this.findArgByName(call.args || [], 'src')
      const trigArg = this.findArgByName(call.args || [], 'trig')
      const speedArg = this.findArgByName(call.args || [], 'speed')
      const offsetArg = this.findArgByName(call.args || [], 'offset')
      const thresholdArg = this.findArgByName(call.args || [], 'threshold')

      let sampleId = 0
      if (srcArg && srcArg.value?.type === 'Literal' && typeof srcArg.value.value === 'string') {
        const samplePath = srcArg.value.value
        sampleId = this.getNextSampleId(samplePath)
      }

      // Push trig (default to 0)
      if (trigArg) {
        this.compileArgValue(trigArg.value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // No trigger
      }

      // Push speed (default to 1.0)
      if (speedArg) {
        this.compileArgValue(speedArg.value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 1.0) // Normal speed
      }

      // Push offset (default to 0.0 which maps to middle slice)
      if (offsetArg) {
        this.compileArgValue(offsetArg.value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.0) // Middle slice
      }

      // Push threshold (default to 0.5 = 50% of RMS)
      if (thresholdArg) {
        this.compileArgValue(thresholdArg.value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.5) // Default threshold
      }

      this.encoder.emit(OpCode.SLICER, callId, sampleId)
      return
    }

    // Handle freesoundslicer - like freesound but with peak detection
    if (call.name === 'freesoundslicer') {
      this.validateFunctionParams(call.name, call.args || [])

      const idArg = this.findArgByName(call.args || [], 'id')
      const trigArg = this.findArgByName(call.args || [], 'trig')
      const speedArg = this.findArgByName(call.args || [], 'speed')
      const offsetArg = this.findArgByName(call.args || [], 'offset')
      const thresholdArg = this.findArgByName(call.args || [], 'threshold')

      let sampleId = 0
      if (idArg && idArg.value?.type === 'Literal' && typeof idArg.value.value === 'number') {
        const freesoundId = idArg.value.value
        const samplePath = `https://freesound.cowbell.workers.dev/get?id=${freesoundId}`
        sampleId = this.getNextSampleId(samplePath)
      }

      // Push trig (default to 0)
      if (trigArg) {
        this.compileArgValue(trigArg.value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // No trigger
      }

      // Push speed (default to 1.0)
      if (speedArg) {
        this.compileArgValue(speedArg.value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 1.0) // Normal speed
      }

      // Push offset (default to 0.0 which maps to middle slice)
      if (offsetArg) {
        this.compileArgValue(offsetArg.value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.0) // Middle slice
      }

      // Push threshold (default to 0.5 = 50% of RMS)
      if (thresholdArg) {
        this.compileArgValue(thresholdArg.value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0.5) // Default threshold
      }

      this.encoder.emit(OpCode.SLICER, callId, sampleId)
      return
    }

    // Handle select specially - it needs in1, in2, and sign in order
    if (call.name === 'select') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for in1
      const in1Arg = this.findArgByName(namedArgs, 'in1')
      const in1Value = in1Arg ? in1Arg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for in2
      const in2Arg = this.findArgByName(namedArgs, 'in2')
      const in2Value = in2Arg ? in2Arg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for sign
      const signArg = this.findArgByName(namedArgs, 'sign')
      const signValue = signArg ? signArg.value : positionalArgs[positionalIndex++]?.value

      // Push in1 (use default if not provided)
      if (in1Value) {
        this.compileArgValue(in1Value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default in1
      }

      // Push in2 (use default if not provided)
      if (in2Value) {
        this.compileArgValue(in2Value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default in2
      }

      // Push sign (use default if not provided)
      if (signValue) {
        this.compileArgValue(signValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default sign (selects in1)
      }

      const callId = this.getCounterId('select')
      this.encoder.emit(OpCode.SELECT, callId)
      return
    }

    // Handle crossfade specially - it needs in1, in2, and amount in order
    if (call.name === 'crossfade') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for in1
      const in1Arg = this.findArgByName(namedArgs, 'in1')
      const in1Value = in1Arg ? in1Arg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for in2
      const in2Arg = this.findArgByName(namedArgs, 'in2')
      const in2Value = in2Arg ? in2Arg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for amount
      const amountArg = this.findArgByName(namedArgs, 'amount')
      const amountValue = amountArg ? amountArg.value : positionalArgs[positionalIndex++]?.value

      // Push in1 (use default if not provided)
      if (in1Value) {
        this.compileArgValue(in1Value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default in1
      }

      // Push in2 (use default if not provided)
      if (in2Value) {
        this.compileArgValue(in2Value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default in2
      }

      // Push amount (use default if not provided)
      if (amountValue) {
        this.compileArgValue(amountValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default amount (equal mix)
      }

      const callId = this.getCounterId('crossfade')
      this.encoder.emit(OpCode.CROSSFADE, callId)
      return
    }

    // Handle swap specially - it needs in1, in2, and trig in order
    if (call.name === 'swap') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Separate positional and named arguments
      const { positional: positionalArgs, named: namedArgs } = this.separateArgs(args)
      let positionalIndex = 0

      // Find or use positional args for in1
      const in1Arg = this.findArgByName(namedArgs, 'in1')
      const in1Value = in1Arg ? in1Arg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for in2
      const in2Arg = this.findArgByName(namedArgs, 'in2')
      const in2Value = in2Arg ? in2Arg.value : positionalArgs[positionalIndex++]?.value

      // Find or use positional args for trig
      const trigArg = this.findArgByName(namedArgs, 'trig')
      const trigValue = trigArg ? trigArg.value : positionalArgs[positionalIndex++]?.value

      // Push in1 (use default if not provided)
      if (in1Value) {
        this.compileArgValue(in1Value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default in1
      }

      // Push in2 (use default if not provided)
      if (in2Value) {
        this.compileArgValue(in2Value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default in2
      }

      // Push trig (use default if not provided)
      if (trigValue) {
        this.compileArgValue(trigValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0) // Default trig (no swap)
      }

      this.encoder.emit(OpCode.SWAP, callId)
      return
    }

    // Handle min specially - it needs a and b in order
    if (call.name === 'min') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      const positionalArgs = args.filter((a: any) => !a.name)
      const namedArgs = args.filter((a: any) => a.name)
      let positionalIndex = 0

      const aArg = this.findArgByName(namedArgs, 'a')
      const aValue = aArg ? aArg.value : positionalArgs[positionalIndex++]?.value

      const bArg = this.findArgByName(namedArgs, 'b')
      const bValue = bArg ? bArg.value : positionalArgs[positionalIndex++]?.value

      if (aValue) {
        this.compileArgValue(aValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0)
      }

      if (bValue) {
        this.compileArgValue(bValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0)
      }

      this.encoder.emit(OpCode.MIN, callId)
      return
    }

    // Handle max specially - it needs a and b in order
    if (call.name === 'max') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      const positionalArgs = args.filter((a: any) => !a.name)
      const namedArgs = args.filter((a: any) => a.name)
      let positionalIndex = 0

      const aArg = this.findArgByName(namedArgs, 'a')
      const aValue = aArg ? aArg.value : positionalArgs[positionalIndex++]?.value

      const bArg = this.findArgByName(namedArgs, 'b')
      const bValue = bArg ? bArg.value : positionalArgs[positionalIndex++]?.value

      if (aValue) {
        this.compileArgValue(aValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0)
      }

      if (bValue) {
        this.compileArgValue(bValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0)
      }

      this.encoder.emit(OpCode.MAX, callId)
      return
    }

    // Handle clamp specially - it needs x, min, and max in order
    if (call.name === 'clamp') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      const positionalArgs = args.filter((a: any) => !a.name)
      const namedArgs = args.filter((a: any) => a.name)
      let positionalIndex = 0

      const xArg = this.findArgByName(namedArgs, 'x')
      const xValue = xArg ? xArg.value : positionalArgs[positionalIndex++]?.value

      const minArg = this.findArgByName(namedArgs, 'min')
      const minValue = minArg ? minArg.value : positionalArgs[positionalIndex++]?.value

      const maxArg = this.findArgByName(namedArgs, 'max')
      const maxValue = maxArg ? maxArg.value : positionalArgs[positionalIndex++]?.value

      if (xValue) {
        this.compileArgValue(xValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0)
      }

      if (minValue) {
        this.compileArgValue(minValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0)
      }

      if (maxValue) {
        this.compileArgValue(maxValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 1)
      }

      this.encoder.emit(OpCode.CLAMP, callId)
      return
    }

    // Handle wrap specially - it needs x, min, and max in order
    if (call.name === 'wrap') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      const positionalArgs = args.filter((a: any) => !a.name)
      const namedArgs = args.filter((a: any) => a.name)
      let positionalIndex = 0

      const xArg = this.findArgByName(namedArgs, 'x')
      const xValue = xArg ? xArg.value : positionalArgs[positionalIndex++]?.value

      const minArg = this.findArgByName(namedArgs, 'min')
      const minValue = minArg ? minArg.value : positionalArgs[positionalIndex++]?.value

      const maxArg = this.findArgByName(namedArgs, 'max')
      const maxValue = maxArg ? maxArg.value : positionalArgs[positionalIndex++]?.value

      if (xValue) {
        this.compileArgValue(xValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0)
      }

      if (minValue) {
        this.compileArgValue(minValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 0)
      }

      if (maxValue) {
        this.compileArgValue(maxValue, voiceId, impulseVar, pitchesVar, seqId)
      }
      else {
        this.encoder.emit(OpCode.PUSH, 1)
      }

      this.encoder.emit(OpCode.WRAP, callId)
      return
    }

    // Handle out specially - it needs L and R arguments
    if (call.name === 'out') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)

      if (hasNamedArgs) {
        // Named arguments: check if R is missing, duplicate L if so
        const lArg = this.findArgByName(args, 'L')
        const rArg = this.findArgByName(args, 'R')

        if (lArg && !rArg) {
          // Only L provided, duplicate it for R
          this.compileArgValue(lArg.value, voiceId, impulseVar, pitchesVar, seqId)
          this.compileArgValue(lArg.value, voiceId, impulseVar, pitchesVar, seqId)
        }
        else {
          // Both provided or only R provided, use normal compilation
          this.compileCallArgs(call, impulseVar, pitchesVar, seqId)
        }
      }
      else {
        // Positional arguments: check if only one argument provided
        const positionalArgs = args.filter((a: any) => !a.name)
        if (positionalArgs.length === 1) {
          // Only L provided, duplicate it for R
          this.compileArgValue(positionalArgs[0].value, voiceId, impulseVar, pitchesVar, seqId)
          this.compileArgValue(positionalArgs[0].value, voiceId, impulseVar, pitchesVar, seqId)
        }
        else {
          // Both provided or none, use normal compilation
          this.compileCallArgs(call, impulseVar, pitchesVar, seqId)
        }
      }

      this.encoder.emit(OpCode.OUTPUT)
      return
    }

    // Handle solo specially - it needs L and R arguments, but clears other outputs
    if (call.name === 'solo') {
      const args = call.args || []
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)

      if (hasNamedArgs) {
        // Named arguments: check if R is missing, duplicate L if so
        const lArg = this.findArgByName(args, 'L')
        const rArg = this.findArgByName(args, 'R')

        if (lArg && !rArg) {
          // Only L provided, duplicate it for R
          this.compileArgValue(lArg.value, voiceId, impulseVar, pitchesVar, seqId)
          this.compileArgValue(lArg.value, voiceId, impulseVar, pitchesVar, seqId)
        }
        else {
          // Both provided or only R provided, use normal compilation
          this.compileCallArgs(call, impulseVar, pitchesVar, seqId)
        }
      }
      else {
        // Positional arguments: check if only one argument provided
        const positionalArgs = args.filter((a: any) => !a.name)
        if (positionalArgs.length === 1) {
          // Only L provided, duplicate it for R
          this.compileArgValue(positionalArgs[0].value, voiceId, impulseVar, pitchesVar, seqId)
          this.compileArgValue(positionalArgs[0].value, voiceId, impulseVar, pitchesVar, seqId)
        }
        else {
          // Both provided or none, use normal compilation
          this.compileCallArgs(call, impulseVar, pitchesVar, seqId)
        }
      }

      this.encoder.emit(OpCode.SOLO)
      return
    }

    // For other functions, use normal arg compilation
    const irHash = this.compileCallArgs(call, impulseVar, pitchesVar, seqId)
    this.emitFunctionOpcode(call.name, callId, irHash)

    // Track amplitude widget info for UI visualization
    if (call.name === 'amplitude') {
      const wfLine = call.line !== undefined && call.line > 0
        ? call.line - this.preludeLineCount
        : call.line ?? 1
      this.amplitudeInfos.push({
        id: callId,
        line: wfLine,
        column: call.column ?? 0,
        length: this.calculateCallLength(call),
      })
    }

    // Track waveform widget info for UI visualization
    if (call.name === 'waveform') {
      const wfLine = call.line !== undefined && call.line > 0
        ? call.line - this.preludeLineCount
        : call.line ?? 1
      this.waveformInfos.push({
        id: callId,
        line: wfLine,
        column: call.column ?? 0,
        length: this.calculateCallLength(call),
      })
    }

    // Track level widget info for UI visualization
    if (call.name === 'level') {
      const wfLine = call.line !== undefined && call.line > 0
        ? call.line - this.preludeLineCount
        : call.line ?? 1
      this.levelInfos.push({
        id: callId,
        line: wfLine,
        column: call.column ?? 0,
        length: this.calculateCallLength(call),
      })
    }
  }

  private emitFunctionOpcode(name: string, callId: number, irHash?: number) {
    switch (name) {
      case 'ar':
        this.encoder.emit(OpCode.ENVELOPE, callId)
        break
      case 'adsr':
        this.encoder.emit(OpCode.ENVELOPE_ADSR, callId)
        break
      case 'lp':
        this.encoder.emit(OpCode.FILTER, FilterType.LP, callId)
        break
      case 'hp':
        this.encoder.emit(OpCode.FILTER, FilterType.HP, callId)
        break
      case 'bp':
        this.encoder.emit(OpCode.FILTER, FilterType.BP, callId)
        break
      case 'svflp':
        this.encoder.emit(OpCode.FILTER, FilterType.SVFLP, callId)
        break
      case 'svfhp':
        this.encoder.emit(OpCode.FILTER, FilterType.SVFHP, callId)
        break
      case 'svfbp':
        this.encoder.emit(OpCode.FILTER, FilterType.SVFBP, callId)
        break
      case 'svfnotch':
        this.encoder.emit(OpCode.FILTER, FilterType.SVFNOTCH, callId)
        break
      case 'svfpeak':
        this.encoder.emit(OpCode.FILTER, FilterType.SVFPEAK, callId)
        break
      case 'svfallpass':
        this.encoder.emit(OpCode.FILTER, FilterType.SVFALLPASS, callId)
        break
      case 'bs':
        this.encoder.emit(OpCode.FILTER, FilterType.BS, callId)
        break
      case 'notch':
        this.encoder.emit(OpCode.FILTER, FilterType.NOTCH, callId)
        break
      case 'ls':
        this.encoder.emit(OpCode.FILTER, FilterType.LS, callId)
        break
      case 'hs':
        this.encoder.emit(OpCode.FILTER, FilterType.HS, callId)
        break
      case 'peak':
        this.encoder.emit(OpCode.FILTER, FilterType.PEAK, callId)
        break
      case 'eq3':
        this.encoder.emit(OpCode.FILTER, FilterType.EQ3, callId)
        break
      case 'allpass':
        this.encoder.emit(OpCode.FILTER, FilterType.ALLPASS, callId)
        break
      case 'diodeladder':
        this.encoder.emit(OpCode.FILTER, FilterType.DIODELADDER, callId)
        break
      case 'mooglp':
        this.encoder.emit(OpCode.FILTER, FilterType.MOOGLP, callId)
        break
      case 'mooghp':
        this.encoder.emit(OpCode.FILTER, FilterType.MOOGHP, callId)
        break
      case 'ffthp':
        this.encoder.emit(OpCode.FILTER, FilterType.FFTHP, callId)
        break
      case 'onepole':
        this.encoder.emit(OpCode.FILTER, FilterType.ONEPOLE, callId)
        break
      case 'delay':
        this.encoder.emit(OpCode.DELAY, DelayType.DELAY, callId)
        break
      case 'beatdelay':
        this.encoder.emit(OpCode.DELAY, DelayType.BEAT_DELAY, callId)
        break
      case 'comb':
        this.encoder.emit(OpCode.COMB, callId)
        break
      case 'chorus':
        this.encoder.emit(OpCode.CHORUS, callId)
        break
      case 'flanger':
        this.encoder.emit(OpCode.FLANGER, callId)
        break
      case 'dattorro':
        this.encoder.emit(OpCode.DATTORRO, callId)
        break
      case 'freeverb':
        this.encoder.emit(OpCode.FREEVERB, callId)
        break
      case 'fdn':
        this.encoder.emit(OpCode.FDN, callId)
        break
      case 'fdn2':
        this.encoder.emit(OpCode.FDN2, callId)
        break
      case 'velvet':
        this.encoder.emit(OpCode.VELVET, callId)
        break
      case 'lexicon':
        this.encoder.emit(OpCode.LEXICON, callId)
        break
      case 'spring':
        this.encoder.emit(OpCode.SPRING, callId)
        break
      case 'comp':
      case 'compressor':
        this.encoder.emit(OpCode.COMPRESSOR, callId)
        break
      case 'expander':
        this.encoder.emit(OpCode.EXPANDER, callId)
        break
      case 'gate':
        this.encoder.emit(OpCode.GATE, callId)
        break
      case 'limiter':
        this.encoder.emit(OpCode.LIMITER, callId)
        break
      case 'vocoder':
        this.encoder.emit(OpCode.VOCODER, callId)
        break
      case 'side':
      case 'sidechain':
        this.encoder.emit(OpCode.SIDECHAIN, callId)
        break
      case 'conv':
        this.encoder.emit(OpCode.CONV, callId, irHash!)
        break
      case 'tanh':
        this.encoder.emit(OpCode.TANH, callId)
        break
      case 'amplitude':
        this.encoder.emit(OpCode.AMPLITUDE, callId)
        break
      case 'waveform':
        this.encoder.emit(OpCode.AMPLITUDE, callId)
        break
      case 'level':
        this.encoder.emit(OpCode.AMPLITUDE, callId)
        break
      case 'atan':
        this.encoder.emit(OpCode.ATAN, callId)
        break
      case 'abs':
        this.encoder.emit(OpCode.ABS, callId)
        break
      case 'sqrt':
        this.encoder.emit(OpCode.SQRT, callId)
        break
      case 'log':
        this.encoder.emit(OpCode.LOG, callId)
        break
      case 'exp':
        this.encoder.emit(OpCode.EXP, callId)
        break
      case 'db':
        this.encoder.emit(OpCode.DB, callId)
        break
      case 'floor':
        this.encoder.emit(OpCode.FLOOR, callId)
        break
      case 'ceil':
        this.encoder.emit(OpCode.CEIL, callId)
        break
      case 'fract':
        this.encoder.emit(OpCode.FRACT, callId)
        break
      case 'sign':
        this.encoder.emit(OpCode.SIGN, callId)
        break
      case 'soft':
        this.encoder.emit(OpCode.SOFT, callId)
        break
      case 'asym':
        this.encoder.emit(OpCode.ASYM, callId)
        break
      case 'mtof':
        this.encoder.emit(OpCode.MTOF, callId)
        break
      case 'transpose':
        this.encoder.emit(OpCode.TRANSPOSE, callId)
        break
      case 'pitchshift':
        this.encoder.emit(OpCode.PITCHSHIFT, callId)
        break
      case 'beatgrain':
      case 'beat':
        this.encoder.emit(OpCode.BEAT_GRAIN, callId)
        break
      case 'skip':
        this.encoder.emit(OpCode.SKIP, callId)
        break
      case 'every':
        this.encoder.emit(OpCode.EVERY, callId)
        break
      case 'euclid':
        this.encoder.emit(OpCode.EUCLID, callId)
        break
      case 'subdivide':
        this.encoder.emit(OpCode.SUBDIVIDE, callId)
        break
      case 'karplus':
        this.encoder.emit(OpCode.KARPLUS, callId)
        break
      case 'dc':
        this.encoder.emit(OpCode.DC, callId)
        break
      case 'slew':
        this.encoder.emit(OpCode.SLEW, callId)
        break
      case 'sah':
        this.encoder.emit(OpCode.SAH, callId)
        break
      case 'envfollow':
        this.encoder.emit(OpCode.ENVELOPE_FOLLOWER, callId)
        break
      case 'bitcrusher':
        this.encoder.emit(OpCode.BITCRUSHER, callId)
        break
      case 'sin':
        this.encoder.emit(OpCode.OSC, OscType.SIN, callId)
        break
      case 'tri':
        this.encoder.emit(OpCode.OSC, OscType.TRI, callId)
        break
      case 'saw':
        this.encoder.emit(OpCode.OSC, OscType.SAW, callId)
        break
      case 'sqr':
        this.encoder.emit(OpCode.OSC, OscType.SQR, callId)
        break
      case 'ramp':
        this.encoder.emit(OpCode.OSC, OscType.RAMP, callId)
        break
      case 'phasor':
        this.encoder.emit(OpCode.OSC, OscType.PHASOR, callId)
        break
      case 'noise':
        this.encoder.emit(OpCode.NOISE, callId)
        break
      case 'grain':
        this.encoder.emit(OpCode.GRAIN, callId)
        break
      case 'lfosin':
        this.encoder.emit(OpCode.LFO, LfoType.SIN, callId)
        break
      case 'lfotri':
        this.encoder.emit(OpCode.LFO, LfoType.TRI, callId)
        break
      case 'lfosaw':
        this.encoder.emit(OpCode.LFO, LfoType.SAW, callId)
        break
      case 'lforamp':
        this.encoder.emit(OpCode.LFO, LfoType.RAMP, callId)
        break
      case 'lfosqr':
        this.encoder.emit(OpCode.LFO, LfoType.SQR, callId)
        break
      case 'lfopwm':
        this.encoder.emit(OpCode.LFO, LfoType.PWM, callId)
        break
      case 'lfosah':
        this.encoder.emit(OpCode.LFO, LfoType.SAH, callId)
        break
      case 'select':
        this.encoder.emit(OpCode.SELECT, callId)
        break
      case 'swap':
        this.encoder.emit(OpCode.SWAP, callId)
        break
      case 'crossfade':
        this.encoder.emit(OpCode.CROSSFADE, callId)
        break
      case 'wrap':
        this.encoder.emit(OpCode.WRAP, callId)
        break
      case 'clamp':
        this.encoder.emit(OpCode.CLAMP, callId)
        break
      case 'out':
        break
      case 'stereo':
        break
      case 'pan':
        break
      default:
        throw new Error(`Unknown function: ${name}`)
    }
  }

  private compilePipelineStage(
    stage: any,
    stageId: number,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Store the piped value in a special variable so % always refers to the same value
    const pipeVar = this.varCounter++
    this.symbols.set('%', pipeVar)
    this.encoder.emit(OpCode.STORE, pipeVar)

    // Handle any expression type, not just Call nodes
    if (stage.type === 'Call') {
      const { name } = stage

      // Check if this is a user-defined function
      const userFunc = this.userFunctions.get(name)
      if (userFunc) {
        this.inlineUserFunction(userFunc, stage.args, voiceId, impulseVar, pitchesVar, seqId)
        this.symbols.delete('%')
        return
      }

      if (name === 'out') {
        const args = stage.args || []
        const positionalArgs = args.filter((a: any) => !a.name)
        const namedArgs = args.filter((a: any) => a.name)

        // Check if only one positional argument is provided (e.g., out(%))
        if (positionalArgs.length === 1 && namedArgs.length === 0) {
          // Only L provided as positional, duplicate it for R
          this.compileArgValue(positionalArgs[0].value, voiceId, impulseVar, pitchesVar, seqId)
          this.compileArgValue(positionalArgs[0].value, voiceId, impulseVar, pitchesVar, seqId)
        }
        // Check if only L is provided as named argument (e.g., out(L:%))
        else if (namedArgs.length === 1 && positionalArgs.length === 0) {
          const lArg = this.findArgByName(namedArgs, 'L')
          if (lArg) {
            // Only L provided, duplicate it for R
            this.compileArgValue(lArg.value, voiceId, impulseVar, pitchesVar, seqId)
            this.compileArgValue(lArg.value, voiceId, impulseVar, pitchesVar, seqId)
          }
          else {
            // Only R or other named arg provided, use normal compilation
            this.compileCallArgs(stage, impulseVar, pitchesVar, seqId)
          }
        }
        else {
          // Both provided, none provided, or mixed - use normal compilation
          this.compileCallArgs(stage, impulseVar, pitchesVar, seqId)
        }

        this.encoder.emit(OpCode.OUTPUT)
        this.symbols.delete('%')
        return
      }

      if (name === 'solo') {
        const args = stage.args || []
        const positionalArgs = args.filter((a: any) => !a.name)
        const namedArgs = args.filter((a: any) => a.name)

        // Check if only one positional argument is provided (e.g., solo(%))
        if (positionalArgs.length === 1 && namedArgs.length === 0) {
          // Only L provided as positional, duplicate it for R
          this.compileArgValue(positionalArgs[0].value, voiceId, impulseVar, pitchesVar, seqId)
          this.compileArgValue(positionalArgs[0].value, voiceId, impulseVar, pitchesVar, seqId)
        }
        // Check if only L is provided as named argument (e.g., solo(L:%))
        else if (namedArgs.length === 1 && positionalArgs.length === 0) {
          const lArg = this.findArgByName(namedArgs, 'L')
          if (lArg) {
            // Only L provided, duplicate it for R
            this.compileArgValue(lArg.value, voiceId, impulseVar, pitchesVar, seqId)
            this.compileArgValue(lArg.value, voiceId, impulseVar, pitchesVar, seqId)
          }
          else {
            // Only R or other named arg provided, use normal compilation
            this.compileCallArgs(stage, impulseVar, pitchesVar, seqId)
          }
        }
        else {
          // Both provided, none provided, or mixed - use normal compilation
          this.compileCallArgs(stage, impulseVar, pitchesVar, seqId)
        }

        this.encoder.emit(OpCode.SOLO)
        this.symbols.delete('%')
        return
      }

      // Check if this is a known built-in function
      const knownFunctions = new Set(getAllFunctionNames())
      knownFunctions.add('out')
      knownFunctions.add('solo')
      knownFunctions.add('post')
      knownFunctions.add('seq')
      knownFunctions.add('pat')
      knownFunctions.add('map')
      knownFunctions.add('sum')

      // Also check for method names without dot prefix
      const methodNames = ['walk', 'slide', 'step', 'adv', 'advslide', 'rand', 'randslide', 'sum', 'pick', 'map']
      for (const methodName of methodNames) {
        knownFunctions.add(methodName)
      }

      if (!knownFunctions.has(name) && !knownFunctions.has('.' + name)) {
        throw new Error(`Unknown function: ${name}`)
      }

      // Functions that don't need counter IDs (handled specially in compileVoice)
      // Note: 'out' and 'solo' are handled above and can be used in pipelines
      const functionsWithoutCounters = ['post', 'seq', 'pat']
      if (functionsWithoutCounters.includes(name)) {
        throw new Error(`${name}() cannot be used as a pipeline stage. It must be the first stage of a pipeline.`)
      }

      const irHash = this.compileCallArgs(stage, impulseVar, pitchesVar, seqId)
      const callId = this.getCounterId(name)
      this.emitFunctionOpcode(name, callId, irHash)

      // Track amplitude widget info for UI visualization
      if (name === 'amplitude') {
        const wfLine = stage.line !== undefined && stage.line > 0
          ? stage.line - this.preludeLineCount
          : stage.line ?? 1
        this.amplitudeInfos.push({
          id: callId,
          line: wfLine,
          column: stage.column ?? 0,
          length: this.calculateCallLength(stage),
        })
      }

      // Track waveform widget info for UI visualization
      if (name === 'waveform') {
        const wfLine = stage.line !== undefined && stage.line > 0
          ? stage.line - this.preludeLineCount
          : stage.line ?? 1
        this.waveformInfos.push({
          id: callId,
          line: wfLine,
          column: stage.column ?? 0,
          length: this.calculateCallLength(stage),
        })
      }

      // Track level widget info for UI visualization
      if (name === 'level') {
        const wfLine = stage.line !== undefined && stage.line > 0
          ? stage.line - this.preludeLineCount
          : stage.line ?? 1
        this.levelInfos.push({
          id: callId,
          line: wfLine,
          column: stage.column ?? 0,
          length: this.calculateCallLength(stage),
        })
      }
    }
    else {
      // Handle non-Call expressions (like BinaryOp: %*v)
      this.compileExpression(stage, voiceId, impulseVar, pitchesVar, seqId)
    }

    this.symbols.delete('%')
  }

  private compileMiniNotationInline(
    pattern: string,
    literal: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    const lineOffset = literal.line || 0
    const columnOffset = (literal.column || 0) + 1 // column points to quote, content starts after

    try {
      // Parse the mini-notation pattern
      const seq = parsePattern(pattern, lineOffset, columnOffset, pattern)

      // Expand pattern to get timed events
      const events = expandPattern(pattern, undefined, lineOffset, columnOffset, pattern)

      if (events.length === 0) {
        // Empty pattern, push 0
        this.encoder.emit(OpCode.PUSH, 0)
        return
      }

      // Convert expanded events to sequence notes
      // Need to preserve timing by inserting rests where there are gaps
      const noteData: NoteData[] = []
      let currentTime = 0

      for (const [time, values, duration, velocity, glide] of events) {
        // If there's a gap between current time and this event's start, insert a rest
        if (time > currentTime) {
          const gapDuration = time - currentTime
          noteData.push({
            pitches: new Float32Array([0]),
            duration: gapDuration,
            velocity: 0,
            hold: 0,
            glide: 0,
          })
          currentTime = time
        }

        // Add the actual event (rest or note)
        if (velocity <= 0 || values.length === 0) {
          // Rest event
          noteData.push({
            pitches: new Float32Array([0]),
            duration,
            velocity: 0,
            hold: 0,
            glide: 0,
          })
        }
        else {
          // Parse note values to frequencies
          const pitches: number[] = []
          for (const value of values) {
            const parsedPitches = this.parseNote(value)
            pitches.push(...parsedPitches)
          }

          noteData.push({
            pitches: new Float32Array(pitches),
            duration,
            velocity,
            hold: 0,
            glide, // Pass glide value from pattern
          })
        }

        currentTime = time + duration
      }

      // Create a unique sequence ID for this inline pattern
      const inlineSeqId = this.sequenceCounterId++

      // For inline use, we only use the first pitch (like walk does)
      // This converts chords to single notes by taking the first note
      this.sequenceMaxPitches.set(inlineSeqId, 1)

      // Add sequence to the program (use same format as walk/slide)
      this.sequences.push({
        id: inlineSeqId,
        notes: noteData,
        offset: 0,
      })

      // Collect pattern events for UI highlighting
      const collectEvents = (events: PatternEvent[], patternEventIndex: { value: number }) => {
        for (const event of events) {
          if ((event as any).nested) {
            // Recursively collect from nested sequences
            collectEvents((event as any).nested.events, patternEventIndex)
          }
          else {
            // Only collect actual notes/rests/chords, not nested placeholders
            if (event.values.length > 0 || event.type === 'rest') {
              const adjustedLine = event.line > 0 ? event.line - this.preludeLineCount : event.line
              this.patternEvents.push({
                line: adjustedLine,
                column: event.column,
                length: event.length,
                type: event.type,
                sequenceId: inlineSeqId,
                noteIndex: patternEventIndex.value,
              })
              patternEventIndex.value++
            }
          }
        }
      }

      const patternEventIndex = { value: 0 }
      collectEvents(seq.events, patternEventIndex)

      // Emit SEQ_STEP to get current value based on beat timing
      this.encoder.emit(OpCode.SEQ_STEP, inlineSeqId)

      // SEQ_STEP pushes: [pitch0, velocity, impulse] (since maxPitches=1)
      // For inline use, we only care about pitch0 (the frequency value)
      // Pop impulse
      this.encoder.emit(OpCode.POP)
      // Pop velocity
      this.encoder.emit(OpCode.POP)
      // Keep pitch0 (the frequency) on stack
    }
    catch (error) {
      const location = literal.line && literal.column
        ? ` at line ${literal.line}, column ${literal.column}`
        : ''
      throw new Error(
        `Invalid mini-notation pattern "${pattern}"${location}: ${
          error instanceof Error ? error.message : String(error)
        }`,
      )
    }
  }

  private compileArgValue(
    value: ExpressionNode,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    if (value.type === 'Literal') {
      // Validate that string literals are not used where numbers are expected
      if (typeof value.value === 'string') {
        // Allow '_' to output 0
        if (value.value === '_') {
          this.encoder.emit(OpCode.PUSH, 0)
          return
        }

        // Check for mini-notation patterns (contain brackets like '[c1 c2]' or '<a2 a3 a4>')
        if (value.value.includes('[') || value.value.includes('<')) {
          this.compileMiniNotationInline(value.value, value as any, voiceId, impulseVar, pitchesVar, seqId)
          return
        }

        // Check if it looks like an incomplete note (just a letter without octave)
        if (/^[a-g][#b]?$/i.test(value.value)) {
          throw new Error(
            `Invalid note "${value.value}". Notes must include an octave number (e.g., "${value.value}4" instead of "${value.value}").`,
          )
        }
        // Check if it looks like a note (has letters and digits) but doesn't match valid note pattern
        // Before throwing error, try parsing as mini-notation pattern
        if (/[a-z]/i.test(value.value) && /\d/.test(value.value) && !/^[a-g][#b]?\d+$/i.test(value.value)) {
          // Try parsing with Mini-Notation parser
          try {
            const lineOffset = (value as any).line || 0
            const columnOffset = ((value as any).column || 0) + 1
            parsePattern(value.value, lineOffset, columnOffset, value.value)
            // If parsing succeeds, treat as mini-notation pattern
            this.compileMiniNotationInline(value.value, value as any, voiceId, impulseVar, pitchesVar, seqId)
            return
          }
          catch {
            // If parsing fails, throw the original error
          }
          throw new Error(
            `Invalid note syntax "${value.value}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`,
          )
        }
        // Check if it looks like a sequence pattern (contains spaces or multiple tokens)
        if (/\s/.test(value.value) || (value.value.includes('_') && value.value.length > 1)) {
          // Check for invalid note syntax (e.g., "dc3" instead of "c3" or "d3")
          const tokens = value.value.trim().split(/\s+/)
          const invalidNotes = tokens.filter(token => {
            if (token === '_') return false
            // Valid note pattern: [a-g][#b]?\d+
            // Invalid: anything else that looks like it might be a note (has letters and digits)
            if (/[a-g]/i.test(token) && /\d/.test(token)) {
              return !/^[a-g][#b]?\d+$/i.test(token)
            }
            return false
          })

          if (invalidNotes.length > 0) {
            throw new Error(
              `Invalid note syntax in "${value.value}". Note "${
                invalidNotes[0]
              }" is not valid. Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`,
            )
          }

          throw new Error(
            `Cannot use sequence pattern "${value.value}" where a number is expected. Sequence patterns can only be used with array methods like .walk(), .slide(), etc.`,
          )
        }
        throw new Error(`Cannot use string "${value.value}" where a number is expected.`)
      }
      this.encoder.emit(OpCode.PUSH, value.value)
    }
    else if (value.type === 'Identifier') {
      // Check for global 'co' (BPM coefficient: bpm/60 to convert beats to Hz)
      // At 120 BPM: co = 120/60 = 2, so sin(1*co) = sin(2 Hz) = 1 cycle per beat
      if (value.name === 'co') {
        const co = this.bpm / 60
        this.encoder.emit(OpCode.PUSH, co)
      }
      else {
        // Look up in symbol table (% is now a proper variable)
        const varSlot = this.symbols.get(value.name)
        if (varSlot !== undefined) {
          this.encoder.emit(OpCode.LOAD, varSlot)
        }
        else {
          // Check if it's an expression assignment like main=hh+pad+bass+ping
          const exprValue = this.expressionAssignments.get(value.name)
          if (exprValue) {
            // Recursively compile the expression
            this.compileArgValue(exprValue, voiceId, impulseVar, pitchesVar, seqId)
          }
          else if (value.name === '_') {
            // Allow '_' as it's used for ignored parameters
            this.encoder.emit(OpCode.PUSH, 0)
          }
          else {
            // Check if it's a known function name
            const knownFunctions = new Set(getAllFunctionNames())
            knownFunctions.add('out')
            knownFunctions.add('post')
            knownFunctions.add('seq')
            knownFunctions.add('pat')
            knownFunctions.add('map')
            knownFunctions.add('sum')
            if (knownFunctions.has(value.name)) {
              throw new Error(
                `Function '${value.name}' must be called with parentheses: ${value.name}(...)`,
              )
            }
            const line = (value as any).line
            const column = (value as any).column
            const location = line && column ? ` at line ${line}, column ${column}` : ''
            throw new Error(`Undefined variable: ${value.name}${location}`)
          }
        }
      }
    }
    else if (value.type === 'ArrayLiteral') {
      // When an array is used where a number is expected, automatically sum it
      // This allows hz:"a4" to work by converting the array [440] to 440
      this.compileArrayLiteralSum(value, voiceId, impulseVar, pitchesVar, seqId)
    }
    else if (value.type === 'Pipeline') {
      // Handle pipeline expressions in arguments
      const nestedVoiceId = hashString(`nested${this.idCounter++}`)
      this.compileExpression(value, nestedVoiceId, impulseVar, pitchesVar, seqId)
    }
    else if (value.type === 'BinaryOp') {
      // Use a stable voiceId for nested expressions
      const nestedVoiceId = hashString(`nested${this.idCounter++}`)
      this.compileExpression(value, nestedVoiceId, impulseVar, pitchesVar, seqId)
    }
    else if (value.type === 'Call') {
      // For Call nodes in arguments, use compileExpression which handles chaining
      const nestedVoiceId = hashString(`nested${this.idCounter++}`)
      this.compileExpression(value, nestedVoiceId, impulseVar, pitchesVar, seqId)
      return
    }
    else if (value.type === 'NumberWithParams') {
      // Handle numbers with parameter metadata
      const nestedVoiceId = hashString(`nested${this.idCounter++}`)
      this.compileExpression(value, nestedVoiceId, impulseVar, pitchesVar, seqId)
      return
    }
    else if (value.type === 'MemberAccess') {
      const validArrayMethods = new Set(['walk', 'slide', 'step', 'adv', 'advslide', 'rand', 'randslide', 'subdivide', 'map', 'sum', 'pick', 'euclid'])
      if (!validArrayMethods.has(value.property)) {
        const line = (value as any).line
        const column = (value as any).column
        const location = line && column ? ` at line ${line}, column ${column}` : ''
        throw new Error(`Invalid property '${value.property}'${location}`)
      }
      // If it's a valid method but not called, that's also an error
      throw new Error(`Property '${value.property}' must be called as a method: [array].${value.property}(...)`)
    }
  }

  private compileCallArgs(
    call: any,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ): number {
    const args = call.args || []
    let irHash = 0

    // Get expected parameter order from function definitions
    const functionDef = this.getFunctionParamOrder(call.name)

    if (functionDef) {
      // Validate that all named arguments are valid
      const hasNamedArgs = args.some((arg: any) => arg.name !== undefined)
      if (hasNamedArgs) {
        this.validateFunctionParams(call.name, args)
      }

      // Handle mixed positional and named arguments
      // Collect positional args (those without names) in order
      // Also treat shorthand args (name === value.name) as positional if the name
      // doesn't match a parameter of this function
      const validParamNames = new Set(functionDef.map(p => this.normalizeArgName(p)))
      const positionalArgs = args.filter((a: any) => {
        if (!a.name) return true
        // If it's shorthand syntax (name === value.name) and the name is not a parameter
        // of this function, treat it as positional
        if (a.value?.type === 'Identifier'
          && a.name === a.value.name
          && !validParamNames.has(this.normalizeArgName(a.name)))
        {
          return true
        }
        return false
      })
      const namedArgs = args.filter((a: any) => {
        if (!a.name) return false
        // Exclude shorthand args that we're treating as positional
        if (a.value?.type === 'Identifier'
          && a.name === a.value.name
          && !validParamNames.has(this.normalizeArgName(a.name)))
        {
          return false
        }
        return true
      })

      // Track how many positional arguments have been consumed
      let positionalIndex = 0

      // Push arguments in the order defined by the function signature
      for (let i = 0; i < functionDef.length; i++) {
        const paramName = functionDef[i]

        // First check if this parameter was provided as a named argument (case-insensitive)
        const namedArg = this.findArgByName(namedArgs, paramName)
        if (namedArg) {
          // Special handling for string arguments (impulse response paths)
          if (namedArg.value?.type === 'Literal' && typeof namedArg.value.value === 'string') {
            const irPath = namedArg.value.value
            // Track this IR path
            if (paramName === 'impulse') {
              this.impulseResponses.add(irPath)
              // For conv, don't push IR hash - return it to be passed as opcode arg
              irHash = hashString(irPath)
              // Don't emit PUSH for conv's impulse parameter
              if (call.name !== 'conv') {
                this.encoder.emit(OpCode.PUSH, irHash)
              }
            }
            else {
              // For other string params, push the hash
              const strHash = hashString(irPath)
              this.encoder.emit(OpCode.PUSH, strHash)
            }
          }
          else {
            this.compileArgValue(namedArg.value, 0, impulseVar, pitchesVar, seqId)
          }
        }
        else if (positionalIndex < positionalArgs.length) {
          // Use next available positional argument
          const positionalArg = positionalArgs[positionalIndex++]
          if (
            positionalArg.value?.type === 'Literal'
            && typeof positionalArg.value.value === 'string'
          ) {
            const irPath = positionalArg.value.value
            if (paramName === 'impulse') {
              this.impulseResponses.add(irPath)
              irHash = hashString(irPath)
              if (call.name !== 'conv') {
                this.encoder.emit(OpCode.PUSH, irHash)
              }
            }
            else {
              const strHash = hashString(irPath)
              this.encoder.emit(OpCode.PUSH, strHash)
            }
          }
          else {
            this.compileArgValue(positionalArg.value, 0, impulseVar, pitchesVar, seqId)
          }
        }
        else {
          // Parameter not provided, use default value
          const defaultValue = this.getParameterDefault(call.name, paramName)
          if (!(call.name === 'conv' && paramName === 'impulse')) {
            this.encoder.emit(OpCode.PUSH, defaultValue)
          }
        }
      }
      return irHash
    }

    // Fallback: function not defined, push arguments in order they appear
    for (const arg of args) {
      this.compileArgValue(arg.value, 0, impulseVar, pitchesVar, seqId)
    }
    return irHash
  }

  private normalizeArgName(name: string): string {
    return name.toLowerCase()
  }

  private findParamCaseInsensitive(params: string[], name: string): string | null {
    const normalized = this.normalizeArgName(name)
    return params.find(p => this.normalizeArgName(p) === normalized) || null
  }

  private findArgByName(args: any[], name: string): any | undefined {
    const normalizedName = this.normalizeArgName(name)
    return args.find(
      (arg: any) => arg.name && this.normalizeArgName(arg.name) === normalizedName,
    )
  }

  private separateArgs(args: any[]): { positional: any[]; named: any[] } {
    const positional: any[] = []
    const named: any[] = []
    for (const arg of args) {
      if (arg.name) {
        named.push(arg)
      }
      else {
        positional.push(arg)
      }
    }
    return { positional, named }
  }

  private getParameterDefault(funcName: string, paramName: string): number {
    const funcDef = functionDefinitions[funcName]
    if (!funcDef) return 0

    const param = funcDef.parameters.find(
      p => this.normalizeArgName(p.name) === this.normalizeArgName(paramName),
    )
    return (param as any)?.defaultValue ?? 0
  }

  private getFunctionParamOrder(funcName: string): string[] | null {
    const funcDef = functionDefinitions[funcName]
    if (!funcDef) return null
    return funcDef.parameters.map(param => param.name)
  }

  private validateFunctionParams(funcName: string, args: any[]) {
    const validParams = this.getFunctionParamOrder(funcName)
    if (!validParams) return

    for (const arg of args) {
      if (arg.name && !this.findParamCaseInsensitive(validParams, arg.name)) {
        // Skip validation for shorthand syntax (name === value.name)
        // These will be treated as positional arguments
        if (arg.value?.type === 'Identifier' && arg.name === arg.value.name) {
          continue
        }
        throw new Error(`Unknown parameter '${arg.name}' for function '${funcName}()'`)
      }
    }
  }

  private inlineUserFunction(
    funcDef: any,
    callArgs: any[],
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Save entire symbol state before entering function (for proper scoping)
    const savedSymbols = new Map(this.symbols)

    // Map function parameters to arguments
    const params = funcDef.params || []
    const args = callArgs || []
    const paramDefaults = funcDef.paramDefaults

    for (let i = 0; i < params.length; i++) {
      const paramName = params[i]
      const namedArg = this.findArgByName(args, paramName)
      const positionalArg = args[i]
      const arg = namedArg || positionalArg

      // Compile the argument value and store in a temp variable
      const tempVar = this.varCounter++

      if (arg) {
        // Argument provided, compile it
        this.compileArgValue(arg.value, voiceId, impulseVar, pitchesVar, seqId)
      }
      else if (paramDefaults && paramDefaults.has(paramName)) {
        // No argument provided, use default value
        const defaultValue = paramDefaults.get(paramName)
        this.encoder.emit(OpCode.PUSH, defaultValue)
      }
      else {
        // No argument and no default, skip this parameter
        continue
      }

      this.encoder.emit(OpCode.STORE, tempVar)
      this.symbols.set(paramName, tempVar)
    }

    // Compile the function body (handle both Block and single expression)
    const body = funcDef.body
    if (body.type === 'Block') {
      this.compileBlock(body, voiceId, impulseVar, pitchesVar, seqId)
    }
    else {
      this.compileExpression(body, voiceId, impulseVar, pitchesVar, seqId)
    }

    // Restore symbols to remove function parameters and any variables declared inside
    this.symbols = savedSymbols
  }

  private compilePostProcessing(arrowFunc: any) {
    // POST opcode: signal that post-processing should be applied
    // The arrow function takes (L, R) -> out(L:..., R:...)
    const postId = hashString('post_master')

    // Save entire symbol state before entering post function (for proper scoping)
    const savedSymbols = new Map(this.symbols)

    const params = arrowFunc.params || []
    const LParam = params[0] || 'L'
    const RParam = params[1] || 'R'

    // Allocate variable slots for L and R channels
    const LVar = this.varCounter++
    const RVar = this.varCounter++
    this.symbols.set(LParam, LVar)
    this.symbols.set(RParam, RVar)

    // Emit POST opcode with variable slots
    // The executor will load accumulated output into these variable slots
    this.encoder.emit(OpCode.POST, postId, LVar, RVar)

    // Compile the body of the arrow function
    // For post((L,R)->out(L:tanh(in:L),R:tanh(in:R)))
    // The body should be the out() call
    const body = arrowFunc.body
    if (body.type === 'Call' && body.name === 'out') {
      // Extract L and R arguments from the out() call
      this.compileCallArgs(body, -1, -1, 0)
      // The args are now on the stack in order [L, R]
      // OUTPUT opcode will consume them
      this.encoder.emit(OpCode.OUTPUT)
    }
    else if (body.type === 'Pipeline') {
      // Handle pipeline: (L,R)->%*0.5 |> out(L:%,R:%)
      this.compileExpression(body, postId, -1, -1, 0)
    }

    // Restore symbols to remove parameters and any variables declared inside
    this.symbols = savedSymbols
  }

  private getCallArg(call: any, name: string, position: number): any {
    const args = call.args || []
    // Try to find by name first (case-insensitive)
    const namedArg = this.findArgByName(args, name)
    if (namedArg) return namedArg.value

    // Fall back to positional (only if position >= 0)
    if (position >= 0 && position < args.length) {
      const arg = args[position]
      if (!arg) return undefined
      // Accept the arg if:
      // 1. It has no name (purely positional), OR
      // 2. The name matches what we're looking for (case-insensitive), OR
      // 3. It has a name but that name matches the value (shorthand syntax like hh_seq for hh_seq:hh_seq)
      //    and we should treat it as positional
      if (
        !arg.name
        || (arg.name && this.normalizeArgName(arg.name) === this.normalizeArgName(name))
      ) {
        return arg.value
      }
      // For shorthand syntax where arg.name exists and matches arg.value.name, treat as positional
      if (arg.name && arg.value?.type === 'Identifier' && arg.name === arg.value.name) {
        return arg.value
      }
    }
    return undefined
  }

  private findSeqCall(expr: any): any {
    // Recursively search for a seq() or pat() call in an expression tree
    if (expr.type === 'Call' && (expr.name === 'seq' || expr.name === 'pat')) {
      return expr
    }
    if (expr.type === 'BinaryOp') {
      const leftSeq = this.findSeqCall(expr.left)
      if (leftSeq) return leftSeq
      const rightSeq = this.findSeqCall(expr.right)
      if (rightSeq) return rightSeq
    }
    return null
  }

  private replaceSeqWithPlaceholder(expr: any): any {
    // Replace seq() or pat() call with a placeholder that will be filled with the result
    // Returns the modified expression tree
    if (expr.type === 'Call' && (expr.name === 'seq' || expr.name === 'pat')) {
      return { type: 'Placeholder' }
    }
    if (expr.type === 'BinaryOp') {
      return {
        type: 'BinaryOp',
        operator: expr.operator,
        left: this.replaceSeqWithPlaceholder(expr.left),
        right: this.replaceSeqWithPlaceholder(expr.right),
      }
    }
    return expr
  }

  private applyPostSeqOperation(
    operation: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    // Apply the operation to the current stack value (result of seq callback)
    // The operation tree has Placeholder nodes where the seq result should be used
    this.compileOperationWithPlaceholder(operation, voiceId, impulseVar, pitchesVar, seqId)
  }

  private compileOperationWithPlaceholder(
    expr: any,
    voiceId: number,
    impulseVar: number,
    pitchesVar: number,
    seqId: number,
  ) {
    if (expr.type === 'Placeholder') {
      // This is where the seq result should be used - it's already on the stack, do nothing
      return
    }
    if (expr.type === 'BinaryOp') {
      // Check if left side is the placeholder (seq result is already on stack)
      if (expr.left.type === 'Placeholder') {
        // Stack has: [seq_result]
        // We need: [seq_result, right_value]
        this.compileExpression(expr.right, voiceId, impulseVar, pitchesVar, seqId)
      }
      else if (expr.right.type === 'Placeholder') {
        // Stack has: [seq_result]
        // We need: [left_value, seq_result]
        // But the stack is backwards, so we need to compile left, then swap
        const tempVar = this.varCounter++
        this.encoder.emit(OpCode.STORE, tempVar) // Store seq result
        this.compileExpression(expr.left, voiceId, impulseVar, pitchesVar, seqId)
        this.encoder.emit(OpCode.LOAD, tempVar) // Load seq result back
      }
      else {
        // Neither side is placeholder, recurse
        this.compileOperationWithPlaceholder(expr.left, voiceId, impulseVar, pitchesVar, seqId)
        this.compileOperationWithPlaceholder(expr.right, voiceId, impulseVar, pitchesVar, seqId)
      }

      // Emit the operation
      switch (expr.operator) {
        case '+':
          this.encoder.emit(OpCode.ADD)
          break
        case '-':
          this.encoder.emit(OpCode.SUB)
          break
        case '*':
          this.encoder.emit(OpCode.MUL)
          break
        case '/':
          this.encoder.emit(OpCode.DIV)
          break
      }
    }
    else {
      this.compileExpression(expr, voiceId, impulseVar, pitchesVar, seqId)
    }
  }
}
